/**
 * PayrollPage.js - Gestão de Folha de Pagamento Legal (Moçambique)
 * CG Business ERP - P0 ABSOLUTO
 * 
 * Conformidade Legal:
 * - INSS: 3% trabalhador + 4% empregador
 * - IRPS: Tabela oficial AT Moçambique (configurável)
 * - Comissões: 1.5% sobre vendas (meta individual/empresa)
 * - Recibo A4 Portrait com original/duplicado
 * - Dossier Digital do Trabalhador obrigatório
 */

import { useEffect, useState, useMemo, useCallback } from 'react';
import { payrollAPI, rbacAPI, settingsAPI, usersAPI, getErrorMessage } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogTrigger } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { Separator } from '@/components/ui/separator';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Progress } from '@/components/ui/progress';
import { Switch } from '@/components/ui/switch';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import { UniversalCrudActions } from '@/components/UniversalCrudActions';
import { ModulePageHero } from '@/components/common/ModulePageHero';
import { ModuleStateBoundary } from '@/components/common/ModuleStateBoundary';
import { EmptyStatePanel } from '@/components/common/EmptyStatePanel';
import { PayrollAlertsPanel } from '@/components/payroll/PayrollAlertsPanel';
import { PayrollEmployeeCreateDialog } from '@/components/payroll/PayrollEmployeeCreateDialog';
import { PayrollEmployeeDirectorySection } from '@/components/payroll/PayrollEmployeeDirectorySection';
import { PayrollEmployeeDossierDialog } from '@/components/payroll/PayrollEmployeeDossierDialog';
import { PayrollAttendanceOperationsSection } from '@/components/payroll/PayrollAttendanceOperationsSection';
import { PayrollAttendanceManagementSection } from '@/components/payroll/PayrollAttendanceManagementSection';
import { PayrollEmployeesTabContent } from '@/components/payroll/PayrollEmployeesTabContent';
import { PayrollEmployeesToolbar } from '@/components/payroll/PayrollEmployeesToolbar';
import { PayrollVacationsTabContent } from '@/components/payroll/PayrollVacationsTabContent';
import { PayrollFiltersToolbar } from '@/components/payroll/PayrollFiltersToolbar';
import { PayrollPeriodWorkflowCard, PayrollStateObligationsCard } from '@/components/payroll/PayrollOperationalCards';
import { PayrollTemplateConfigPanel } from '@/components/payroll/PayrollTemplateConfigPanel';
import { safeRequest } from '@/lib/safeRequest';
import { useFetchTelemetry } from '@/hooks/useFetchTelemetry';
import { 
  Plus, Users, DollarSign, Calendar, Check, CreditCard, Trash2,
  Eye, Receipt, Wallet, TrendingUp, AlertCircle, FileText, Download, Shield, Edit3,
  Percent, Clock, Minus, ArrowRight, RefreshCw, Filter, Search, Settings,
  CircleDollarSign, Calculator, UserCheck, AlertTriangle,
  Building2, User, Briefcase, Landmark, Target, ChevronRight
} from 'lucide-react';
import { formatCurrency, formatDateTime, cn } from '@/lib/utils';
import { useAuth } from '@/hooks/useAuth';
import { useLanguage } from '@/i18n';

// Status configuration
const STATUS_CONFIG = {
  draft: { label: 'Gerado', color: 'bg-slate-100 text-slate-700 border-slate-200', icon: FileText },
  confirmed: { label: 'Revisto', color: 'bg-blue-100 text-blue-700 border-blue-200', icon: Check },
  paid: { label: 'Processado', color: 'bg-green-100 text-green-700 border-green-200', icon: CreditCard },
};

// Month names in Portuguese
const MONTHS = [
  { value: 1, label: 'Janeiro' },
  { value: 2, label: 'Fevereiro' },
  { value: 3, label: 'Março' },
  { value: 4, label: 'Abril' },
  { value: 5, label: 'Maio' },
  { value: 6, label: 'Junho' },
  { value: 7, label: 'Julho' },
  { value: 8, label: 'Agosto' },
  { value: 9, label: 'Setembro' },
  { value: 10, label: 'Outubro' },
  { value: 11, label: 'Novembro' },
  { value: 12, label: 'Dezembro' },
];

const NOTICE_PAY_OPTIONS = [
  { value: '0', label: 'Não aplicar' },
  { value: '7', label: '7 dias' },
  { value: '15', label: '15 dias' },
  { value: '30', label: '30 dias' },
];

const formatPayrollShortDate = (value) => {
  if (!value) return '—';
  const [year, month, day] = String(value).slice(0, 10).split('-');
  if (!year || !month || !day) return value;
  return `${day}/${month}/${year}`;
};

const isEmployeeEligibleForPayrollProcessing = (employee, month, year) => {
  if (!employee) return false;
  if (!employee.contract_end_date) return employee.payroll_eligible !== false || employee.is_active !== false;

  const [exitYear, exitMonth] = String(employee.contract_end_date).slice(0, 7).split('-').map(Number);
  if (!exitYear || !exitMonth) return employee.payroll_eligible !== false || employee.is_active !== false;

  const targetKey = (Number(year) * 100) + Number(month);
  const exitKey = (exitYear * 100) + exitMonth;
  return targetKey <= exitKey;
};

const MARITAL_STATUS_OPTIONS = ['Solteiro(a)', 'Casado(a)', 'União de Facto', 'Divorciado(a)', 'Viúvo(a)', 'Separado(a)'];
const GENDER_OPTIONS = ['Masculino', 'Feminino', 'Outro', 'Prefiro não informar'];
const DOCUMENT_IDENTIFICATION_TYPE_OPTIONS = [
  'D.I.R.E.',
  'CERTIDÃO DE NASCIMENTO',
  'BILHETE DE IDENTIDADE',
  'CÉDULA PESSOAL',
  'PASSAPORTE',
  'ASSENTO DE NASCIMENTO',
  'BOLETIM DE NASCIMENTO',
  'TALÃO DO BILHETE DE IDENTIDADE',
  'DESCONHECIDO',
];

const EMPTY_PAYROLL_EMPLOYEE_FORM = {
  name: '',
  email: '',
  phone: '',
  employee_number: '',
  nuit: '',
  inss_number: '',
  birth_date: '',
  marital_status: '',
  gender: '',
  father_name: '',
  mother_name: '',
  nationality: 'Moçambicana',
  address: '',
  emergency_contact: '',
  emergency_phone: '',
  department: '',
  job_title: '',
  store_location: '',
  hire_date: '',
  contract_type: '',
  contract_end_date: '',
  bank_name: '',
  bank_account: '',
  bank_nib: '',
  base_salary: '',
  attendance_bonus: '0',
  performance_bonus: '0',
  other_bonuses: '',
  document_ident_number: '',
  document_ident_type: '',
  document_issuer_service: '',
  bi_number: '',
  selected_profile: 'base:staff',
  can_sell: false,
};

// Contract types
const CONTRACT_TYPES = [
  { value: 'efectivo', label: 'Efectivo (Permanente)' },
  { value: 'prazo', label: 'Contrato a Prazo' },
  { value: 'temporario', label: 'Temporário' },
  { value: 'estagio', label: 'Estágio' },
];

const DEFAULT_PAYROLL_EDIT_FORM = {
  base_salary: '',
  overtime_hours: '0',
  overtime_rate: '1.5',
  manual_commission: '',
  attendance_bonus: '0',
  performance_bonus: '0',
  other_bonuses: '0',
  transport_allowance: '0',
  meal_allowance: '0',
  other_allowances: '0',
  advance_deduction: '0',
  other_deductions: '0',
  notes: '',
};

const DEFAULT_IRPS_BRACKET = {
  min_value: '0',
  max_value: '',
  rate: '0',
  deduction: '0',
};

const PAYROLL_SORT_OPTIONS = [
  { value: 'employee_name', label: 'Nome' },
  { value: 'payroll_number', label: 'Número' },
  { value: 'employee_role', label: 'Cargo / perfil' },
  { value: 'base_salary', label: 'Salário base' },
  { value: 'gross_salary', label: 'Salário bruto' },
  { value: 'net_salary', label: 'Salário líquido' },
  { value: 'status', label: 'Estado' },
];

const EMPLOYEE_SORT_OPTIONS = [
  { value: 'name', label: 'Nome' },
  { value: 'employee_number', label: 'Nº colaborador' },
  { value: 'completion', label: 'Dossier' },
  { value: 'base_salary', label: 'Salário base' },
  { value: 'role', label: 'Cargo / perfil' },
];

const ATTENDANCE_SORT_OPTIONS = [
  { value: 'date', label: 'Data' },
  { value: 'employee', label: 'Funcionário' },
  { value: 'hours', label: 'Horas' },
  { value: 'location', label: 'Loja' },
  { value: 'status', label: 'Estado' },
];

const PAYROLL_TEMPLATE_FALLBACK_DOCUMENTS = [
  { key: 'receipt', label: 'Recibo de Salário' },
  { key: 'salary_sheet', label: 'Folha Salarial RH' },
  { key: 'nominal', label: 'Relação Nominal' },
  { key: 'attendance_map', label: 'Mapa de Assiduidade' },
];

const PAYROLL_TEMPLATE_PRESETS = [
  { value: 'compact', label: 'A4 compacto' },
  { value: 'institutional', label: 'Institucional' },
  { value: 'wide_signatures', label: 'Assinaturas amplas' },
];

const buildTemplatePresetOverrides = (documentKey, preset) => {
  if (preset === 'compact') {
    return {
      left_margin_cm: 0.35,
      right_margin_cm: 0.35,
      top_margin_cm: 0.35,
      bottom_margin_cm: 0.35,
      table_side_margin_cm: 0.2,
      header_top_offset_mm: 0,
      split_gap_cm: documentKey === 'receipt' ? 1.2 : 0.5,
      signature_space_mm: documentKey === 'receipt' ? 10 : 12,
      show_logo: true,
      show_footer: documentKey === 'salary_sheet',
      repeat_header: documentKey !== 'receipt',
      title_font_size_pt: documentKey === 'receipt' ? 10 : 13.5,
      subtitle_font_size_pt: 7,
      table_font_size_pt: documentKey === 'attendance_map' ? 6.4 : 6.8,
    };
  }

  if (preset === 'wide_signatures') {
    return {
      left_margin_cm: 0.55,
      right_margin_cm: 0.55,
      top_margin_cm: 0.5,
      bottom_margin_cm: 0.9,
      table_side_margin_cm: 0.45,
      header_top_offset_mm: 0,
      split_gap_cm: documentKey === 'receipt' ? 2.6 : 0.6,
      signature_space_mm: documentKey === 'receipt' ? 24 : 20,
      show_logo: true,
      show_footer: documentKey === 'salary_sheet',
      repeat_header: documentKey !== 'receipt',
      title_font_size_pt: documentKey === 'receipt' ? 11 : 14.6,
      subtitle_font_size_pt: 7.6,
      table_font_size_pt: documentKey === 'attendance_map' ? 6.8 : 7.2,
    };
  }

  return {
    left_margin_cm: 0.5,
    right_margin_cm: 0.5,
    top_margin_cm: documentKey === 'salary_sheet' ? 0.45 : 0.5,
    bottom_margin_cm: 0.5,
    table_side_margin_cm: 0.5,
    header_top_offset_mm: 0,
    split_gap_cm: documentKey === 'receipt' ? 2 : 0.5,
    signature_space_mm: documentKey === 'receipt' ? 15 : 15,
    show_logo: true,
    show_footer: documentKey === 'salary_sheet',
    repeat_header: documentKey !== 'receipt',
    title_font_size_pt: documentKey === 'receipt' ? 10.8 : 14.6,
    subtitle_font_size_pt: documentKey === 'receipt' ? 7.1 : 7.6,
    table_font_size_pt: documentKey === 'receipt' ? 6.8 : documentKey === 'attendance_map' ? 6.8 : 7.2,
  };
};

const normalizeText = (value) => String(value || '').trim().toLowerCase();
const getPayrollDisplayReference = (payroll) => payroll?.display_reference || payroll?.payroll_number || payroll?.id || '—';
const formatShortDate = (value) => {
  if (!value) return '—';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return String(value);
  return parsed.toLocaleDateString('pt-MZ');
};

const normalizeComparable = (value) => {
  if (value === null || value === undefined || value === '') return '';
  if (typeof value === 'number') return value;
  if (typeof value === 'boolean') return value ? 1 : 0;
  if (value instanceof Date) return value.getTime();

  const stringValue = String(value).trim();
  if (!stringValue) return '';

  const numericValue = Number(stringValue);
  if (!Number.isNaN(numericValue) && stringValue !== '') {
    return numericValue;
  }

  const parsedDate = Date.parse(stringValue);
  if (!Number.isNaN(parsedDate) && /\d{4}-\d{2}-\d{2}|\d{2}\/\d{2}\/\d{4}/.test(stringValue)) {
    return parsedDate;
  }

  return stringValue.toLowerCase();
};

const sortRows = (rows, direction, resolver) => {
  const directionFactor = direction === 'asc' ? 1 : -1;

  return [...rows].sort((left, right) => {
    const leftValue = normalizeComparable(resolver(left));
    const rightValue = normalizeComparable(resolver(right));

    if (leftValue === rightValue) return 0;
    if (leftValue === '') return 1;
    if (rightValue === '') return -1;

    return leftValue > rightValue ? directionFactor : -directionFactor;
  });
};

const moveOrderedItem = (items, itemKey, direction) => {
  const ordered = [...items].sort((left, right) => (left.order ?? 0) - (right.order ?? 0));
  const currentIndex = ordered.findIndex((item) => item.key === itemKey);
  if (currentIndex === -1) return items;
  const targetIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;
  if (targetIndex < 0 || targetIndex >= ordered.length) return items;
  [ordered[currentIndex], ordered[targetIndex]] = [ordered[targetIndex], ordered[currentIndex]];
  return ordered.map((item, index) => ({ ...item, order: index }));
};

const createPayrollEditForm = (payroll) => ({
  base_salary: String(payroll?.base_salary ?? ''),
  overtime_hours: String(payroll?.overtime_hours ?? 0),
  overtime_rate: String(payroll?.overtime_rate ?? 1.5),
  manual_commission: payroll?.commission_data ? '' : String(payroll?.commission ?? ''),
  attendance_bonus: String(payroll?.attendance_bonus ?? 0),
  performance_bonus: String(payroll?.performance_bonus ?? 0),
  other_bonuses: String(payroll?.other_bonuses ?? 0),
  transport_allowance: String(payroll?.transport_allowance ?? 0),
  meal_allowance: String(payroll?.meal_allowance ?? 0),
  other_allowances: String(payroll?.other_allowances ?? 0),
  advance_deduction: String(payroll?.advance_deduction ?? 0),
  other_deductions: String(payroll?.other_deductions ?? 0),
  notes: payroll?.notes || '',
});

const downloadBlobFile = (blob, filename) => {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
};

const downloadCsvFile = (filename, headers, rows) => {
  const csv = [headers, ...rows]
    .map((row) => row.map((field) => `"${String(field ?? '').replace(/"/g, '""')}"`).join(';'))
    .join('\n');
  const blob = new Blob([`\uFEFF${csv}`], { type: 'text/csv;charset=utf-8;' });
  downloadBlobFile(blob, filename);
};

const openBlobInNewTab = (blob) => {
  const url = window.URL.createObjectURL(blob);
  window.open(url, '_blank', 'noopener,noreferrer');
  window.setTimeout(() => window.URL.revokeObjectURL(url), 60_000);
};

const buildPayrollDueDates = (month, year) => {
  const dueMonth = month === 12 ? 1 : month + 1;
  const dueYear = month === 12 ? year + 1 : year;
  return {
    inss: `10/${String(dueMonth).padStart(2, '0')}/${dueYear}`,
    irps: `20/${String(dueMonth).padStart(2, '0')}/${dueYear}`,
  };
};

const buildPayrollOperationalSummary = (rows, month, year) => {
  const totals = rows.reduce((accumulator, row) => {
    accumulator.baseSalary += Number(row.base_salary || 0);
    accumulator.bonuses += Number(row.total_bonuses ?? row.bonuses ?? 0);
    accumulator.deductions += Number(row.total_deductions ?? row.deductions ?? 0);
    accumulator.grossSalary += Number(row.gross_salary || 0);
    accumulator.netSalary += Number(row.net_salary || 0);
    accumulator.inssEmployee += Number(row.inss_employee || 0);
    accumulator.inssEmployer += Number(row.inss_employer || 0);
    accumulator.irps += Number(row.irps_retention || 0);
    accumulator.generated += row.status === 'draft' ? 1 : 0;
    accumulator.reviewed += row.status === 'confirmed' ? 1 : 0;
    accumulator.processed += row.status === 'paid' ? 1 : 0;
    return accumulator;
  }, { baseSalary: 0, bonuses: 0, deductions: 0, grossSalary: 0, netSalary: 0, inssEmployee: 0, inssEmployer: 0, irps: 0, generated: 0, reviewed: 0, processed: 0 });

  const totalRows = rows.length;
  const periodState = totalRows === 0
    ? 'Sem folhas geradas'
    : totals.processed === totalRows
      ? 'Período processado'
      : totals.reviewed + totals.processed === totalRows
        ? 'Período revisto'
        : 'Período gerado';

  return {
    totalRows,
    periodState,
    dueDates: buildPayrollDueDates(month, year),
    note: 'Consolidação contributiva do período para RH e finanças.',
    irpsPending: rows.some((row) => row.irps_table_loaded === false),
    irpsNote: rows.some((row) => row.irps_table_loaded === false)
      ? 'IRPS do período depende de tabela activa para o ano seleccionado. A folha pode ser gerada, mas não deve ser fechada como revista/processada sem essa tabela.'
      : 'IRPS calculado com tabela activa do ano seleccionado.',
    totals: {
      ...totals,
      inssState: totals.inssEmployee + totals.inssEmployer,
    },
  };
};

export default function PayrollPage() {
  // State
  const [payrolls, setPayrolls] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [eligibleEmployees, setEligibleEmployees] = useState({ total: 0, eligible: 0, incomplete: 0, employees: [] });
  const [attendanceRecords, setAttendanceRecords] = useState([]);
  const [attendanceShifts, setAttendanceShifts] = useState([]);
  const [attendanceReport, setAttendanceReport] = useState({ summary: {}, by_employee: [], by_store: [] });
  const [attendanceWeeklyMap, setAttendanceWeeklyMap] = useState({ week_start: '', week_end: '', rows: [] });
  const [justifiedAbsences, setJustifiedAbsences] = useState([]);
  const [attendanceClosures, setAttendanceClosures] = useState([]);
  const [attendanceBootstrapStatus, setAttendanceBootstrapStatus] = useState({ employees_ready: 0, counts: { attendance_records: 0, manual_records: 0, imported_records: 0, justified_absences: 0, attendance_shifts: 0, month_closures: 0 }, is_empty_operational_data: true, ready_for_real_launch: false, notes: '' });
  const [locations, setLocations] = useState([]);
  const [attendanceLoading, setAttendanceLoading] = useState(false);
  const [attendanceLoadedKey, setAttendanceLoadedKey] = useState('');
  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState({ count: 0, total_gross: 0, total_net: 0, total_inss_employee: 0, total_inss_employer: 0, total_irps: 0 });
  const [activeTab, setActiveTab] = useState('payrolls');
  
  // Configurations
  const [inssConfig, setInssConfig] = useState({ employee_rate: 0.03, employer_rate: 0.04 });
  const [companyGoal, setCompanyGoal] = useState({ monthly_goal: 2000000, commission_rate: 0.015 });
  const [irpsTables, setIrpsTables] = useState([]);
  const [configSubmitting, setConfigSubmitting] = useState(false);
  const [inssForm, setInssForm] = useState({ employee_rate: '3', employer_rate: '4' });
  const [companyGoalForm, setCompanyGoalForm] = useState({ monthly_goal: '2000000', commission_rate: '1.5' });
  const [irpsEditorMode, setIrpsEditorMode] = useState('create');
  const [irpsEditor, setIrpsEditor] = useState({ id: null, year: String(new Date().getFullYear()), is_active: true, notes: '', brackets: [{ ...DEFAULT_IRPS_BRACKET }] });
  
  // Filters
  const [employeeFilter, setEmployeeFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [periodMonth, setPeriodMonth] = useState(new Date().getMonth() + 1);
  const [periodYear, setPeriodYear] = useState(new Date().getFullYear());
  const [searchQuery, setSearchQuery] = useState('');
  const [payrollSortField, setPayrollSortField] = useState('employee_name');
  const [payrollSortDirection, setPayrollSortDirection] = useState('asc');
  const [employeeSearchQuery, setEmployeeSearchQuery] = useState('');
  const [employeeStatusFilter, setEmployeeStatusFilter] = useState('all');
  const [employeeSortField, setEmployeeSortField] = useState('name');
  const [employeeSortDirection, setEmployeeSortDirection] = useState('asc');
  const [attendanceSearchQuery, setAttendanceSearchQuery] = useState('');
  const [attendanceSortField, setAttendanceSortField] = useState('date');
  const [attendanceSortDirection, setAttendanceSortDirection] = useState('desc');
  const [attendanceFile, setAttendanceFile] = useState(null);
  const [attendanceUploading, setAttendanceUploading] = useState(false);
  const [shiftForm, setShiftForm] = useState({ id: null, store_location: '', employee_number: '', weekday: 'all', shift_name: 'Turno padrão', work_start: '08:00', lunch_start: '12:00', lunch_end: '13:00', work_end: '17:00', tolerance_minutes: 10 });
  const [absenceForm, setAbsenceForm] = useState({ id: null, employee_number: '', date: '', reason: '', is_paid: true });
  const [attendanceEditForm, setAttendanceEditForm] = useState({ id: null, employee_number: '', employee_name: '', work_start: '', lunch_start: '', lunch_end: '', work_end: '' });
  const [manualAttendanceForm, setManualAttendanceForm] = useState({ employee_number: '', date: '', work_start: '08:00', lunch_start: '12:00', lunch_end: '13:00', work_end: '17:00', notes: '' });
  const [attendanceResetIncludesShifts, setAttendanceResetIncludesShifts] = useState(true);
  const [reportScope, setReportScope] = useState('company');
  const [reportStoreLocation, setReportStoreLocation] = useState('all');
  const [reportEmployeeNumber, setReportEmployeeNumber] = useState('all');
  const [documentPayrollId, setDocumentPayrollId] = useState('');
  const [documentsLoading, setDocumentsLoading] = useState(false);
  const [templateDocuments, setTemplateDocuments] = useState(PAYROLL_TEMPLATE_FALLBACK_DOCUMENTS);
  const [templateSettings, setTemplateSettings] = useState({});
  const [selectedTemplateDocument, setSelectedTemplateDocument] = useState('receipt');
  const [selectedTemplatePreset, setSelectedTemplatePreset] = useState('institutional');
  const [reportsSubTab, setReportsSubTab] = useState('documents');
  const [templateSettingsLoading, setTemplateSettingsLoading] = useState(false);
  const [templateSettingsSaving, setTemplateSettingsSaving] = useState(false);
  const [templatePreviewLoading, setTemplatePreviewLoading] = useState(false);
  const [templateExcelLoading, setTemplateExcelLoading] = useState(false);
  const [salarySheetPreview, setSalarySheetPreview] = useState({ groups: [], totals: { base_salary: 0, gross_salary: 0, bonuses: 0, deductions: 0, net_salary: 0 }, total_rows: 0, period: { month: new Date().getMonth() + 1, year: new Date().getFullYear() } });
  const [nominalPreview, setNominalPreview] = useState({ groups: [], total_rows: 0 });
  const [roleAlignmentStats, setRoleAlignmentStats] = useState({ employees_aligned: 0, divergences_found: 0, payroll_records_aligned: 0, exceptions_found: 0, exceptions: [] });
  const [attendanceDocumentPreview, setAttendanceDocumentPreview] = useState({ summary: {}, by_employee: [], by_store: [], days: [] });
  const [vacationYear, setVacationYear] = useState(new Date().getFullYear());
  const [vacationsLoading, setVacationsLoading] = useState(false);
  const [vacationsDashboard, setVacationsDashboard] = useState({ policy: null, requests: [], individual_plans: [], general_plan: [], summary: {} });
  const [selectedVacationEmployeeNumber, setSelectedVacationEmployeeNumber] = useState('all');
  const [vacationRequestForm, setVacationRequestForm] = useState({ employee_id: '', start_date: '', end_date: '', notes: '' });
  const [vacationPolicyForm, setVacationPolicyForm] = useState({ active_regime_key: 'mozambique_law_13_2023_current', first_year_days: '12', subsequent_year_days: '30', fixed_term_days_per_month: '1', fixed_term_min_months: '3', fixed_term_max_months: '12', notes: '' });
  const [editingPayroll, setEditingPayroll] = useState(null);
  const [payrollEditDialogOpen, setPayrollEditDialogOpen] = useState(false);
  const [payrollEditForm, setPayrollEditForm] = useState(DEFAULT_PAYROLL_EDIT_FORM);
  
  // Dialogs
  const [processDialogOpen, setProcessDialogOpen] = useState(false);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [markPaidDialogOpen, setMarkPaidDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [dossierDialogOpen, setDossierDialogOpen] = useState(false);
  const [configDialogOpen, setConfigDialogOpen] = useState(false);
  const [selectedPayroll, setSelectedPayroll] = useState(null);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [selectedPayrollIds, setSelectedPayrollIds] = useState([]);
  const [selectedEmployeeIds, setSelectedEmployeeIds] = useState([]);
  const [employeeDossier, setEmployeeDossier] = useState(null);
  const [employeeDirectory, setEmployeeDirectory] = useState({ total: 0, eligible: 0, incomplete: 0, employees: [] });
  const [payrollEmployeeDialogOpen, setPayrollEmployeeDialogOpen] = useState(false);
  const [creatingPayrollEmployee, setCreatingPayrollEmployee] = useState(false);
  const [payrollEmployeeForm, setPayrollEmployeeForm] = useState(EMPTY_PAYROLL_EMPLOYEE_FORM);
  const [payrollBaseRoles, setPayrollBaseRoles] = useState([]);
  
  // Form state for processing payroll
  const initialFormData = {
    employee_id: '',
    period_month: new Date().getMonth() + 1,
    period_year: new Date().getFullYear(),
    base_salary: '',
    manual_days_worked: '',
    notice_pay_days: '0',
    overtime_hours: '0',
    overtime_rate: '1.5',
    attendance_bonus: '0',
    performance_bonus: '0',
    other_bonuses: '0',
    transport_allowance: '0',
    meal_allowance: '0',
    other_allowances: '0',
    advance_deduction: '0',
    other_deductions: '0',
    manual_commission: '',
    notes: ''
  };
  const [formData, setFormData] = useState(initialFormData);
  const [simulationResult, setSimulationResult] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  
  // Dossier form
  const [dossierForm, setDossierForm] = useState({
    name: '',
    email: '',
    phone: '',
    employee_number: '',
    bi_number: '',
    document_ident_number: '',
    document_ident_type: '',
    document_issuer_service: '',
    nuit: '',
    inss_number: '',
    birth_date: '',
    marital_status: '',
    gender: '',
    father_name: '',
    mother_name: '',
    nationality: 'Moçambicana',
    address: '',
    emergency_contact: '',
    emergency_phone: '',
    department: '',
    job_title: '',
    store_location: '',
    education_level: '',
    dependents_count: '0',
    profile_role_key: '',
    profile_role_source: '',
    hire_date: '',
    contract_type: '',
    contract_end_date: '',
    exit_note: '',
    bank_name: '',
    bank_account: '',
    bank_nib: '',
    base_salary: '',
    attendance_bonus: '0',
    performance_bonus: '0',
    other_bonuses: ''
  });
  const [reintegrationForm, setReintegrationForm] = useState({ date: '', note: '' });
  
  const { hasRole, hasPermission, user } = useAuth();
  const { t } = useLanguage();

  const buildIrpsEditorState = useCallback((table = null) => ({
    id: table?.id || null,
    year: String(table?.year || new Date().getFullYear()),
    is_active: table?.is_active ?? true,
    notes: table?.notes || '',
    brackets: table?.brackets?.length
      ? table.brackets.map((bracket) => ({
          min_value: String(bracket.min_value ?? 0),
          max_value: bracket.max_value === null || bracket.max_value === undefined ? '' : String(bracket.max_value),
          rate: String((bracket.rate ?? 0) * 100),
          deduction: String(bracket.deduction ?? 0),
        }))
      : [{ ...DEFAULT_IRPS_BRACKET }],
  }), []);
  const isAdmin = hasRole(['admin']);
  const isManagerReviewer = hasRole(['admin', 'accountant', 'store_manager', 'supervisor']);
  const canViewPayroll = hasPermission('payroll.view') || hasPermission('payroll.edit') || hasRole(['admin', 'accountant']);
  const canManagePayroll = hasPermission('payroll.edit') || hasRole(['admin', 'accountant']);
  const canProcess = canManagePayroll;
  const canConfirm = canManagePayroll;
  const canMarkPaid = canManagePayroll;

  // Filtered payrolls
  const filteredPayrolls = useMemo(() => {
    return payrolls.filter(p => {
      if (employeeFilter !== 'all' && p.employee_id !== employeeFilter) return false;
      if (statusFilter !== 'all' && p.status !== statusFilter) return false;
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        if (!getPayrollDisplayReference(p).toLowerCase().includes(query) && 
            !p.employee_name?.toLowerCase().includes(query) &&
            !String(p.employee_number || '').toLowerCase().includes(query)) return false;
      }
      return true;
    });
  }, [payrolls, employeeFilter, statusFilter, searchQuery]);

  const filteredSummary = useMemo(() => {
    return filteredPayrolls.reduce((accumulator, payroll) => {
      accumulator.totalGross += Number(payroll.gross_salary || 0);
      accumulator.totalNet += Number(payroll.net_salary || 0);
      accumulator.totalIrps += Number(payroll.irps_retention || 0);
      accumulator.totalInss += Number(payroll.inss_employee || 0) + Number(payroll.inss_employer || 0);
      accumulator.draft += payroll.status === 'draft' ? 1 : 0;
      accumulator.confirmed += payroll.status === 'confirmed' ? 1 : 0;
      accumulator.paid += payroll.status === 'paid' ? 1 : 0;
      return accumulator;
    }, { totalGross: 0, totalNet: 0, totalIrps: 0, totalInss: 0, draft: 0, confirmed: 0, paid: 0 });
  }, [filteredPayrolls]);
  const payrollOperationalSummary = useMemo(() => buildPayrollOperationalSummary(payrolls, periodMonth, periodYear), [payrolls, periodMonth, periodYear]);
  const selectedProcessEmployee = useMemo(() => employees.find((employee) => employee.id === formData.employee_id) || null, [employees, formData.employee_id]);
  const processEligibleEmployees = useMemo(() => employees.filter((employee) => isEmployeeEligibleForPayrollProcessing(employee, formData.period_month, formData.period_year)), [employees, formData.period_month, formData.period_year]);
  const processExitMonthContext = useMemo(() => {
    if (!selectedProcessEmployee?.contract_end_date) return null;
    const [year, month, day] = String(selectedProcessEmployee.contract_end_date).slice(0, 10).split('-').map(Number);
    if (!year || !month || !day) return null;
    return {
      isExitMonth: month === Number(formData.period_month) && year === Number(formData.period_year),
      endDate: selectedProcessEmployee.contract_end_date,
      automaticWorkedDays: Math.min(day, 30),
    };
  }, [selectedProcessEmployee, formData.period_month, formData.period_year]);

  useEffect(() => {
    if (formData.employee_id && !processEligibleEmployees.some((employee) => employee.id === formData.employee_id)) {
      setFormData((current) => ({ ...current, employee_id: '' }));
      setSimulationResult(null);
    }
  }, [formData.employee_id, processEligibleEmployees]);
  const hasIrpsTableForSelectedYear = useMemo(() => irpsTables.some((table) => Number(table.year) === Number(periodYear) && table.is_active), [irpsTables, periodYear]);
  const selectedMonthLabel = useMemo(() => MONTHS.find((month) => month.value === periodMonth)?.label || 'Período', [periodMonth]);
  const reportsOperationalSummary = useMemo(() => {
    if (!salarySheetPreview?.state_obligations) return payrollOperationalSummary;

    const stateLabelMap = {
      sem_folhas: 'Sem folhas geradas',
      gerado: 'Período gerado',
      revisto: 'Período revisto',
      processado: 'Período processado',
    };

    return {
      totalRows: salarySheetPreview.total_rows || 0,
      periodState: stateLabelMap[salarySheetPreview.processing?.period_state] || payrollOperationalSummary.periodState,
      dueDates: salarySheetPreview.state_obligations?.due_dates || payrollOperationalSummary.dueDates,
      note: salarySheetPreview.state_obligations?.note || payrollOperationalSummary.note,
      irpsPending: salarySheetPreview.state_obligations?.irps_table_ready === false,
      irpsNote: salarySheetPreview.state_obligations?.irps_note || payrollOperationalSummary.irpsNote,
      totals: {
        baseSalary: Number(salarySheetPreview.totals?.base_salary || 0),
        bonuses: Number(salarySheetPreview.totals?.bonuses || 0),
        deductions: Number(salarySheetPreview.totals?.deductions || 0),
        grossSalary: Number(salarySheetPreview.totals?.gross_salary || 0),
        netSalary: Number(salarySheetPreview.state_obligations?.net_salary_to_employees || salarySheetPreview.totals?.net_salary || 0),
        inssEmployee: Number(salarySheetPreview.state_obligations?.inss_employee_retained || 0),
        inssEmployer: Number(salarySheetPreview.state_obligations?.inss_employer || 0),
        inssState: Number(salarySheetPreview.state_obligations?.inss_total_state || 0),
        irps: Number(salarySheetPreview.state_obligations?.irps_total_state || 0),
        generated: Number(salarySheetPreview.processing?.status_counts?.generated || 0),
        reviewed: Number(salarySheetPreview.processing?.status_counts?.reviewed || 0),
        processed: Number(salarySheetPreview.processing?.status_counts?.processed || 0),
      },
    };
  }, [payrollOperationalSummary, salarySheetPreview]);

  const activePayrollFiltersCount = useMemo(() => {
    return [employeeFilter !== 'all', statusFilter !== 'all', Boolean(searchQuery)].filter(Boolean).length;
  }, [employeeFilter, statusFilter, searchQuery]);
  const sortedPayrolls = useMemo(() => sortRows(filteredPayrolls, payrollSortDirection, (payroll) => {
    if (payrollSortField === 'status') return STATUS_CONFIG[payroll.status]?.label || payroll.status;
    if (payrollSortField === 'employee_role') return payroll.employee_role || payroll.role || '';
    if (payrollSortField === 'payroll_number') return getPayrollDisplayReference(payroll);
    return payroll[payrollSortField];
  }), [filteredPayrolls, payrollSortDirection, payrollSortField]);
  const selectedDocumentPayroll = useMemo(() => sortedPayrolls.find((payroll) => payroll.id === documentPayrollId) || sortedPayrolls[0] || null, [documentPayrollId, sortedPayrolls]);
  const selectedDocumentPayrollPreview = useMemo(() => {
    if (!selectedDocumentPayroll) return null;

    const directoryEmployee = employees.find((employee) => employee.id === selectedDocumentPayroll.employee_id);

    return {
      employeeNumber: selectedDocumentPayroll.employee_number || directoryEmployee?.employee_number || '—',
      role: selectedDocumentPayroll.role || selectedDocumentPayroll.employee_role || directoryEmployee?.profile_role_label || directoryEmployee?.role || '—',
      baseSalary: Number(selectedDocumentPayroll.base_salary || 0),
      monthlyBaseSalary: Number(selectedDocumentPayroll.monthly_base_salary || selectedDocumentPayroll.base_salary || 0),
      daysWorked: selectedDocumentPayroll.days_worked ?? 30,
      dailyRate: Number(selectedDocumentPayroll.daily_base_salary || (Number(selectedDocumentPayroll.monthly_base_salary || selectedDocumentPayroll.base_salary || 0) / 30) || 0),
      grossSalary: Number(selectedDocumentPayroll.gross_salary || 0),
      bonuses: Number(selectedDocumentPayroll.bonuses ?? selectedDocumentPayroll.total_bonuses ?? 0),
      deductions: Number(selectedDocumentPayroll.deductions ?? selectedDocumentPayroll.total_deductions ?? 0),
      netSalary: Number(selectedDocumentPayroll.net_salary || 0),
    };
  }, [employees, selectedDocumentPayroll]);
  const currentTemplateSettings = useMemo(() => templateSettings?.[selectedTemplateDocument] || {}, [selectedTemplateDocument, templateSettings]);
  const activeEmployeeFiltersCount = useMemo(() => [Boolean(employeeSearchQuery), employeeStatusFilter !== 'all'].filter(Boolean).length, [employeeSearchQuery, employeeStatusFilter]);
  const filteredEmployees = useMemo(() => {
    const query = normalizeText(employeeSearchQuery);
    return (employeeDirectory.employees || []).filter((employee) => {
      if (employeeStatusFilter === 'complete' && !employee.dossier_complete) return false;
      if (employeeStatusFilter === 'incomplete' && employee.dossier_complete) return false;
      if (!query) return true;

      return [
        employee.full_name,
        employee.name,
        employee.employee_number,
        employee.role,
        employee.employment_state,
      ].some((value) => normalizeText(value).includes(query));
    });
  }, [employeeDirectory.employees, employeeSearchQuery, employeeStatusFilter]);
  const sortedEmployees = useMemo(() => sortRows(filteredEmployees, employeeSortDirection, (employee) => {
    switch (employeeSortField) {
      case 'name':
        return employee.full_name || employee.name;
      case 'employee_number':
        return employee.employee_number;
      case 'completion':
        return employee.completion_percentage;
      case 'base_salary':
        return employee.base_salary;
      case 'role':
        return employee.role;
      default:
        return employee.full_name || employee.name;
    }
  }), [employeeSortDirection, employeeSortField, filteredEmployees]);
  const selectableEmployees = useMemo(() => sortedEmployees.filter((employee) => isEmployeeEligibleForPayrollProcessing(employee, periodMonth, periodYear)), [periodMonth, periodYear, sortedEmployees]);
  const allVisiblePayrollsSelected = sortedPayrolls.length > 0 && sortedPayrolls.every((payroll) => selectedPayrollIds.includes(payroll.id));
  const allVisibleEmployeesSelected = selectableEmployees.length > 0 && selectableEmployees.every((employee) => selectedEmployeeIds.includes(employee.id));

  useEffect(() => {
    setSelectedPayrollIds((current) => current.filter((id) => sortedPayrolls.some((payroll) => payroll.id === id)));
  }, [sortedPayrolls]);

  useEffect(() => {
    setSelectedEmployeeIds((current) => current.filter((id) => sortedEmployees.some((employee) => employee.id === id)));
  }, [sortedEmployees]);

  const togglePayrollSelection = useCallback((payrollId, checked) => {
    setSelectedPayrollIds((current) => (checked
      ? (current.includes(payrollId) ? current : [...current, payrollId])
      : current.filter((id) => id !== payrollId)));
  }, []);

  const toggleEmployeeSelection = useCallback((employeeId, checked) => {
    setSelectedEmployeeIds((current) => (checked
      ? (current.includes(employeeId) ? current : [...current, employeeId])
      : current.filter((id) => id !== employeeId)));
  }, []);

  const toggleAllVisiblePayrolls = useCallback((checked) => {
    setSelectedPayrollIds(checked ? sortedPayrolls.map((payroll) => payroll.id) : []);
  }, [sortedPayrolls]);

  const resolveTargetPayrollRows = useCallback(() => {
    const targetIds = selectedPayrollIds.length ? selectedPayrollIds : sortedPayrolls.map((payroll) => payroll.id);
    const targetIdSet = new Set(targetIds);
    return sortedPayrolls.filter((payroll) => targetIdSet.has(payroll.id));
  }, [selectedPayrollIds, sortedPayrolls]);

  const toggleAllVisibleEmployees = useCallback((checked) => {
    setSelectedEmployeeIds(checked ? selectableEmployees.map((employee) => employee.id) : []);
  }, [selectableEmployees]);
  const activeAttendanceFiltersCount = useMemo(() => [Boolean(attendanceSearchQuery)].filter(Boolean).length, [attendanceSearchQuery]);
  const filteredAttendanceRecords = useMemo(() => {
    const query = normalizeText(attendanceSearchQuery);
    return attendanceRecords.filter((record) => {
      if (!query) return true;
      return [
        record.employee_number,
        record.employee_name,
        record.store_location,
        record.date,
        record.comparison?.status,
      ].some((value) => normalizeText(value).includes(query));
    });
  }, [attendanceRecords, attendanceSearchQuery]);
  const sortedAttendanceRecords = useMemo(() => sortRows(filteredAttendanceRecords, attendanceSortDirection, (record) => {
    switch (attendanceSortField) {
      case 'employee':
        return `${record.employee_name || ''} ${record.employee_number || ''}`;
      case 'hours':
        return Number(record.worked_hours || 0);
      case 'location':
        return record.store_location;
      case 'status':
        return record.comparison?.status;
      case 'date':
      default:
        return record.date;
    }
  }), [attendanceSortDirection, attendanceSortField, filteredAttendanceRecords]);
  const filteredAttendanceShifts = useMemo(() => {
    const query = normalizeText(attendanceSearchQuery);
    return attendanceShifts.filter((shift) => {
      if (!query) return true;
      return [shift.store_location, shift.employee_number, shift.shift_name, shift.weekday].some((value) => normalizeText(value).includes(query));
    });
  }, [attendanceSearchQuery, attendanceShifts]);
  const sortedAttendanceShifts = useMemo(() => sortRows(filteredAttendanceShifts, attendanceSortDirection, (shift) => {
    switch (attendanceSortField) {
      case 'employee':
        return shift.employee_number || 'Todos';
      case 'location':
        return shift.store_location;
      case 'status':
        return shift.shift_name;
      case 'hours':
        return `${shift.work_start || ''}-${shift.work_end || ''}`;
      case 'date':
      default:
        return shift.weekday ?? -1;
    }
  }), [attendanceSortDirection, attendanceSortField, filteredAttendanceShifts]);
  const filteredJustifiedAbsences = useMemo(() => {
    const query = normalizeText(attendanceSearchQuery);
    return justifiedAbsences.filter((absence) => {
      if (!query) return true;
      return [absence.employee_number, absence.reason, absence.date].some((value) => normalizeText(value).includes(query));
    });
  }, [attendanceSearchQuery, justifiedAbsences]);
  const sortedJustifiedAbsences = useMemo(() => sortRows(filteredJustifiedAbsences, attendanceSortDirection, (absence) => {
    switch (attendanceSortField) {
      case 'employee':
        return absence.employee_number;
      case 'status':
        return absence.reason;
      case 'date':
      default:
        return absence.date;
    }
  }), [attendanceSortDirection, attendanceSortField, filteredJustifiedAbsences]);
  const sortedAttendanceEmployeeReport = useMemo(() => sortRows(
    (attendanceReport.by_employee || []).filter((row) => {
      const query = normalizeText(attendanceSearchQuery);
      if (!query) return true;
      return [row.employee_number, row.employee_name, row.store_location].some((value) => normalizeText(value).includes(query));
    }),
    attendanceSortDirection,
    (row) => {
      switch (attendanceSortField) {
        case 'employee':
          return `${row.employee_name || ''} ${row.employee_number || ''}`;
        case 'hours':
          return Number(row.total_hours || 0);
        case 'location':
          return row.store_location;
        case 'status':
          return Number(row.unjustified_absence_count || 0);
        case 'date':
        default:
          return Number(row.days || 0);
      }
    },
  ), [attendanceReport.by_employee, attendanceSearchQuery, attendanceSortDirection, attendanceSortField]);
  const sortedAttendanceStoreReport = useMemo(() => sortRows(
    (attendanceReport.by_store || []).filter((row) => {
      const query = normalizeText(attendanceSearchQuery);
      if (!query) return true;
      return normalizeText(row.store_location).includes(query);
    }),
    attendanceSortDirection,
    (row) => {
      switch (attendanceSortField) {
        case 'hours':
          return Number(row.total_hours || 0);
        case 'date':
          return Number(row.days || 0);
        case 'location':
        case 'employee':
        case 'status':
        default:
          return row.store_location;
      }
    },
  ), [attendanceReport.by_store, attendanceSearchQuery, attendanceSortDirection, attendanceSortField]);
  const sortedAttendanceWeeklyRows = useMemo(() => sortRows(
    (attendanceWeeklyMap.rows || []).filter((row) => {
      const query = normalizeText(attendanceSearchQuery);
      if (!query) return true;
      return [row.employee_number, row.employee_name].some((value) => normalizeText(value).includes(query));
    }),
    attendanceSortDirection,
    (row) => {
      switch (attendanceSortField) {
        case 'employee':
          return `${row.employee_name || ''} ${row.employee_number || ''}`;
        case 'hours':
          return Number(row.bank_hours || 0);
        case 'date':
        default:
          return row.week_start;
      }
    },
  ), [attendanceSearchQuery, attendanceSortDirection, attendanceSortField, attendanceWeeklyMap.rows]);
  const sortedAttendanceClosures = useMemo(() => sortRows(
    attendanceClosures.filter((closure) => {
      const query = normalizeText(attendanceSearchQuery);
      if (!query) return true;
      return [closure.status, closure.month, closure.year].some((value) => normalizeText(value).includes(query));
    }),
    'desc',
    (closure) => closure.last_updated_at || closure.closed_at || `${closure.year}-${String(closure.month || '').padStart(2, '0')}`,
  ), [attendanceClosures, attendanceSearchQuery]);
  const { partialIssues, captureFetchTelemetry, resetFetchTelemetry } = useFetchTelemetry();
  const currentPeriodKey = `${periodMonth}-${periodYear}`;
  const visiblePartialIssues = useMemo(() => (
    partialIssues.filter((issue) => {
      if (activeTab === 'attendance') return true;
      return ![
        'payroll.attendance',
        'payroll.attendance_shifts',
        'payroll.attendance_report',
        'payroll.attendance_weekly_map',
        'payroll.justified_absences',
        'payroll.attendance_closures',
        'settings.locations',
      ].includes(issue.endpoint);
    })
  ), [activeTab, partialIssues]);

  const normalizeLocationOptions = useCallback((response) => {
    const rows = Array.isArray(response) ? response : response?.data || [];
    return rows.filter((item) => item && !item.is_deleted);
  }, []);

  const employeeStoreLocationOptions = useMemo(() => (
    locations.filter((location) => location?.type === 'store' && location?.is_active !== false)
  ), [locations]);

  const loadAttendanceData = useCallback(async ({ force = false } = {}) => {
    if (!force && attendanceLoadedKey === currentPeriodKey) {
      return;
    }

    try {
      setAttendanceLoading(true);
      const [attendanceRes, shiftsRes, locationsRes, attendanceReportRes, weeklyMapRes, justifiedRes, closuresRes, bootstrapRes] = await Promise.all([
        safeRequest(() => payrollAPI.listAttendance({ month: periodMonth, year: periodYear }), { data: { records: [] } }, { retries: 1, required: true, telemetry: { module: 'payroll', endpoint: 'payroll.attendance', onEvent: captureFetchTelemetry } }),
        safeRequest(() => payrollAPI.listAttendanceShifts(), { data: { shifts: [] } }, { retries: 1, required: true, telemetry: { module: 'payroll', endpoint: 'payroll.attendance_shifts', onEvent: captureFetchTelemetry } }),
        safeRequest(() => settingsAPI.listLocations(false), { data: [] }, { retries: 1, required: true, telemetry: { module: 'payroll', endpoint: 'settings.locations', onEvent: captureFetchTelemetry } }),
        safeRequest(() => payrollAPI.getAttendanceReport({ month: periodMonth, year: periodYear }), { data: { summary: {}, by_employee: [], by_store: [] } }, { retries: 1, required: true, telemetry: { module: 'payroll', endpoint: 'payroll.attendance_report', onEvent: captureFetchTelemetry } }),
        safeRequest(() => payrollAPI.getAttendanceWeeklyMap({}), { data: { week_start: '', week_end: '', rows: [] } }, { retries: 1, required: true, telemetry: { module: 'payroll', endpoint: 'payroll.attendance_weekly_map', onEvent: captureFetchTelemetry } }),
        safeRequest(() => payrollAPI.listJustifiedAbsences({ month: periodMonth, year: periodYear }), { data: { records: [] } }, { retries: 1, required: true, telemetry: { module: 'payroll', endpoint: 'payroll.justified_absences', onEvent: captureFetchTelemetry } }),
        safeRequest(() => payrollAPI.listAttendanceMonthClosures(), { data: { records: [] } }, { retries: 1, required: true, telemetry: { module: 'payroll', endpoint: 'payroll.attendance_closures', onEvent: captureFetchTelemetry } }),
        safeRequest(() => payrollAPI.getAttendanceBootstrapStatus(), { data: { employees_ready: 0, counts: {} } }, { retries: 1, required: true, telemetry: { module: 'payroll', endpoint: 'payroll.attendance_bootstrap_status', onEvent: captureFetchTelemetry } }),
      ]);

      setAttendanceRecords(attendanceRes.data?.records || []);
      setAttendanceShifts(shiftsRes.data?.shifts || []);
      setLocations(normalizeLocationOptions(locationsRes));
      setAttendanceReport(attendanceReportRes.data || { summary: {}, by_employee: [], by_store: [] });
      setAttendanceWeeklyMap(weeklyMapRes.data || { week_start: '', week_end: '', rows: [] });
      setJustifiedAbsences(justifiedRes.data?.records || []);
      setAttendanceClosures(closuresRes.data?.records || []);
      setAttendanceBootstrapStatus(bootstrapRes.data || bootstrapRes);
      setAttendanceLoadedKey(currentPeriodKey);
    } catch (error) {
      console.error('Error loading attendance data:', error);
      toast.error(getErrorMessage(error, 'Erro ao carregar assiduidade'));
    } finally {
      setAttendanceLoading(false);
    }
  }, [attendanceLoadedKey, captureFetchTelemetry, currentPeriodKey, normalizeLocationOptions, periodMonth, periodYear]);

  const loadCoreData = useCallback(async () => {
    try {
      setLoading(true);
      resetFetchTelemetry();
      let alignmentStats = { employees_aligned: 0, divergences_found: 0, payroll_records_aligned: 0, exceptions_found: 0, exceptions: [] };
      if (canManagePayroll) {
        const alignmentRes = await safeRequest(
          () => payrollAPI.syncProfileRoleAlignment(),
          { data: alignmentStats },
          { retries: 1, required: false, telemetry: { module: 'payroll', endpoint: 'payroll.profile_role_alignment', onEvent: captureFetchTelemetry } },
        );
        alignmentStats = alignmentRes.data || alignmentRes || alignmentStats;
      }
      const [payrollsRes, eligibleRes, directoryRes, inssRes, goalRes, irpsRes, summaryRes, locationsRes] = await Promise.all([
        safeRequest(() => payrollAPI.list({ period_month: periodMonth, period_year: periodYear }), { data: [] }, { retries: 1, required: true, telemetry: { module: 'payroll', endpoint: 'payroll.list', onEvent: captureFetchTelemetry } }),
        safeRequest(() => payrollAPI.listEligibleEmployees({ include_inactive: true }), { data: { employees: [], total: 0, eligible: 0, incomplete: 0 } }, { retries: 1, required: true, telemetry: { module: 'payroll', endpoint: 'payroll.eligible_employees', onEvent: captureFetchTelemetry } }),
        safeRequest(() => payrollAPI.listEmployeeDirectory(), { data: { employees: [], total: 0, eligible: 0, incomplete: 0 } }, { retries: 1, required: true, telemetry: { module: 'payroll', endpoint: 'payroll.employee_directory', onEvent: captureFetchTelemetry } }),
        safeRequest(() => payrollAPI.getINSSConfig(), { data: { employee_rate: 0.03, employer_rate: 0.04 } }, { retries: 1, required: true, telemetry: { module: 'payroll', endpoint: 'payroll.inss_config', onEvent: captureFetchTelemetry } }),
        safeRequest(() => payrollAPI.getCompanyGoal(), { data: { monthly_goal: 2000000, commission_rate: 0.015 } }, { retries: 1, required: true, telemetry: { module: 'payroll', endpoint: 'payroll.company_goal', onEvent: captureFetchTelemetry } }),
        safeRequest(() => payrollAPI.getIRPSTables(), { data: { tables: [] } }, { retries: 1, required: true, telemetry: { module: 'payroll', endpoint: 'payroll.irps_tables', onEvent: captureFetchTelemetry } }),
        safeRequest(() => payrollAPI.getSummary({ period_month: periodMonth, period_year: periodYear }), { data: {} }, { retries: 1, required: true, telemetry: { module: 'payroll', endpoint: 'payroll.summary', onEvent: captureFetchTelemetry } }),
        safeRequest(() => settingsAPI.listLocations(false), { data: [] }, { retries: 1, required: false, telemetry: { module: 'payroll', endpoint: 'settings.locations', onEvent: captureFetchTelemetry } }),
      ]);
      
      setPayrolls(payrollsRes.data || payrollsRes || []);
      setEligibleEmployees(eligibleRes.data || eligibleRes);
      setEmployeeDirectory(directoryRes.data || directoryRes);
      setEmployees((eligibleRes.data?.employees || eligibleRes.employees || []).filter(Boolean).map((e) => {
        const displayName = e.full_name || e.name || '—';
        return {
          id: e.id,
          name: displayName,
          full_name: displayName,
          employee_number: e.employee_number,
          role: e.profile_role_label || e.role,
          profile_role_key: e.profile_role_key,
          profile_role_label: e.profile_role_label || e.role,
          store_location: e.store_location,
          base_salary: e.base_salary,
          attendance_bonus: e.attendance_bonus,
          performance_bonus: e.performance_bonus,
          other_bonuses: e.other_bonuses,
          dossier_complete: e.dossier_complete,
          completion_percentage: e.completion_percentage,
          required_documents_count: e.required_documents_count,
          completed_documents_count: e.completed_documents_count,
          contract_end_date: e.contract_end_date || '',
          employment_state: e.employment_state,
        };
      }));
      setInssConfig(inssRes.data || inssRes);
      setCompanyGoal(goalRes.data || goalRes);
      setIrpsTables((irpsRes.data?.tables || irpsRes.tables || []));
      setSummary(summaryRes.data || summaryRes || {});
      setLocations(normalizeLocationOptions(locationsRes));
      setRoleAlignmentStats(alignmentStats);
      setAttendanceRecords([]);
      setAttendanceShifts([]);
      setAttendanceReport({ summary: {}, by_employee: [], by_store: [] });
      setAttendanceWeeklyMap({ week_start: '', week_end: '', rows: [] });
      setJustifiedAbsences([]);
      setAttendanceClosures([]);
      setAttendanceLoadedKey('');
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error(getErrorMessage(error, 'Erro ao carregar dados de payroll'));
    } finally {
      setLoading(false);
    }
  }, [canManagePayroll, captureFetchTelemetry, normalizeLocationOptions, periodMonth, periodYear, resetFetchTelemetry]);

  const loadData = useCallback(async () => {
    await loadCoreData();
    if (activeTab === 'attendance') {
      await loadAttendanceData({ force: true });
    }
  }, [activeTab, loadAttendanceData, loadCoreData]);

  const loadDocumentsData = useCallback(async () => {
    const scopeStoreLocation = reportScope === 'store' && reportStoreLocation !== 'all' ? reportStoreLocation : undefined;
    const selectedEmployeeNumber = reportEmployeeNumber !== 'all' ? reportEmployeeNumber : undefined;

    try {
      setDocumentsLoading(true);
      setTemplateSettingsLoading(true);
      const [locationsRes, bootstrapRes, salaryRes, nominalRes, attendanceDocRes, templateSettingsRes] = await Promise.all([
        safeRequest(() => settingsAPI.listLocations(false), { data: [] }, { retries: 1, required: true, telemetry: { module: 'payroll', endpoint: 'settings.locations', onEvent: captureFetchTelemetry } }),
        safeRequest(() => payrollAPI.getAttendanceBootstrapStatus(), { data: { employees_ready: 0, counts: {} } }, { retries: 1, required: true, telemetry: { module: 'payroll', endpoint: 'payroll.attendance_bootstrap_status', onEvent: captureFetchTelemetry } }),
        safeRequest(() => payrollAPI.getSalarySheetPreview({ month: periodMonth, year: periodYear, scope: reportScope, store_location: scopeStoreLocation }), { data: { groups: [], totals: {}, total_rows: 0 } }, { retries: 1, required: true, telemetry: { module: 'payroll', endpoint: 'payroll.salary_sheet_preview', onEvent: captureFetchTelemetry } }),
        safeRequest(() => payrollAPI.getNominalPreview({ month: periodMonth, year: periodYear, scope: reportScope, store_location: scopeStoreLocation }), { data: { groups: [], total_rows: 0 } }, { retries: 1, required: true, telemetry: { module: 'payroll', endpoint: 'payroll.nominal_preview', onEvent: captureFetchTelemetry } }),
        safeRequest(() => payrollAPI.getAttendanceDocumentPreview({ month: periodMonth, year: periodYear, scope: reportScope, store_location: scopeStoreLocation, employee_number: selectedEmployeeNumber }), { data: { summary: {}, by_employee: [], by_store: [], days: [] } }, { retries: 1, required: true, telemetry: { module: 'payroll', endpoint: 'payroll.attendance_document_preview', onEvent: captureFetchTelemetry } }),
        safeRequest(() => payrollAPI.getTemplateSettings(), { data: { settings: {}, documents: PAYROLL_TEMPLATE_FALLBACK_DOCUMENTS } }, { retries: 1, required: true, telemetry: { module: 'payroll', endpoint: 'payroll.template_settings', onEvent: captureFetchTelemetry } }),
      ]);

      setLocations(normalizeLocationOptions(locationsRes));
      setAttendanceBootstrapStatus(bootstrapRes.data || bootstrapRes);
      setSalarySheetPreview(salaryRes.data || salaryRes);
      setNominalPreview(nominalRes.data || nominalRes);
      setAttendanceDocumentPreview(attendanceDocRes.data || attendanceDocRes);
      const templatePayload = templateSettingsRes.data || templateSettingsRes || {};
      setTemplateSettings(templatePayload.settings || {});
      setTemplateDocuments(templatePayload.documents || PAYROLL_TEMPLATE_FALLBACK_DOCUMENTS);
    } catch (error) {
      toast.error(getErrorMessage(error, 'Erro ao carregar documentos RH'));
    } finally {
      setDocumentsLoading(false);
      setTemplateSettingsLoading(false);
    }
  }, [captureFetchTelemetry, normalizeLocationOptions, periodMonth, periodYear, reportEmployeeNumber, reportScope, reportStoreLocation]);

  const loadVacationData = useCallback(async () => {
    try {
      setVacationsLoading(true);
      const response = await safeRequest(
        () => payrollAPI.getVacationsDashboard(vacationYear),
        { data: { policy: null, requests: [], individual_plans: [], general_plan: [], summary: {} } },
        { retries: 1, required: true, telemetry: { module: 'payroll', endpoint: 'payroll.vacations_dashboard', onEvent: captureFetchTelemetry } },
      );
      const payload = response.data || response || {};
      setVacationsDashboard(payload);
      const activeRegime = payload.policy?.active_regime || payload.policy?.regimes?.[payload.policy?.active_regime_key] || {};
      setVacationPolicyForm({
        active_regime_key: payload.policy?.active_regime_key || 'mozambique_law_13_2023_current',
        first_year_days: String(activeRegime.first_year_days ?? 12),
        subsequent_year_days: String(activeRegime.subsequent_year_days ?? 30),
        fixed_term_days_per_month: String(activeRegime.fixed_term_days_per_month ?? 1),
        fixed_term_min_months: String(activeRegime.fixed_term_min_months ?? 3),
        fixed_term_max_months: String(activeRegime.fixed_term_max_months ?? 12),
        notes: activeRegime.notes || '',
      });
    } catch (error) {
      toast.error(getErrorMessage(error, 'Erro ao carregar férias'));
    } finally {
      setVacationsLoading(false);
    }
  }, [captureFetchTelemetry, safeRequest, vacationYear]);

  useEffect(() => {
    void loadCoreData();
  }, [loadCoreData]);

  useEffect(() => {
    if (activeTab === 'attendance') {
      loadAttendanceData();
    }
  }, [activeTab, loadAttendanceData]);

  useEffect(() => {
    if (activeTab === 'reports') {
      loadDocumentsData();
    }
  }, [activeTab, loadDocumentsData]);

  useEffect(() => {
    if (activeTab === 'vacations' || activeTab === 'reports') {
      void loadVacationData();
    }
  }, [activeTab, loadVacationData]);

  useEffect(() => {
    setInssForm({
      employee_rate: String(((inssConfig.employee_rate || 0) * 100).toFixed(2)).replace(/\.00$/, ''),
      employer_rate: String(((inssConfig.employer_rate || 0) * 100).toFixed(2)).replace(/\.00$/, ''),
    });
  }, [inssConfig]);

  useEffect(() => {
    setCompanyGoalForm({
      monthly_goal: String(companyGoal.monthly_goal || 2000000),
      commission_rate: String(((companyGoal.commission_rate || 0) * 100).toFixed(2)).replace(/\.00$/, ''),
    });
  }, [companyGoal]);

  useEffect(() => {
    if (!configDialogOpen) {
      setIrpsEditorMode('create');
      setIrpsEditor(buildIrpsEditorState());
    }
  }, [buildIrpsEditorState, configDialogOpen]);

  useEffect(() => {
    if (!documentPayrollId && sortedPayrolls.length > 0) {
      setDocumentPayrollId(sortedPayrolls[0].id);
    }
    if (documentPayrollId && !sortedPayrolls.some((payroll) => payroll.id === documentPayrollId)) {
      setDocumentPayrollId(sortedPayrolls[0]?.id || '');
    }
  }, [documentPayrollId, sortedPayrolls]);

  useEffect(() => {
    if (!templateDocuments.some((document) => document.key === selectedTemplateDocument)) {
      setSelectedTemplateDocument(templateDocuments[0]?.key || 'receipt');
    }
  }, [selectedTemplateDocument, templateDocuments]);

  // Clear filters
  const handleClearFilters = () => {
    setEmployeeFilter('all');
    setStatusFilter('all');
    setSearchQuery('');
  };

  const buildPayrollPayload = useCallback(() => {
    const employee = employees.find((entry) => entry.id === formData.employee_id);
    return {
      employee_id: formData.employee_id,
      period_month: parseInt(formData.period_month),
      period_year: parseInt(formData.period_year),
      base_salary: parseFloat(formData.base_salary) || employee?.base_salary || 0,
      manual_days_worked: formData.manual_days_worked === '' ? null : parseInt(formData.manual_days_worked, 10),
      notice_pay_days: parseInt(formData.notice_pay_days || '0', 10) || 0,
      overtime_hours: parseFloat(formData.overtime_hours) || 0,
      overtime_rate: parseFloat(formData.overtime_rate) || 1.5,
      attendance_bonus: parseFloat(formData.attendance_bonus) || 0,
      performance_bonus: parseFloat(formData.performance_bonus) || 0,
      other_bonuses: parseFloat(formData.other_bonuses) || 0,
      transport_allowance: parseFloat(formData.transport_allowance) || 0,
      meal_allowance: parseFloat(formData.meal_allowance) || 0,
      other_allowances: parseFloat(formData.other_allowances) || 0,
      advance_deduction: parseFloat(formData.advance_deduction) || 0,
      other_deductions: parseFloat(formData.other_deductions) || 0,
      manual_commission: formData.manual_commission ? parseFloat(formData.manual_commission) : null,
      notes: formData.notes || null,
    };
  }, [employees, formData]);

  // Simulate payroll
  const handleSimulate = async () => {
    if (!formData.employee_id) {
      toast.error('Seleccione um funcionário');
      return;
    }
    
    const employee = employees.find(e => e.id === formData.employee_id);
    if (!employee?.dossier_complete) {
      toast.error('O dossier do funcionário está incompleto. Complete antes de processar.');
      return;
    }
    
    setSubmitting(true);
    try {
      const payload = buildPayrollPayload();
      const result = await payrollAPI.simulate(payload);
      setSimulationResult(result.data || result);
      toast.success('Simulação concluída');
    } catch (error) {
      console.error('Error simulating payroll:', error);
      toast.error(error.response?.data?.detail || 'Erro ao simular folha');
    } finally {
      setSubmitting(false);
    }
  };

  // Process payroll
  const handleProcessPayroll = async () => {
    if (!formData.employee_id) {
      toast.error('Seleccione um funcionário');
      return;
    }
    
    setSubmitting(true);
    try {
      const payload = buildPayrollPayload();
      await payrollAPI.process(payload);
      toast.success('Folha gerada com sucesso. Revise os valores e marque como revista.');
      setProcessDialogOpen(false);
      setFormData(initialFormData);
      setSimulationResult(null);
      loadData();
    } catch (error) {
      console.error('Error processing payroll:', error);
      toast.error(error.response?.data?.detail || 'Erro ao processar folha');
    } finally {
      setSubmitting(false);
    }
  };

  // Confirm payroll
  const handleConfirmPayroll = async () => {
    if (!selectedPayroll) return;
    
    setSubmitting(true);
    try {
      await payrollAPI.confirm(selectedPayroll.id);
      toast.success('Folha revista com sucesso. Pronta para processamento final.');
      setConfirmDialogOpen(false);
      setSelectedPayroll(null);
      loadData();
    } catch (error) {
      console.error('Error confirming payroll:', error);
      toast.error(error.response?.data?.detail || 'Erro ao confirmar');
    } finally {
      setSubmitting(false);
    }
  };

  // Mark as paid
  const handleMarkPaid = async () => {
    if (!selectedPayroll) return;
    
    setSubmitting(true);
    try {
      await payrollAPI.markPaid(selectedPayroll.id);
      toast.success('Folha marcada como processada!');
      setMarkPaidDialogOpen(false);
      setSelectedPayroll(null);
      loadData();
    } catch (error) {
      console.error('Error marking payroll as paid:', error);
      toast.error(error.response?.data?.detail || 'Erro ao marcar como pago');
    } finally {
      setSubmitting(false);
    }
  };

  const handleBulkProcessEmployees = async () => {
    const targetEmployees = sortedEmployees.filter((employee) => selectedEmployeeIds.includes(employee.id));
    if (!targetEmployees.length) {
      toast.error('Seleccione funcionários para processamento em massa');
      return;
    }

    const processableEmployees = targetEmployees.filter((employee) => employee.dossier_complete && isEmployeeEligibleForPayrollProcessing(employee, periodMonth, periodYear));
    const skippedEmployees = targetEmployees.length - processableEmployees.length;
    if (!processableEmployees.length) {
      toast.error('Nenhum colaborador seleccionado está elegível para processamento');
      return;
    }

    setSubmitting(true);
    try {
      const results = await Promise.allSettled(processableEmployees.map((employee) => payrollAPI.process({
        employee_id: employee.id,
        period_month: periodMonth,
        period_year: periodYear,
        base_salary: Number(employee.base_salary || 0),
        overtime_hours: 0,
        overtime_rate: 1.5,
        attendance_bonus: Number(employee.attendance_bonus || 0),
        performance_bonus: Number(employee.performance_bonus || 0),
        other_bonuses: Number(employee.other_bonuses || 0),
        transport_allowance: 0,
        meal_allowance: 0,
        other_allowances: 0,
        advance_deduction: 0,
        other_deductions: 0,
        manual_commission: null,
        notes: null,
      })));
      const successCount = results.filter((result) => result.status === 'fulfilled').length;
      const failedCount = results.length - successCount;
      if (successCount) toast.success(`${successCount} folha(s) gerada(s) em massa`);
      if (failedCount) toast.warning(`${failedCount} funcionário(s) não puderam ser processados`);
      if (skippedEmployees) toast.info(`${skippedEmployees} funcionário(s) foram ignorados por dossier incompleto`);
      setSelectedEmployeeIds([]);
      await loadData();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Erro ao processar funcionários em massa'));
    } finally {
      setSubmitting(false);
    }
  };

  const handleBulkConfirmPayrolls = async () => {
    const targetPayrolls = sortedPayrolls.filter((payroll) => selectedPayrollIds.includes(payroll.id) && payroll.status === 'draft');
    if (!targetPayrolls.length) {
      toast.error('Seleccione folhas geradas para revisão em massa');
      return;
    }

    setSubmitting(true);
    try {
      const results = await Promise.allSettled(targetPayrolls.map((payroll) => payrollAPI.confirm(payroll.id)));
      const successCount = results.filter((result) => result.status === 'fulfilled').length;
      const failedCount = results.length - successCount;
      if (successCount) toast.success(`${successCount} folha(s) revista(s)`);
      if (failedCount) toast.warning(`${failedCount} folha(s) falharam na revisão em massa`);
      setSelectedPayrollIds([]);
      await loadData();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Erro ao rever folhas em massa'));
    } finally {
      setSubmitting(false);
    }
  };

  const handleClosePayrollPeriod = async () => {
    const targetPayrolls = resolveTargetPayrollRows();
    if (!targetPayrolls.length) {
      toast.error('Nenhuma folha disponível para fechar no período actual');
      return;
    }

    const scopeMessage = selectedPayrollIds.length
      ? `${targetPayrolls.length} folha(s) seleccionada(s)`
      : `${targetPayrolls.length} folha(s) visível(is) no período filtrado`;

    if (!window.confirm(`Fechar ${scopeMessage}? As folhas geradas passam para revista/processada e as revistas ficam processadas.`)) {
      return;
    }

    setSubmitting(true);
    try {
      const response = await payrollAPI.closePeriod({
        month: periodMonth,
        year: periodYear,
        payroll_ids: targetPayrolls.map((payroll) => payroll.id),
      });
      const payload = response.data || response;
      toast.success(payload?.message || 'Período fechado com sucesso');
      if (payload?.summary?.blocked_count) {
        toast.warning(`${payload.summary.blocked_count} folha(s) ficaram pendentes por IRPS.`);
      }
      setSelectedPayrollIds([]);
      await loadData();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Erro ao fechar o período'));
    } finally {
      setSubmitting(false);
    }
  };

  const handleDownloadPayrollZip = useCallback(async () => {
    const targetPayrolls = resolveTargetPayrollRows();
    if (!targetPayrolls.length) {
      toast.error('Nenhuma folha disponível para exportar em ZIP');
      return;
    }

    try {
      const response = await payrollAPI.downloadDocumentsZip({
        payroll_ids: targetPayrolls.map((payroll) => payroll.id).join(','),
        month: periodMonth,
        year: periodYear,
        include_receipts: true,
        include_salary_sheet: true,
      });
      downloadBlobFile(response.data, `payroll_documentos_${periodYear}_${String(periodMonth).padStart(2, '0')}.zip`);
      toast.success(selectedPayrollIds.length
        ? `ZIP gerado para ${targetPayrolls.length} folha(s) seleccionada(s)`
        : `ZIP gerado para o período filtrado (${targetPayrolls.length} folha(s))`);
    } catch (error) {
      toast.error(getErrorMessage(error, 'Erro ao gerar ZIP de payroll'));
    }
  }, [periodMonth, periodYear, resolveTargetPayrollRows, selectedPayrollIds]);

  const handleExportSelectedPayrollsCsv = useCallback(() => {
    const selectedRows = sortedPayrolls.filter((payroll) => selectedPayrollIds.includes(payroll.id));
    if (!selectedRows.length) {
      toast.error('Seleccione folhas para exportar');
      return;
    }

    downloadCsvFile(
      `folhas_seleccionadas_${periodYear}_${String(periodMonth).padStart(2, '0')}.csv`,
      ['Número', 'Funcionário', 'Nº colaborador', 'Perfil', 'Estado', 'Base', 'Bruto', 'Bónus', 'Descontos', 'Líquido'],
      selectedRows.map((payroll) => [
        getPayrollDisplayReference(payroll),
        payroll.employee_name,
        payroll.employee_number,
        payroll.employee_role || payroll.role || '',
        STATUS_CONFIG[payroll.status]?.label || payroll.status,
        Number(payroll.base_salary || 0).toFixed(2),
        Number(payroll.gross_salary || 0).toFixed(2),
        Number(payroll.total_bonuses ?? payroll.bonuses ?? 0).toFixed(2),
        Number(payroll.total_deductions ?? payroll.deductions ?? 0).toFixed(2),
        Number(payroll.net_salary || 0).toFixed(2),
      ]),
    );
    toast.success(`${selectedRows.length} folha(s) exportada(s) em CSV`);
  }, [periodMonth, periodYear, selectedPayrollIds, sortedPayrolls]);

  // Delete payroll (draft only)
  const handleDeletePayroll = async () => {
    if (!selectedPayroll) return;
    
    setSubmitting(true);
    try {
      await payrollAPI.delete(selectedPayroll.id);
      toast.success('Rascunho eliminado');
      setDeleteDialogOpen(false);
      setSelectedPayroll(null);
      loadData();
    } catch (error) {
      console.error('Error deleting payroll:', error);
      toast.error(error.response?.data?.detail || 'Erro ao eliminar');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDirectAdminDeletePayroll = async (payroll) => {
    try {
      await payrollAPI.delete(payroll.id);
      toast.success('Folha eliminada pelo administrador');
      loadData();
    } catch (error) {
      console.error('Error deleting payroll:', error);
      toast.error(error.response?.data?.detail || 'Erro ao eliminar folha');
    }
  };

  const handleDeleteAction = (payroll) => {
    if (!payroll) return;
    if (isAdmin) {
      handleDirectAdminDeletePayroll(payroll);
      return;
    }
    setSelectedPayroll(payroll);
    setDeleteDialogOpen(true);
  };

  const resetPayrollEditState = () => {
    setPayrollEditDialogOpen(false);
    setEditingPayroll(null);
    setPayrollEditForm(DEFAULT_PAYROLL_EDIT_FORM);
  };

  const openPayrollEditDialog = (payroll) => {
    setEditingPayroll(payroll);
    setPayrollEditForm(createPayrollEditForm(payroll));
    setPayrollEditDialogOpen(true);
  };

  const openIrpsEditor = (table = null, overrides = {}) => {
    setIrpsEditorMode(table ? 'edit' : 'create');
    setIrpsEditor({ ...buildIrpsEditorState(table), ...overrides });
  };

  const handleResolveIRPSTableGap = () => {
    setConfigSubmitting(true);
    payrollAPI.applyOfficialIRPSYear(periodYear)
      .then(async (response) => {
        const payload = response.data || response;
        toast.success(`${payload.message}. Folhas actualizadas: ${payload.payroll_refresh?.updated_count || 0}`);
        await loadData();
        if (payload.table) {
          openIrpsEditor(payload.table);
        }
      })
      .catch((error) => {
        toast.error(error?.response?.data?.detail || `Não foi possível activar a tabela oficial de ${periodYear}`);
      })
      .finally(() => {
        setConfigSubmitting(false);
      });
  };

  const handleIrpsBracketChange = (index, field, value) => {
    setIrpsEditor((current) => ({
      ...current,
      brackets: current.brackets.map((bracket, bracketIndex) => (
        bracketIndex === index ? { ...bracket, [field]: value } : bracket
      )),
    }));
  };

  const handleAddIrpsBracket = () => {
    setIrpsEditor((current) => ({
      ...current,
      brackets: [...current.brackets, { ...DEFAULT_IRPS_BRACKET }],
    }));
  };

  const handleRemoveIrpsBracket = (index) => {
    setIrpsEditor((current) => ({
      ...current,
      brackets: current.brackets.filter((_, bracketIndex) => bracketIndex !== index),
    }));
  };

  const handleSaveINSSConfig = async () => {
    setConfigSubmitting(true);
    try {
      await payrollAPI.updateINSSConfig({
        employee_rate: (parseFloat(inssForm.employee_rate) || 0) / 100,
        employer_rate: (parseFloat(inssForm.employer_rate) || 0) / 100,
      });
      toast.success('INSS actualizado');
      await loadData();
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Erro ao actualizar INSS');
    } finally {
      setConfigSubmitting(false);
    }
  };

  const handleResetINSSConfig = async () => {
    if (!window.confirm('Repor INSS para os valores padrão?')) return;
    setConfigSubmitting(true);
    try {
      await payrollAPI.deleteINSSConfig();
      toast.success('INSS reposto para os valores padrão');
      await loadData();
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Erro ao repor INSS');
    } finally {
      setConfigSubmitting(false);
    }
  };

  const handleSaveCompanyGoal = async () => {
    setConfigSubmitting(true);
    try {
      await payrollAPI.updateCompanyGoal({
        monthly_goal: parseFloat(companyGoalForm.monthly_goal) || 0,
        commission_rate: (parseFloat(companyGoalForm.commission_rate) || 0) / 100,
      });
      toast.success('Meta empresa actualizada');
      await loadData();
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Erro ao actualizar meta empresa');
    } finally {
      setConfigSubmitting(false);
    }
  };

  const handleResetCompanyGoal = async () => {
    if (!window.confirm('Repor a meta empresa para os valores padrão?')) return;
    setConfigSubmitting(true);
    try {
      await payrollAPI.deleteCompanyGoal();
      toast.success('Meta empresa reposta para os valores padrão');
      await loadData();
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Erro ao repor meta empresa');
    } finally {
      setConfigSubmitting(false);
    }
  };

  const handleSaveIRPSTable = async () => {
    const brackets = irpsEditor.brackets
      .map((bracket) => ({
        min_value: parseFloat(bracket.min_value),
        max_value: String(bracket.max_value).trim() === '' ? null : parseFloat(bracket.max_value),
        rate: (parseFloat(bracket.rate) || 0) / 100,
        deduction: parseFloat(bracket.deduction) || 0,
      }))
      .filter((bracket) => !Number.isNaN(bracket.min_value));

    if (!brackets.length) {
      toast.error('Adicione pelo menos um escalão IRPS válido');
      return;
    }

    const payload = {
      year: parseInt(irpsEditor.year, 10),
      is_active: irpsEditor.is_active,
      notes: irpsEditor.notes,
      brackets,
    };

    if (!payload.year) {
      toast.error('Ano da tabela IRPS é obrigatório');
      return;
    }

    setConfigSubmitting(true);
    try {
      if (irpsEditor.id) {
        await payrollAPI.updateIRPSTable(irpsEditor.id, payload);
        toast.success(`Tabela IRPS ${payload.year} actualizada`);
      } else {
        await payrollAPI.createIRPSTable(payload);
        toast.success(`Tabela IRPS ${payload.year} criada`);
      }
      await loadData();
      openIrpsEditor();
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Erro ao guardar tabela IRPS');
    } finally {
      setConfigSubmitting(false);
    }
  };

  const handleActivateIRPSTable = async (tableId) => {
    setConfigSubmitting(true);
    try {
      await payrollAPI.activateIRPSTable(tableId);
      toast.success('Tabela IRPS activada');
      await loadData();
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Erro ao activar tabela IRPS');
    } finally {
      setConfigSubmitting(false);
    }
  };

  const handleDeleteIRPSTable = async (tableId) => {
    if (!window.confirm('Eliminar esta tabela IRPS?')) return;
    setConfigSubmitting(true);
    try {
      await payrollAPI.deleteIRPSTable(tableId);
      toast.success('Tabela IRPS eliminada');
      await loadData();
      if (irpsEditor.id === tableId) {
        openIrpsEditor();
      }
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Erro ao eliminar tabela IRPS');
    } finally {
      setConfigSubmitting(false);
    }
  };

  // Load employee dossier
  const loadDossier = async (employeeId, { syncForm = true } = {}) => {
    try {
      const result = await payrollAPI.getEmployeeDossier(employeeId);
      const data = result.data || result;
      setEmployeeDossier(data);
      if (syncForm) {
        setDossierForm({
          name: data.name || data.employee_name || selectedEmployee?.full_name || selectedEmployee?.name || '',
          email: data.email || '',
          phone: data.phone || '',
          employee_number: data.employee_number || '',
          bi_number: data.bi_number || '',
          document_ident_number: data.document_ident_number || data.bi_number || '',
          document_ident_type: data.document_ident_type || '',
          document_issuer_service: data.document_issuer_service || '',
          nuit: data.nuit || '',
          inss_number: data.inss_number || '',
          birth_date: data.birth_date || '',
          marital_status: data.marital_status || '',
          gender: data.gender || '',
          father_name: data.father_name || '',
          mother_name: data.mother_name || '',
          nationality: data.nationality || 'Moçambicana',
          address: data.address || '',
          emergency_contact: data.emergency_contact || '',
          emergency_phone: data.emergency_phone || '',
          department: data.department || '',
          job_title: data.job_title || '',
          store_location: data.store_location || '',
          education_level: data.education_level || '',
          dependents_count: data.dependents_count === null || data.dependents_count === undefined ? '0' : String(data.dependents_count),
          profile_role_key: data.profile_role_key || '',
          profile_role_source: data.profile_role_source || '',
          hire_date: data.hire_date || '',
          contract_type: data.contract_type || '',
          contract_end_date: data.contract_end_date || '',
          exit_note: data.exit_note || '',
          bank_name: data.bank_name || '',
          bank_account: data.bank_account || '',
          bank_nib: data.bank_nib || '',
          base_salary: data.base_salary === null || data.base_salary === undefined ? '' : String(data.base_salary),
          attendance_bonus: data.attendance_bonus === null || data.attendance_bonus === undefined ? '0' : String(data.attendance_bonus),
          performance_bonus: data.performance_bonus === null || data.performance_bonus === undefined ? '0' : String(data.performance_bonus),
          other_bonuses: data.other_bonuses === null || data.other_bonuses === undefined ? '' : String(data.other_bonuses),
        });
        setReintegrationForm({
          date: new Date().toISOString().slice(0, 10),
          note: '',
        });
      }
    } catch (error) {
      console.error('Error loading dossier:', error);
      toast.error('Erro ao carregar dossier');
    }
  };

  const refreshDossierOverview = async (employeeId, { syncForm = false } = {}) => {
    await loadDossier(employeeId, { syncForm });
  };

  // Save dossier
  const handleSaveDossier = async () => {
    if (!selectedEmployee) return;
    if (dossierForm.contract_end_date && !String(dossierForm.exit_note || '').trim()) {
      toast.error('Indique o motivo de saída antes de guardar a data de saída');
      return;
    }
    
    setSubmitting(true);
    try {
      await payrollAPI.updateEmployeeDossier(selectedEmployee.id, {
        ...dossierForm,
        dependents_count: dossierForm.dependents_count === '' ? 0 : Number(dossierForm.dependents_count),
        base_salary: dossierForm.base_salary === '' ? undefined : Number(dossierForm.base_salary),
        attendance_bonus: dossierForm.attendance_bonus === '' ? undefined : Number(dossierForm.attendance_bonus),
        performance_bonus: dossierForm.performance_bonus === '' ? undefined : Number(dossierForm.performance_bonus),
        other_bonuses: dossierForm.other_bonuses === '' ? undefined : Number(dossierForm.other_bonuses),
      });
      toast.success('Dossier actualizado com sucesso!');
      await Promise.all([
        loadData(),
        refreshDossierOverview(selectedEmployee.id, { syncForm: true }),
      ]);
    } catch (error) {
      console.error('Error saving dossier:', error);
      toast.error(error.response?.data?.detail || 'Erro ao guardar dossier');
    } finally {
      setSubmitting(false);
    }
  };

  const handleReintegrateEmployee = async () => {
    if (!selectedEmployee) return;
    if (!String(reintegrationForm.note || '').trim()) {
      toast.error('Indique a nota de reintegração');
      return;
    }

    setSubmitting(true);
    try {
      await payrollAPI.reintegrateEmployeeDossier(selectedEmployee.id, {
        reintegration_date: reintegrationForm.date || undefined,
        reintegration_note: reintegrationForm.note.trim(),
      });
      toast.success('Colaborador reintegrado com sucesso');
      setReintegrationForm({ date: new Date().toISOString().slice(0, 10), note: '' });
      await Promise.all([
        loadData(),
        refreshDossierOverview(selectedEmployee.id, { syncForm: true }),
      ]);
    } catch (error) {
      console.error('Error reintegrating employee:', error);
      toast.error(getErrorMessage(error, 'Erro ao reintegrar colaborador'));
    } finally {
      setSubmitting(false);
    }
  };

  const openPayrollEmployeeDialog = async () => {
    try {
      if (!payrollBaseRoles.length) {
        const response = await rbacAPI.listBaseRoles();
        const roles = response.data?.roles || [];
        setPayrollBaseRoles(roles);
        if (roles.length) {
          const defaultRole = roles.find((role) => role.key === 'staff') || roles[0];
          setPayrollEmployeeForm((prev) => ({ ...prev, selected_profile: `base:${defaultRole.key}` }));
        }
      }
      setPayrollEmployeeDialogOpen(true);
    } catch (error) {
      toast.error(getErrorMessage(error, 'Erro ao carregar perfis base do RH'));
    }
  };

  const handleCreatePayrollEmployee = async () => {
    if (!payrollEmployeeForm.name.trim()) {
      toast.error('Indique o nome do colaborador');
      return;
    }

    const [, profileKey = 'staff'] = String(payrollEmployeeForm.selected_profile || 'base:staff').split(':');
    const selectedProfile = payrollBaseRoles.find((role) => role.key === profileKey);

    setCreatingPayrollEmployee(true);
    try {
      await settingsAPI.createEmployee({
        name: payrollEmployeeForm.name.trim(),
        email: payrollEmployeeForm.email.trim() || undefined,
        phone: payrollEmployeeForm.phone.trim() || undefined,
        employee_number: payrollEmployeeForm.employee_number.trim() || undefined,
        nuit: payrollEmployeeForm.nuit.trim() || undefined,
        inss_number: payrollEmployeeForm.inss_number.trim() || undefined,
        birth_date: payrollEmployeeForm.birth_date || undefined,
        marital_status: payrollEmployeeForm.marital_status || undefined,
        gender: payrollEmployeeForm.gender || undefined,
        father_name: payrollEmployeeForm.father_name.trim() || undefined,
        mother_name: payrollEmployeeForm.mother_name.trim() || undefined,
        nationality: payrollEmployeeForm.nationality.trim() || undefined,
        address: payrollEmployeeForm.address.trim() || undefined,
        emergency_contact: payrollEmployeeForm.emergency_contact.trim() || undefined,
        emergency_phone: payrollEmployeeForm.emergency_phone.trim() || undefined,
        department: payrollEmployeeForm.department.trim() || undefined,
        job_title: payrollEmployeeForm.job_title.trim() || undefined,
        hire_date: payrollEmployeeForm.hire_date || undefined,
        contract_type: payrollEmployeeForm.contract_type || undefined,
        contract_end_date: payrollEmployeeForm.contract_end_date || undefined,
        bank_name: payrollEmployeeForm.bank_name.trim() || undefined,
        bank_account: payrollEmployeeForm.bank_account.trim() || undefined,
        bank_nib: payrollEmployeeForm.bank_nib.trim() || undefined,
        store_location: payrollEmployeeForm.store_location || undefined,
        base_salary: payrollEmployeeForm.base_salary === '' ? undefined : Number(payrollEmployeeForm.base_salary),
        attendance_bonus: payrollEmployeeForm.attendance_bonus === '' ? undefined : Number(payrollEmployeeForm.attendance_bonus),
        performance_bonus: payrollEmployeeForm.performance_bonus === '' ? undefined : Number(payrollEmployeeForm.performance_bonus),
        other_bonuses: payrollEmployeeForm.other_bonuses === '' ? undefined : Number(payrollEmployeeForm.other_bonuses),
        document_ident_number: payrollEmployeeForm.document_ident_number.trim() || undefined,
        document_ident_type: payrollEmployeeForm.document_ident_type || undefined,
        document_issuer_service: payrollEmployeeForm.document_issuer_service.trim() || undefined,
        bi_number: payrollEmployeeForm.bi_number.trim() || undefined,
        role: selectedProfile?.label || profileKey,
        profile_role_key: profileKey,
        profile_role_label: selectedProfile?.label || profileKey,
        profile_role_source: 'base_role',
        can_sell: payrollEmployeeForm.can_sell,
      });
      toast.success('Colaborador criado no RH e integrado na lista de colaboradores');
      setPayrollEmployeeDialogOpen(false);
      setPayrollEmployeeForm(EMPTY_PAYROLL_EMPLOYEE_FORM);
      await loadData();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Erro ao criar colaborador no RH'));
    } finally {
      setCreatingPayrollEmployee(false);
    }
  };

  // Open dossier dialog
  const openDossierDialog = (employee) => {
    setSelectedEmployee(employee);
    refreshDossierOverview(employee.id, { syncForm: true });
    setDossierDialogOpen(true);
  };

  // Seed default IRPS table
  const handleSeedIRPS = async () => {
    try {
      await payrollAPI.seedDefaultIRPS();
      toast.success('Tabela IRPS 2024-2025 carregada');
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao carregar tabela IRPS');
    }
  };

  // Download receipt
  const handleDownloadReceipt = async (payrollId, payrollNumber = payrollId) => {
    try {
      const response = await payrollAPI.downloadReceipt(payrollId);
      openBlobInNewTab(response.data);
      toast.success(`Recibo ${payrollNumber} pronto para visualização`);
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Erro ao abrir recibo');
    }
  };

  const handleExportAttendanceXlsx = async () => {
    try {
      const response = await payrollAPI.downloadAttendanceXlsx({ month: periodMonth, year: periodYear });
      downloadBlobFile(response.data, `attendance_${periodYear}_${String(periodMonth).padStart(2, '0')}.xlsx`);
      toast.success('Mapa de assiduidade exportado');
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Erro ao exportar assiduidade');
    }
  };

  const handleDownloadAttendanceTemplateXlsx = async () => {
    try {
      const response = await payrollAPI.downloadAttendanceImportTemplateXlsx();
      downloadBlobFile(response.data, 'template_importacao_assiduidade.xlsx');
      toast.success('Template oficial de assiduidade descarregado');
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Erro ao descarregar template de assiduidade');
    }
  };

  const handleDownloadSalarySheetPdf = async () => {
    try {
      const response = await payrollAPI.downloadSalarySheetPdf({ month: periodMonth, year: periodYear, scope: reportScope, store_location: reportScope === 'store' && reportStoreLocation !== 'all' ? reportStoreLocation : undefined });
      openBlobInNewTab(response.data);
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Erro ao gerar folha salarial');
    }
  };

  const handleDownloadSalarySheetXlsx = async () => {
    try {
      const response = await payrollAPI.downloadSalarySheetXlsx({ month: periodMonth, year: periodYear, scope: reportScope, store_location: reportScope === 'store' && reportStoreLocation !== 'all' ? reportStoreLocation : undefined });
      downloadBlobFile(response.data, `folha_salarial_${periodYear}_${String(periodMonth).padStart(2, '0')}.xlsx`);
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Erro ao exportar folha salarial');
    }
  };

  const handleDownloadNominalPdf = async () => {
    try {
      const response = await payrollAPI.downloadNominalPdf({ month: periodMonth, year: periodYear, scope: reportScope, store_location: reportScope === 'store' && reportStoreLocation !== 'all' ? reportStoreLocation : undefined });
      openBlobInNewTab(response.data);
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Erro ao gerar relação nominal');
    }
  };

  const handleDownloadNominalXlsx = async () => {
    try {
      const response = await payrollAPI.downloadNominalXlsx({ month: periodMonth, year: periodYear, scope: reportScope, store_location: reportScope === 'store' && reportStoreLocation !== 'all' ? reportStoreLocation : undefined });
      downloadBlobFile(response.data, `relacao_nominal_${periodYear}_${String(periodMonth).padStart(2, '0')}.xlsx`);
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Erro ao exportar relação nominal');
    }
  };

  const handleDownloadAttendanceDocumentPdf = async () => {
    try {
      const response = await payrollAPI.downloadAttendanceDocumentPdf({ month: periodMonth, year: periodYear, scope: reportScope, store_location: reportScope === 'store' && reportStoreLocation !== 'all' ? reportStoreLocation : undefined, employee_number: reportEmployeeNumber !== 'all' ? reportEmployeeNumber : undefined });
      openBlobInNewTab(response.data);
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Erro ao gerar mapa de assiduidade');
    }
  };

  const handleDownloadAttendanceDocumentXlsx = async () => {
    try {
      const response = await payrollAPI.downloadAttendanceDocumentXlsx({ month: periodMonth, year: periodYear, scope: reportScope, store_location: reportScope === 'store' && reportStoreLocation !== 'all' ? reportStoreLocation : undefined, employee_number: reportEmployeeNumber !== 'all' ? reportEmployeeNumber : undefined });
      downloadBlobFile(response.data, `mapa_assiduidade_${periodYear}_${String(periodMonth).padStart(2, '0')}.xlsx`);
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Erro ao exportar mapa de assiduidade');
    }
  };

  const handleCreateVacationRequest = async () => {
    try {
      await payrollAPI.createVacationRequest(vacationRequestForm);
      toast.success('Pedido de férias registado');
      setVacationRequestForm({ employee_id: '', start_date: '', end_date: '', notes: '' });
      await loadVacationData();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Erro ao registar pedido de férias'));
    }
  };

  const handleUpdateVacationRequestStatus = async (requestId, status) => {
    try {
      await payrollAPI.updateVacationRequestStatus(requestId, { status });
      toast.success(`Pedido actualizado para ${status === 'approved' ? 'aprovado' : status === 'rejected' ? 'rejeitado' : 'pendente'}`);
      await loadVacationData();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Erro ao actualizar estado do pedido de férias'));
    }
  };

  const handleSaveVacationPolicy = async () => {
    try {
      await payrollAPI.updateVacationPolicy({
        active_regime_key: vacationPolicyForm.active_regime_key,
        first_year_days: Number(vacationPolicyForm.first_year_days || 0),
        subsequent_year_days: Number(vacationPolicyForm.subsequent_year_days || 0),
        fixed_term_days_per_month: Number(vacationPolicyForm.fixed_term_days_per_month || 0),
        fixed_term_min_months: Number(vacationPolicyForm.fixed_term_min_months || 0),
        fixed_term_max_months: Number(vacationPolicyForm.fixed_term_max_months || 0),
        notes: vacationPolicyForm.notes || '',
      });
      toast.success('Regra de férias parametrizada');
      await loadVacationData();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Erro ao guardar parametrização de férias'));
    }
  };

  const handleDownloadVacationReport = async (reportKey, format) => {
    const params = { year: vacationYear };
    if (reportKey === 'plan-individual') {
      if (selectedVacationEmployeeNumber === 'all') {
        toast.error('Seleccione um colaborador para o plano individual');
        return;
      }
      params.employee_number = selectedVacationEmployeeNumber;
    }

    const actionMap = {
      'requests:pdf': payrollAPI.downloadVacationRequestsReportPdf,
      'requests:xlsx': payrollAPI.downloadVacationRequestsReportXlsx,
      'plan-general:pdf': payrollAPI.downloadVacationGeneralPlanReportPdf,
      'plan-general:xlsx': payrollAPI.downloadVacationGeneralPlanReportXlsx,
      'plan-individual:pdf': payrollAPI.downloadVacationIndividualPlanReportPdf,
      'plan-individual:xlsx': payrollAPI.downloadVacationIndividualPlanReportXlsx,
      'balance-summary:pdf': payrollAPI.downloadVacationBalanceSummaryReportPdf,
      'balance-summary:xlsx': payrollAPI.downloadVacationBalanceSummaryReportXlsx,
    };

    try {
      const response = await actionMap[`${reportKey}:${format}`](params);
      const filename = `ferias_${reportKey}_${vacationYear}.${format === 'pdf' ? 'pdf' : 'xlsx'}`;
      if (format === 'pdf') {
        openBlobInNewTab(response.data);
      } else {
        downloadBlobFile(response.data, filename);
      }
    } catch (error) {
      toast.error(getErrorMessage(error, 'Erro ao exportar relatório de férias'));
    }
  };

  const updateTemplateSettingField = useCallback((field, value) => {
    setTemplateSettings((current) => ({
      ...current,
      [selectedTemplateDocument]: {
        ...(current?.[selectedTemplateDocument] || {}),
        [field]: value,
      },
    }));
  }, [selectedTemplateDocument]);

  const updateTemplateSection = useCallback((sectionKey, patch) => {
    setTemplateSettings((current) => {
      const currentDocumentSettings = current?.[selectedTemplateDocument] || {};
      const nextSections = (currentDocumentSettings.layout_sections || []).map((section) => (
        section.key === sectionKey ? { ...section, ...patch } : section
      ));
      return {
        ...current,
        [selectedTemplateDocument]: {
          ...currentDocumentSettings,
          layout_sections: nextSections,
        },
      };
    });
  }, [selectedTemplateDocument]);

  const moveTemplateSection = useCallback((sectionKey, direction) => {
    setTemplateSettings((current) => {
      const currentDocumentSettings = current?.[selectedTemplateDocument] || {};
      return {
        ...current,
        [selectedTemplateDocument]: {
          ...currentDocumentSettings,
          layout_sections: moveOrderedItem(currentDocumentSettings.layout_sections || [], sectionKey, direction),
        },
      };
    });
  }, [selectedTemplateDocument]);

  const replaceTemplateSections = useCallback((nextSections) => {
    setTemplateSettings((current) => {
      const currentDocumentSettings = current?.[selectedTemplateDocument] || {};
      return {
        ...current,
        [selectedTemplateDocument]: {
          ...currentDocumentSettings,
          layout_sections: nextSections,
        },
      };
    });
  }, [selectedTemplateDocument]);

  const updateTemplateColumn = useCallback((columnKey, patch) => {
    setTemplateSettings((current) => {
      const currentDocumentSettings = current?.[selectedTemplateDocument] || {};
      const nextColumns = (currentDocumentSettings.column_schema || []).map((column) => (
        column.key === columnKey ? { ...column, ...patch } : column
      ));
      return {
        ...current,
        [selectedTemplateDocument]: {
          ...currentDocumentSettings,
          column_schema: nextColumns,
        },
      };
    });
  }, [selectedTemplateDocument]);

  const moveTemplateColumn = useCallback((columnKey, direction) => {
    setTemplateSettings((current) => {
      const currentDocumentSettings = current?.[selectedTemplateDocument] || {};
      return {
        ...current,
        [selectedTemplateDocument]: {
          ...currentDocumentSettings,
          column_schema: moveOrderedItem(currentDocumentSettings.column_schema || [], columnKey, direction),
        },
      };
    });
  }, [selectedTemplateDocument]);

  const replaceTemplateColumns = useCallback((nextColumns) => {
    setTemplateSettings((current) => {
      const currentDocumentSettings = current?.[selectedTemplateDocument] || {};
      return {
        ...current,
        [selectedTemplateDocument]: {
          ...currentDocumentSettings,
          column_schema: nextColumns,
        },
      };
    });
  }, [selectedTemplateDocument]);

  const applyTemplateJsonDraft = useCallback((jsonDraft) => {
    try {
      const parsed = JSON.parse(jsonDraft || '{}');
      setTemplateSettings((current) => ({
        ...current,
        [selectedTemplateDocument]: parsed,
      }));
      toast.success('JSON aplicado ao rascunho do template');
    } catch (error) {
      toast.error('JSON inválido. Corrija a estrutura antes de aplicar.');
    }
  }, [selectedTemplateDocument]);

  const applyTemplatePresetToAllDocuments = useCallback(() => {
    setTemplateSettings((current) => {
      const nextSettings = { ...current };
      PAYROLL_TEMPLATE_FALLBACK_DOCUMENTS.forEach((document) => {
        nextSettings[document.key] = {
          ...(current?.[document.key] || {}),
          ...buildTemplatePresetOverrides(document.key, selectedTemplatePreset),
        };
      });
      return nextSettings;
    });
    toast.success('Preset aplicado aos 4 documentos RH');
  }, [selectedTemplatePreset]);

  const buildTemplateRequestPayload = useCallback((documentKey = selectedTemplateDocument) => ({
    document_key: documentKey,
    settings: templateSettings?.[documentKey] || {},
    month: periodMonth,
    year: periodYear,
    scope: reportScope,
    store_location: reportScope === 'store' && reportStoreLocation !== 'all' ? reportStoreLocation : undefined,
    employee_number: reportEmployeeNumber !== 'all' ? reportEmployeeNumber : undefined,
    payroll_id: documentPayrollId || undefined,
  }), [documentPayrollId, periodMonth, periodYear, reportEmployeeNumber, reportScope, reportStoreLocation, selectedTemplateDocument, templateSettings]);

  const handleSaveTemplateSettings = useCallback(async () => {
    try {
      setTemplateSettingsSaving(true);
      const response = await payrollAPI.saveTemplateSettings({ settings: templateSettings });
      const payload = response.data || response;
      setTemplateSettings(payload.settings || {});
      toast.success('Configuração global dos templates guardada');
      if (activeTab === 'reports') {
        await loadDocumentsData();
      }
    } catch (error) {
      toast.error(getErrorMessage(error, 'Erro ao guardar templates RH'));
    } finally {
      setTemplateSettingsSaving(false);
    }
  }, [activeTab, loadDocumentsData, templateSettings]);

  const handlePreviewTemplatePdf = useCallback(async () => {
    try {
      setTemplatePreviewLoading(true);
      const response = await payrollAPI.previewTemplatePdf(buildTemplateRequestPayload());
      openBlobInNewTab(response.data);
      toast.success('Pré-visualização PDF pronta');
    } catch (error) {
      toast.error(getErrorMessage(error, 'Erro ao gerar pré-visualização PDF'));
    } finally {
      setTemplatePreviewLoading(false);
    }
  }, [buildTemplateRequestPayload]);

  const handleExportTemplateTestXlsx = useCallback(async () => {
    try {
      setTemplateExcelLoading(true);
      const response = await payrollAPI.exportTemplateTestXlsx(buildTemplateRequestPayload());
      downloadBlobFile(response.data, `template_teste_${selectedTemplateDocument}_${periodYear}_${String(periodMonth).padStart(2, '0')}.xlsx`);
      toast.success('Excel de teste exportado');
    } catch (error) {
      toast.error(getErrorMessage(error, 'Erro ao exportar Excel de teste'));
    } finally {
      setTemplateExcelLoading(false);
    }
  }, [buildTemplateRequestPayload, periodMonth, periodYear, selectedTemplateDocument]);

  const handleSaveManualAttendance = async () => {
    if (!manualAttendanceForm.employee_number || !manualAttendanceForm.date) {
      toast.error('Seleccione o trabalhador e a data');
      return;
    }

    setSubmitting(true);
    try {
      await payrollAPI.createManualAttendanceEntry(manualAttendanceForm);
      toast.success('Assiduidade manual registada');
      setManualAttendanceForm({ employee_number: '', date: '', work_start: '08:00', lunch_start: '12:00', lunch_end: '13:00', work_end: '17:00', notes: '' });
      await loadAttendanceData({ force: true });
      if (activeTab === 'reports') {
        await loadDocumentsData();
      }
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Erro ao lançar assiduidade manual');
    } finally {
      setSubmitting(false);
    }
  };

  const handleResetAttendanceOperationalData = async () => {
    if (!window.confirm('Isto vai limpar os dados operacionais de assiduidade e preparar uma base limpa. Continuar?')) return;

    setSubmitting(true);
    try {
      await payrollAPI.resetAttendanceOperationalData({
        clear_shifts: attendanceResetIncludesShifts,
        clear_records: true,
        clear_absences: true,
        clear_month_closures: true,
      });
      toast.success('Base de assiduidade reiniciada');
      await loadAttendanceData({ force: true });
      await loadDocumentsData();
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Erro ao reiniciar assiduidade');
    } finally {
      setSubmitting(false);
    }
  };

  const handleSavePayrollEdit = async () => {
    if (!editingPayroll) return;

    setSubmitting(true);
    try {
      const payload = {
        base_salary: parseFloat(payrollEditForm.base_salary) || 0,
        overtime_hours: parseFloat(payrollEditForm.overtime_hours) || 0,
        overtime_rate: parseFloat(payrollEditForm.overtime_rate) || 1.5,
        attendance_bonus: parseFloat(payrollEditForm.attendance_bonus) || 0,
        performance_bonus: parseFloat(payrollEditForm.performance_bonus) || 0,
        other_bonuses: parseFloat(payrollEditForm.other_bonuses) || 0,
        transport_allowance: parseFloat(payrollEditForm.transport_allowance) || 0,
        meal_allowance: parseFloat(payrollEditForm.meal_allowance) || 0,
        other_allowances: parseFloat(payrollEditForm.other_allowances) || 0,
        advance_deduction: parseFloat(payrollEditForm.advance_deduction) || 0,
        other_deductions: parseFloat(payrollEditForm.other_deductions) || 0,
        notes: payrollEditForm.notes,
      };

      if (String(payrollEditForm.manual_commission || '').trim() !== '') {
        payload.manual_commission = parseFloat(payrollEditForm.manual_commission) || 0;
      }

      const response = await payrollAPI.update(editingPayroll.id, payload);
      const updatedPayroll = response.data?.payroll;
      if (updatedPayroll && selectedPayroll?.id === updatedPayroll.id) {
        setSelectedPayroll(updatedPayroll);
      }
      toast.success('Folha draft actualizada');
      resetPayrollEditState();
      await loadData();
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Erro ao actualizar folha');
    } finally {
      setSubmitting(false);
    }
  };

  const handleAttendanceImport = async () => {
    if (!attendanceFile) {
      toast.error('Selecione um ficheiro de assiduidade (.csv, .xlsx ou .xls)');
      return;
    }
    try {
      setAttendanceUploading(true);
      const formData = new FormData();
      formData.append('file', attendanceFile);
      formData.append('delimiter', ',');
      const result = await payrollAPI.importAttendanceCsv(formData);
      toast.success(`Importação concluída: ${result.data?.imported || 0} registos`);
      setAttendanceFile(null);
      await loadData();
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Erro ao importar assiduidade');
    } finally {
      setAttendanceUploading(false);
    }
  };

  const handleSaveShift = async () => {
    if (!shiftForm.store_location) {
      toast.error('Seleccione a loja do turno');
      return;
    }
    try {
      await payrollAPI.saveAttendanceShift({
        ...shiftForm,
        weekday: shiftForm.weekday === 'all' ? null : Number(shiftForm.weekday),
        employee_number: shiftForm.employee_number || null,
      });
      toast.success('Turno rotativo guardado');
      setShiftForm({ id: null, store_location: '', employee_number: '', weekday: 'all', shift_name: 'Turno padrão', work_start: '08:00', lunch_start: '12:00', lunch_end: '13:00', work_end: '17:00', tolerance_minutes: 10 });
      await loadData();
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Erro ao guardar turno');
    }
  };

  const handleSaveJustifiedAbsence = async () => {
    if (!absenceForm.employee_number || !absenceForm.date || !absenceForm.reason) {
      toast.error('Preencha nº colaborador, data e motivo');
      return;
    }
    try {
      if (absenceForm.id) {
        await payrollAPI.updateJustifiedAbsence(absenceForm.id, absenceForm);
        toast.success('Falta justificada actualizada');
      } else {
        await payrollAPI.createJustifiedAbsence(absenceForm);
        toast.success('Falta justificada registada');
      }
      setAbsenceForm({ id: null, employee_number: '', date: '', reason: '', is_paid: true });
      await loadData();
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Erro ao registar falta justificada');
    }
  };

  const handleDeleteShift = async (shiftId) => {
    try {
      await payrollAPI.deleteAttendanceShift(shiftId);
      toast.success('Turno eliminado');
      await loadData();
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Erro ao eliminar turno');
    }
  };

  const handleDeleteJustifiedAbsence = async (absenceId) => {
    try {
      await payrollAPI.deleteJustifiedAbsence(absenceId);
      toast.success('Falta justificada eliminada');
      await loadData();
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Erro ao eliminar falta justificada');
    }
  };

  const handleSaveAttendanceRecord = async () => {
    if (!attendanceEditForm.id) return;
    try {
      await payrollAPI.updateAttendanceRecord(attendanceEditForm.id, attendanceEditForm);
      toast.success('Registo de assiduidade actualizado');
      setAttendanceEditForm({ id: null, employee_number: '', employee_name: '', work_start: '', lunch_start: '', lunch_end: '', work_end: '' });
      await loadData();
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Erro ao actualizar registo');
    }
  };

  const handleDeleteAttendanceRecord = async (recordId) => {
    try {
      await payrollAPI.deleteAttendanceRecord(recordId);
      toast.success('Registo de assiduidade eliminado');
      await loadData();
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Erro ao eliminar registo');
    }
  };

  const handleCloseAttendanceMonth = async () => {
    try {
      await payrollAPI.closeAttendanceMonth({ month: periodMonth, year: periodYear, notes: 'Fecho mensal a partir da assiduidade' });
      toast.success('Fecho mensal registado');
      await loadData();
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Erro ao fechar assiduidade do mês');
    }
  };

  const handleReviewAttendanceClose = async (closureId, action) => {
    try {
      await payrollAPI.reviewAttendanceMonthClose(closureId, { action, notes: action === 'approve' ? 'Aprovado pelo gestor/admin' : 'Reaberto para ajustes' });
      toast.success(action === 'approve' ? 'Fecho aprovado' : 'Fecho reaberto');
      await loadData();
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Erro ao rever fecho mensal');
    }
  };

  // Stats calculation
  const stats = useMemo(() => {
    const draft = payrolls.filter(p => p.status === 'draft').length;
    const confirmed = payrolls.filter(p => p.status === 'confirmed').length;
    const paid = payrolls.filter(p => p.status === 'paid').length;
    return { draft, confirmed, paid, total: payrolls.length };
  }, [payrolls]);
  const hasPayrollData = Boolean(
    payrolls.length ||
    employees.length ||
    irpsTables.length ||
    eligibleEmployees.total
  );

  // When employee selected in form, load their base salary
  useEffect(() => {
    if (formData.employee_id) {
      const emp = employees.find(e => e.id === formData.employee_id);
      if (emp) {
        setFormData(prev => ({
          ...prev,
          base_salary: emp.base_salary === null || emp.base_salary === undefined ? '' : String(emp.base_salary),
          attendance_bonus: emp.attendance_bonus === null || emp.attendance_bonus === undefined ? '0' : String(emp.attendance_bonus),
          performance_bonus: emp.performance_bonus === null || emp.performance_bonus === undefined ? '0' : String(emp.performance_bonus),
          other_bonuses: emp.other_bonuses === null || emp.other_bonuses === undefined ? '0' : String(emp.other_bonuses),
        }));
      }
    }
  }, [formData.employee_id, employees]);

  if (!canViewPayroll) {
    return (
      <div className="p-6" data-testid="payroll-access-denied">
        <Card>
          <CardContent className="py-10 text-center">
            <AlertTriangle className="mx-auto mb-3 h-10 w-10 text-amber-500" />
            <h2 className="text-lg font-semibold text-slate-900">{t('payrollPage.noAccessTitle')}</h2>
            <p className="mt-2 text-sm text-slate-500">{t('payrollPage.noAccessDescription')}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-3 p-4 md:p-5" data-testid="payroll-page">
      <Tabs value={activeTab} onValueChange={setActiveTab}>
      <ModulePageHero
        testId="payroll-page-hero"
        title={t('payrollPage.title')}
        description="Processamento salarial por período com leitura operacional compacta."
        icon={Calculator}
        badge={t('payrollPage.badge')}
        className="p-4 md:p-5"
        compact
        stats={[]}
        actions={(
          <>
            {canManagePayroll && (
              <Button variant="outline" size="sm" onClick={() => setConfigDialogOpen(true)} data-testid="config-button">
                <Settings className="h-4 w-4 mr-2" />
                {t('nav.settings')}
              </Button>
            )}

            {canProcess && (
            <Dialog open={processDialogOpen} onOpenChange={(open) => {
              setProcessDialogOpen(open);
              if (!open) {
                setSimulationResult(null);
                setFormData(initialFormData);
              }
            }}>
              <DialogTrigger asChild>
                <Button size="sm" data-testid="process-payroll-button">
                  <Calculator className="mr-2 h-4 w-4" />
                  Gerar folha
                </Button>
              </DialogTrigger>
              <DialogContent className="w-[min(96vw,1220px)] max-w-[1220px] max-h-[90vh] overflow-y-auto rounded-2xl border border-slate-200 bg-white p-4 sm:p-5">
                <DialogHeader className="border-b border-slate-200 pb-4">
                  <DialogTitle>Gerar folha salarial do período</DialogTitle>
                  <DialogDescription>
                    Gere a folha, reveja bruto/líquido e confirme os totais antes de marcar como processada. INSS trabalhador: {(inssConfig.employee_rate * 100).toFixed(0)}% · INSS empresa: {(inssConfig.employer_rate * 100).toFixed(0)}%.
                  </DialogDescription>
                </DialogHeader>
                
                <div className="grid gap-4 xl:grid-cols-[minmax(0,1.12fr)_360px]">
                  {/* Left Column - Form */}
                  <div className="space-y-3">
                    <section className="rounded-xl border border-slate-200 bg-slate-50/70 p-3 sm:p-4">
                      <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">Base operacional</p>
                      <h3 className="mt-1 text-base font-semibold text-slate-950">Funcionário e período</h3>
                      <div className="mt-4 space-y-4">
                    <div className="space-y-2">
                      <Label>{t('labels.employee')} *</Label>
                      <Select 
                        value={formData.employee_id} 
                        onValueChange={(v) => setFormData({...formData, employee_id: v})}
                      >
                        <SelectTrigger data-testid="employee-select">
                          <SelectValue placeholder={t('payrollPage.selectEmployee')} />
                        </SelectTrigger>
                        <SelectContent>
                          {processEligibleEmployees.map(emp => (
                            <SelectItem key={emp.id} value={emp.id} disabled={!emp.dossier_complete}>
                              <div className="flex items-center gap-2">
                                {emp.dossier_complete ? (
                                  <UserCheck className="h-4 w-4 text-green-500" />
                                ) : (
                                  <AlertTriangle className="h-4 w-4 text-amber-500" />
                                )}
                                <span>{emp.employee_number || '—'} · {emp.full_name || emp.name}{emp.role ? ` · ${emp.role}` : ''}</span>
                                {!emp.dossier_complete && (
                                  <Badge variant="outline" className="ml-2 text-xs">
                                    {t('payrollPage.incomplete', { count: emp.completion_percentage })}
                                  </Badge>
                                )}
                              </div>
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      {!processEligibleEmployees.length && (
                        <p className="text-xs text-slate-500">
                          Nenhum colaborador está disponível para processamento neste período. Trabalhadores desligados só aparecem até ao mês efectivo da saída.
                        </p>
                      )}
                      {formData.employee_id && !processEligibleEmployees.find(e => e.id === formData.employee_id)?.dossier_complete && (
                        <p className="text-xs text-amber-600 flex items-center gap-1">
                          <AlertTriangle className="h-3 w-3" />
                          {t('payrollPage.completeDossierFirst')}
                        </p>
                      )}
                    </div>
                    
                    <div className="grid grid-cols-2 gap-3">
                      <div className="space-y-2">
                        <Label>{t('payrollPage.month')} *</Label>
                        <Select 
                          value={formData.period_month.toString()} 
                          onValueChange={(v) => setFormData({...formData, period_month: parseInt(v)})}
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {MONTHS.map(m => (
                              <SelectItem key={m.value} value={m.value.toString()}>{m.label}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label>{t('payrollPage.year')} *</Label>
                        <Select 
                          value={formData.period_year.toString()} 
                          onValueChange={(v) => setFormData({...formData, period_year: parseInt(v)})}
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {[2024, 2025, 2026].map(y => (
                              <SelectItem key={y} value={y.toString()}>{y}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                      </div>
                    </section>
                    
                    <section className="rounded-xl border border-slate-200 bg-slate-50/70 p-3 sm:p-4">
                      <div className="space-y-3">
                      <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">Rendimentos</p>
                      <h4 className="font-medium text-sm flex items-center gap-2">
                        <TrendingUp className="h-4 w-4 text-green-500" />
                        {t('payrollPage.earnings')}
                      </h4>
                      
                      <div className="grid grid-cols-2 gap-3">
                        <div className="space-y-1">
                          <Label className="text-xs">{t('payrollPage.baseSalary')} (MZN) *</Label>
                          <Input
                            type="number"
                            step="0.01"
                            min="0"
                            value={formData.base_salary}
                            onChange={(e) => setFormData({...formData, base_salary: e.target.value})}
                            placeholder="0.00"
                            data-testid="base-salary-input"
                          />
                        </div>
                        <div className="space-y-1">
                          <Label className="text-xs">{t('payrollPage.manualCommission')} (MZN)</Label>
                          <Input
                            type="number"
                            step="0.01"
                            min="0"
                            value={formData.manual_commission}
                            onChange={(e) => setFormData({...formData, manual_commission: e.target.value})}
                            placeholder={t('payrollPage.automatic')}
                          />
                        </div>
                      </div>
                      
                      <div className="grid grid-cols-2 gap-3">
                        <div className="space-y-1">
                          <Label className="text-xs">Horas Extra</Label>
                          <Input
                            type="number"
                            step="0.5"
                            min="0"
                            value={formData.overtime_hours}
                            onChange={(e) => setFormData({...formData, overtime_hours: e.target.value})}
                            placeholder="0"
                          />
                        </div>
                        <div className="space-y-1">
                          <Label className="text-xs">Taxa H. Extra (%)</Label>
                          <Input
                            type="number"
                            step="0.1"
                            min="1"
                            value={formData.overtime_rate}
                            onChange={(e) => setFormData({...formData, overtime_rate: e.target.value})}
                            placeholder="1.5"
                          />
                        </div>
                      </div>

                      <div className="rounded-xl border border-amber-200 bg-amber-50/80 p-3" data-testid="payroll-manual-days-and-notice-panel">
                        <div className="flex items-start justify-between gap-3">
                          <div>
                            <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-amber-700">Saída e acertos finais</p>
                            <h5 className="mt-1 text-sm font-semibold text-slate-900">Dias trabalhados e aviso prévio</h5>
                            <p className="mt-1 text-xs text-slate-600">
                              O campo manual sobrepõe o cálculo automático. “Não aplicar” cobre também casos de mútuo acordo.
                            </p>
                          </div>
                          {processExitMonthContext?.isExitMonth && (
                            <Badge variant="outline" className="border-amber-300 bg-white text-amber-700" data-testid="payroll-exit-month-badge">
                              Saída no mês
                            </Badge>
                          )}
                        </div>

                        <div className="mt-3 grid gap-3 md:grid-cols-2">
                          <div className="space-y-1">
                            <Label htmlFor="manual-days-worked-input" className="text-xs">Dias Trabalhados (override manual)</Label>
                            <Input
                              id="manual-days-worked-input"
                              type="number"
                              min="0"
                              max="30"
                              step="1"
                              value={formData.manual_days_worked}
                              onChange={(e) => setFormData({ ...formData, manual_days_worked: e.target.value })}
                              placeholder="Ex.: 24"
                              data-testid="manual-days-worked-input"
                            />
                          </div>

                          <div className="space-y-1">
                            <Label className="text-xs">Aviso Prévio</Label>
                            <Select value={formData.notice_pay_days} onValueChange={(value) => setFormData({ ...formData, notice_pay_days: value })}>
                              <SelectTrigger data-testid="notice-pay-days-select">
                                <SelectValue placeholder="Não aplicar" />
                              </SelectTrigger>
                              <SelectContent>
                                {NOTICE_PAY_OPTIONS.map((option) => (
                                  <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                        </div>

                        {selectedProcessEmployee?.contract_end_date && (
                          <div className="mt-3 rounded-lg border border-white/80 bg-white/90 p-3 text-xs text-slate-700" data-testid="payroll-exit-rule-hint">
                            <p>
                              Data de saída registada: <span className="font-semibold">{formatPayrollShortDate(selectedProcessEmployee.contract_end_date)}</span>
                            </p>
                            {processExitMonthContext?.isExitMonth ? (
                              <p className="mt-1 text-amber-700">
                                Sem assiduidade preenchida, o sistema calculará automaticamente <span className="font-semibold">{processExitMonthContext.automaticWorkedDays} dias</span> neste mês.
                              </p>
                            ) : (
                              <p className="mt-1 text-slate-600">
                                A data de saída está fora do mês processado, por isso a regra proporcional automática não será aplicada neste processamento.
                              </p>
                            )}
                          </div>
                        )}
                      </div>
                      
                      <div className="grid grid-cols-3 gap-2">
                        <div className="space-y-1">
                          <Label className="text-xs">Bónus Assiduidade</Label>
                          <Input
                            type="number"
                            step="0.01"
                            min="0"
                            value={formData.attendance_bonus}
                            onChange={(e) => setFormData({...formData, attendance_bonus: e.target.value})}
                            placeholder="0"
                          />
                        </div>
                        <div className="space-y-1">
                          <Label className="text-xs">Bónus Performance</Label>
                          <Input
                            type="number"
                            step="0.01"
                            min="0"
                            value={formData.performance_bonus}
                            onChange={(e) => setFormData({...formData, performance_bonus: e.target.value})}
                            placeholder="0"
                          />
                        </div>
                        <div className="space-y-1">
                          <Label className="text-xs">Outros Bónus</Label>
                          <Input
                            type="number"
                            step="0.01"
                            min="0"
                            value={formData.other_bonuses}
                            onChange={(e) => setFormData({...formData, other_bonuses: e.target.value})}
                            placeholder="0"
                          />
                        </div>
                      </div>
                      
                      <div className="grid grid-cols-3 gap-2">
                        <div className="space-y-1">
                          <Label className="text-xs">Sub. Transporte</Label>
                          <Input
                            type="number"
                            step="0.01"
                            min="0"
                            value={formData.transport_allowance}
                            onChange={(e) => setFormData({...formData, transport_allowance: e.target.value})}
                            placeholder="0"
                          />
                        </div>
                        <div className="space-y-1">
                          <Label className="text-xs">Sub. Alimentação</Label>
                          <Input
                            type="number"
                            step="0.01"
                            min="0"
                            value={formData.meal_allowance}
                            onChange={(e) => setFormData({...formData, meal_allowance: e.target.value})}
                            placeholder="0"
                          />
                        </div>
                        <div className="space-y-1">
                          <Label className="text-xs">Outros Subsídios</Label>
                          <Input
                            type="number"
                            step="0.01"
                            min="0"
                            value={formData.other_allowances}
                            onChange={(e) => setFormData({...formData, other_allowances: e.target.value})}
                            placeholder="0"
                          />
                        </div>
                      </div>
                    </div>
                    </section>
                    
                    <section className="rounded-xl border border-slate-200 bg-slate-50/70 p-3 sm:p-4">
                      <div className="grid gap-4 lg:grid-cols-[minmax(0,0.9fr)_minmax(0,1.1fr)]">
                        <div className="space-y-3">
                          <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">Descontos</p>
                      <h4 className="font-medium text-sm flex items-center gap-2">
                        <Minus className="h-4 w-4 text-red-500" />
                        Descontos Internos
                      </h4>
                      
                      <div className="grid grid-cols-2 gap-3">
                        <div className="space-y-1">
                          <Label className="text-xs">Adiantamento</Label>
                          <Input
                            type="number"
                            step="0.01"
                            min="0"
                            value={formData.advance_deduction}
                            onChange={(e) => setFormData({...formData, advance_deduction: e.target.value})}
                            placeholder="0"
                          />
                        </div>
                        <div className="space-y-1">
                          <Label className="text-xs">Outros Descontos</Label>
                          <Input
                            type="number"
                            step="0.01"
                            min="0"
                            value={formData.other_deductions}
                            onChange={(e) => setFormData({...formData, other_deductions: e.target.value})}
                            placeholder="0"
                          />
                        </div>
                      </div>
                        </div>
                    
                    <div className="space-y-2">
                      <Label className="text-xs uppercase tracking-[0.16em] text-slate-500">Observações</Label>
                      <Textarea
                        value={formData.notes}
                        onChange={(e) => setFormData({...formData, notes: e.target.value})}
                        placeholder="Notas adicionais..."
                        rows={4}
                        className="min-h-[140px] rounded-xl border-slate-300 bg-white"
                      />
                    </div>
                      </div>
                    </section>
                  </div>
                  
                  {/* Right Column - Simulation Result */}
                  <div className="space-y-3">
                    <div className="flex gap-2">
                      <Button 
                        variant="outline" 
                        className="flex-1 rounded-xl"
                        onClick={handleSimulate}
                        disabled={submitting || !formData.employee_id}
                        data-testid="simulate-payroll-button"
                      >
                        <Calculator className="h-4 w-4 mr-2" />
                        {t('payrollPage.simulate')}
                      </Button>
                    </div>
                    
                    {simulationResult ? (
                      <Card className="rounded-xl border-slate-200 bg-slate-50" data-testid="payroll-simulation-result-card">
                        <CardHeader className="pb-2">
                          <CardTitle className="text-sm">{t('payrollPage.simulationFor', { employee: simulationResult.employee_name })}</CardTitle>
                          <CardDescription>{t('payrollPage.periodLabel')}: {simulationResult.period}</CardDescription>
                        </CardHeader>
                        <CardContent className="space-y-3 text-sm">
                          <div className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs text-slate-700" data-testid="payroll-simulation-days-summary">
                            <div className="flex items-center justify-between gap-2">
                              <span>Salário base diário</span>
                              <span className="font-semibold">{formatCurrency(simulationResult.daily_base_salary || 0)}</span>
                            </div>
                            <div className="mt-1 flex items-center justify-between gap-2">
                              <span>Dias trabalhados</span>
                              <span className="font-semibold">{simulationResult.days_worked ?? 'Mês completo'}</span>
                            </div>
                            <div className="mt-1 flex items-center justify-between gap-2">
                              <span>Origem</span>
                              <span className="font-semibold">
                                {simulationResult.days_worked_source === 'manual_override'
                                  ? 'Override manual'
                                  : simulationResult.days_worked_source === 'termination_auto'
                                    ? 'Automático por saída'
                                    : 'Comportamento normal'}
                              </span>
                            </div>
                            {simulationResult.contract_end_date && (
                              <div className="mt-1 flex items-center justify-between gap-2">
                                <span>Data de saída</span>
                                <span className="font-semibold">{formatPayrollShortDate(simulationResult.contract_end_date)}</span>
                              </div>
                            )}
                          </div>

                          {/* Rendimentos */}
                          <div className="space-y-1">
                            {simulationResult.days_worked !== null && simulationResult.days_worked !== undefined && (
                              <div className="flex justify-between text-slate-500">
                                <span>Base contratual mensal</span>
                                <span>{formatCurrency(simulationResult.monthly_base_salary || 0)}</span>
                              </div>
                            )}
                            <div className="flex justify-between" data-testid="payroll-simulation-base-salary-row">
                              <span>{simulationResult.days_worked !== null && simulationResult.days_worked !== undefined ? `Salário proporcional (${simulationResult.days_worked}/30 dias)` : t('payrollPage.baseSalary')}</span>
                              <span>{formatCurrency(simulationResult.base_salary)}</span>
                            </div>
                            {simulationResult.notice_pay_value > 0 && (
                              <div className="flex justify-between text-green-600" data-testid="payroll-simulation-notice-pay-row">
                                <span>+ Aviso prévio ({simulationResult.notice_pay_days} dias)</span>
                                <span>{formatCurrency(simulationResult.notice_pay_value)}</span>
                              </div>
                            )}
                            {simulationResult.overtime_value > 0 && (
                              <div className="flex justify-between text-green-600">
                                <span>+ Horas Extra</span>
                                <span>{formatCurrency(simulationResult.overtime_value)}</span>
                              </div>
                            )}
                            <div className="flex justify-between text-green-600">
                              <span>+ {t('payrollPage.commission')}</span>
                              <span>{formatCurrency(simulationResult.commission)}</span>
                            </div>
                            {simulationResult.commission_data && (
                              <p className="text-xs text-muted-foreground pl-2">
                                {simulationResult.commission_data.reason}
                              </p>
                            )}
                            {simulationResult.total_bonuses > 0 && (
                              <div className="flex justify-between text-green-600">
                                <span>+ {t('payrollPage.bonuses')}</span>
                                <span>{formatCurrency(simulationResult.total_bonuses)}</span>
                              </div>
                            )}
                            {simulationResult.total_allowances > 0 && (
                              <div className="flex justify-between text-green-600">
                                <span>+ {t('payrollPage.allowances')}</span>
                                <span>{formatCurrency(simulationResult.total_allowances)}</span>
                              </div>
                            )}
                          </div>
                          
                          <Separator />
                          
                          <div className="flex justify-between font-medium">
                            <span>{t('payrollPage.grossSalary')}</span>
                            <span>{formatCurrency(simulationResult.gross_salary)}</span>
                          </div>
                          
                          <Separator />
                          
                          {/* Descontos Legais */}
                          <div className="space-y-1">
                            <div className="flex justify-between text-red-600">
                              <span>- INSS ({(inssConfig.employee_rate * 100).toFixed(0)}%)</span>
                              <span>{formatCurrency(simulationResult.inss_employee)}</span>
                            </div>
                            <div className="flex justify-between text-red-600">
                              <span>- IRPS ({simulationResult.irps_table_year})</span>
                              <span>{formatCurrency(simulationResult.irps_retention)}</span>
                            </div>
                            {simulationResult.advance_deduction > 0 && (
                              <div className="flex justify-between text-red-600">
                                <span>- Adiantamento</span>
                                <span>{formatCurrency(simulationResult.advance_deduction)}</span>
                              </div>
                            )}
                            {simulationResult.other_deductions > 0 && (
                              <div className="flex justify-between text-red-600">
                                <span>- Outros Descontos</span>
                                <span>{formatCurrency(simulationResult.other_deductions)}</span>
                              </div>
                            )}
                          </div>
                          
                          <div className="flex justify-between text-sm text-red-600">
                            <span>{t('payrollPage.totalDeductions')}</span>
                            <span>{formatCurrency(simulationResult.total_deductions)}</span>
                          </div>
                          
                          <Separator />
                          
                          <div className="flex justify-between font-bold text-lg">
                            <span>{t('payrollPage.netSalary')}</span>
                            <span className="text-green-600">{formatCurrency(simulationResult.net_salary)}</span>
                          </div>
                          
                          {/* Info INSS Patronal */}
                          <div className="mt-4 p-2 bg-blue-50 rounded text-xs">
                            <p className="font-medium text-blue-700">{t('payrollPage.employerContributionInfo')}</p>
                            <p className="text-blue-600">
                              INSS Empresa ({(inssConfig.employer_rate * 100).toFixed(0)}%): {formatCurrency(simulationResult.inss_employer)}
                            </p>
                          </div>
                        </CardContent>
                      </Card>
                    ) : (
                      <Card className="rounded-xl border-dashed bg-slate-50">
                        <CardContent className="py-12 text-center text-muted-foreground">
                          <Calculator className="h-12 w-12 mx-auto mb-4 opacity-30" />
                          <p>{t('payrollPage.fillAndSimulate')}</p>
                        </CardContent>
                      </Card>
                    )}
                  </div>
                </div>
                
                <DialogFooter>
                  <Button variant="outline" onClick={() => setProcessDialogOpen(false)}>
                    Cancelar
                  </Button>
                  <Button 
                    onClick={handleProcessPayroll} 
                    disabled={submitting || !simulationResult}
                    data-testid="submit-payroll-button"
                  >
                    {submitting ? 'A gerar...' : 'Gerar folha do período'}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
            )}
          </>
        )}
        footer={(
          <div className="space-y-3">
            <TabsList className="flex h-auto flex-wrap gap-1.5 rounded-xl border border-slate-200 bg-white/90 p-1 shadow-sm" data-testid="payroll-tabs-list">
              <TabsTrigger value="payrolls" className="h-8 gap-2 rounded-lg px-3 text-xs" data-testid="payroll-tab-payrolls">
                <Receipt className="h-4 w-4" />
                Folhas ({stats.total})
              </TabsTrigger>
              <TabsTrigger value="employees" className="h-8 gap-2 rounded-lg px-3 text-xs" data-testid="payroll-tab-employees">
                <Users className="h-4 w-4" />
                Funcionários ({employeeDirectory.total})
                {employeeDirectory.incomplete > 0 && (
                  <Badge variant="secondary" className="ml-1 bg-amber-100 text-amber-700">
                    {employeeDirectory.incomplete}
                  </Badge>
                )}
              </TabsTrigger>
              <TabsTrigger value="attendance" className="h-8 gap-2 rounded-lg px-3 text-xs" data-testid="payroll-tab-attendance">
                <Clock className="h-4 w-4" />
                Assiduidade ({attendanceRecords.length})
              </TabsTrigger>
              <TabsTrigger value="vacations" className="h-8 gap-2 rounded-lg px-3 text-xs" data-testid="payroll-tab-vacations">
                <Calendar className="h-4 w-4" />
                Férias
              </TabsTrigger>
              <TabsTrigger value="reports" className="h-8 gap-2 rounded-lg px-3 text-xs" data-testid="payroll-tab-reports">
                <FileText className="h-4 w-4" />
                Documentos RH
              </TabsTrigger>
            </TabsList>
          </div>
        )}
      />

      <ModuleStateBoundary
        loading={loading}
        hasData={hasPayrollData}
        partialIssues={visiblePartialIssues}
        loadingTestId="payroll-loading-state"
        partialIssuesTestId="payroll-partial-issues"
        emptyState={(
          <EmptyStatePanel
            title="Folha salarial sem dados iniciais"
            description="Configure funcionários elegíveis, localizações e tabelas obrigatórias para processar salários sem depender do legado."
            ctaLabel="Abrir Configurações"
            ctaTo="/settings"
            testId="payroll-empty-state"
          />
        )}
      >

      <PayrollAlertsPanel
        incompleteEmployees={employeeDirectory.incomplete}
        hasIrpsTableForSelectedYear={hasIrpsTableForSelectedYear}
        periodYear={periodYear}
        onResolveIrpsGap={handleResolveIRPSTableGap}
        configSubmitting={configSubmitting}
      />

        {/* Payrolls Tab */}
        <TabsContent value="payrolls" className="space-y-4">
          <PayrollFiltersToolbar
            monthLabel={selectedMonthLabel}
            periodYear={periodYear}
            totalRows={sortedPayrolls.length}
            activeFiltersCount={activePayrollFiltersCount}
            sortLabel={PAYROLL_SORT_OPTIONS.find((option) => option.value === payrollSortField)?.label || payrollSortField}
            filteredSummary={filteredSummary}
            searchQuery={searchQuery}
            onSearchChange={setSearchQuery}
            employeeFilter={employeeFilter}
            onEmployeeFilterChange={setEmployeeFilter}
            employees={employees}
            periodMonth={periodMonth}
            onPeriodMonthChange={(value) => setPeriodMonth(parseInt(value, 10))}
            onPeriodYearChange={(value) => setPeriodYear(parseInt(value, 10))}
            yearOptions={[2024, 2025, 2026]}
            monthOptions={MONTHS}
            statusFilter={statusFilter}
            onStatusFilterChange={setStatusFilter}
            payrollSortField={payrollSortField}
            onPayrollSortFieldChange={setPayrollSortField}
            payrollSortDirection={payrollSortDirection}
            onPayrollSortDirectionChange={setPayrollSortDirection}
            payrollSortOptions={PAYROLL_SORT_OPTIONS}
            onClearFilters={handleClearFilters}
          />

          <div className="grid gap-4 xl:grid-cols-[1.05fr_0.95fr]">
            <PayrollPeriodWorkflowCard summary={payrollOperationalSummary} monthLabel={selectedMonthLabel} year={periodYear} />
            <PayrollStateObligationsCard summary={payrollOperationalSummary} />
          </div>

          {/* Payroll List */}
          <Card data-testid="payroll-list" className="overflow-hidden rounded-2xl border-slate-200 shadow-sm">
            <CardHeader className="border-b border-slate-200 bg-white/90 px-4 py-3 md:px-5">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <CardTitle className="flex items-center gap-2 text-base md:text-lg">
                    <Receipt className="h-5 w-5" />
                    {selectedMonthLabel} {periodYear} ({sortedPayrolls.length})
                  </CardTitle>
                  <CardDescription className="mt-1">Lista compacta para leitura diária, revisão rápida e acções operacionais agrupadas.</CardDescription>
                </div>
                <div className="flex flex-wrap gap-2 text-xs">
                  <Badge variant="outline" className="bg-white">Ordenação: {PAYROLL_SORT_OPTIONS.find((option) => option.value === payrollSortField)?.label || payrollSortField}</Badge>
                  <Badge variant="outline" className="bg-white">Direcção: {payrollSortDirection === 'asc' ? 'Ascendente' : 'Descendente'}</Badge>
                  <Badge variant="outline" className="bg-white">Filtros activos: {activePayrollFiltersCount}</Badge>
                </div>
              </div>
              <div className="mt-3 flex flex-wrap items-center gap-2" data-testid="payroll-bulk-toolbar">
                {selectedPayrollIds.length > 0 && (
                  <Badge variant="outline" className="bg-white" data-testid="payroll-selection-badge">{selectedPayrollIds.length} seleccionada(s)</Badge>
                )}
                {!selectedPayrollIds.length && sortedPayrolls.length > 0 && (
                  <Badge variant="outline" className="bg-slate-50 text-slate-600" data-testid="payroll-smart-period-badge">Sem selecção = período filtrado</Badge>
                )}
                <Button variant="outline" size="sm" onClick={() => toggleAllVisiblePayrolls(!allVisiblePayrollsSelected)} data-testid="payroll-select-all-visible-button">
                  {allVisiblePayrollsSelected ? 'Limpar visíveis' : 'Seleccionar visíveis'}
                </Button>
                <Button variant="outline" size="sm" disabled={!selectedPayrollIds.length} onClick={handleBulkConfirmPayrolls} data-testid="payroll-bulk-confirm-button">
                  Rever em massa
                </Button>
                <Button variant="outline" size="sm" disabled={!sortedPayrolls.length} onClick={handleClosePayrollPeriod} data-testid="payroll-bulk-close-period-button">
                  Fechar período
                </Button>
                <Button variant="outline" size="sm" disabled={!sortedPayrolls.length} onClick={handleDownloadPayrollZip} data-testid="payroll-bulk-export-button">
                  Baixar ZIP
                </Button>
                <Button variant="outline" size="sm" disabled={!selectedPayrollIds.length} onClick={handleExportSelectedPayrollsCsv} data-testid="payroll-bulk-export-csv-button">
                  CSV
                </Button>
              </div>
            </CardHeader>
            <CardContent className="p-0">
              {loading ? (
                <div className="flex items-center justify-center py-12">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
                </div>
              ) : sortedPayrolls.length === 0 ? (
                <div className="text-center py-12">
                  <Receipt className="h-12 w-12 mx-auto text-muted-foreground/50 mb-4" />
                  <p className="text-muted-foreground">
                    Nenhuma folha encontrada para este período
                  </p>
                </div>
              ) : (
                <div className="max-h-[calc(100vh-290px)] overflow-auto">
                  <table className="w-full">
                    <thead className="sticky top-0 z-10 border-b bg-slate-50/95 backdrop-blur">
                      <tr>
                        <th className="text-left py-2 px-2.5 text-[11px] font-semibold uppercase tracking-[0.18em] text-muted-foreground">
                          <Checkbox checked={allVisiblePayrollsSelected} onCheckedChange={(checked) => toggleAllVisiblePayrolls(Boolean(checked))} data-testid="payroll-select-all-checkbox" />
                        </th>
                        <th className="text-left py-2 px-2.5 text-[11px] font-semibold uppercase tracking-[0.18em] text-muted-foreground">Número</th>
                        <th className="text-left py-2 px-2.5 text-[11px] font-semibold uppercase tracking-[0.18em] text-muted-foreground">Funcionário</th>
                        <th className="text-left py-2 px-2.5 text-[11px] font-semibold uppercase tracking-[0.18em] text-muted-foreground">Cargo / Perfil</th>
                        <th className="text-right py-2 px-2.5 text-[11px] font-semibold uppercase tracking-[0.18em] text-muted-foreground">Base</th>
                        <th className="text-right py-2 px-2.5 text-[11px] font-semibold uppercase tracking-[0.18em] text-muted-foreground">Bónus</th>
                        <th className="text-right py-2 px-2.5 text-[11px] font-semibold uppercase tracking-[0.18em] text-muted-foreground">Descontos</th>
                        <th className="text-right py-2 px-2.5 text-[11px] font-semibold uppercase tracking-[0.18em] text-muted-foreground">Bruto</th>
                        <th className="text-right py-2 px-2.5 text-[11px] font-semibold uppercase tracking-[0.18em] text-muted-foreground">INSS</th>
                        <th className="text-right py-2 px-2.5 text-[11px] font-semibold uppercase tracking-[0.18em] text-muted-foreground">IRPS</th>
                        <th className="text-right py-2 px-2.5 text-[11px] font-semibold uppercase tracking-[0.18em] text-muted-foreground">Líquido</th>
                        <th className="text-center py-2 px-2.5 text-[11px] font-semibold uppercase tracking-[0.18em] text-muted-foreground">Estado</th>
                        <th className="text-center py-2 px-2.5 text-[11px] font-semibold uppercase tracking-[0.18em] text-muted-foreground">Acções</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {sortedPayrolls.map(payroll => {
                        const statusConfig = STATUS_CONFIG[payroll.status] || STATUS_CONFIG.draft;
                        const StatusIcon = statusConfig.icon;
                        
                        return (
                          <tr key={payroll.id} className={cn('hover:bg-slate-50/50', selectedPayrollIds.includes(payroll.id) && 'bg-slate-100/80')} data-testid={`payroll-row-${payroll.id}`}>
                            <td className="py-2 px-2.5 align-top">
                              <Checkbox checked={selectedPayrollIds.includes(payroll.id)} onCheckedChange={(checked) => togglePayrollSelection(payroll.id, Boolean(checked))} data-testid={`payroll-select-checkbox-${payroll.id}`} />
                            </td>
                            <td className="py-2 px-2.5 align-top">
                              <div className="space-y-1">
                                <span className="font-mono text-[11px] font-semibold">{getPayrollDisplayReference(payroll)}</span>
                                <p className="text-[11px] text-slate-500">Ref. {String(payroll.id || '').slice(0, 8)}</p>
                              </div>
                            </td>
                            <td className="py-2 px-2.5 align-top">
                              <div className="flex items-start gap-2">
                                <UserCheck className="h-4 w-4 text-slate-400" />
                                <div>
                                  <span className="font-medium text-sm">{payroll.employee_name}</span>
                                  <p className="text-[11px] text-slate-500">Colab. {payroll.employee_number || '—'} • {payroll.period_month?.toString?.().padStart?.(2, '0')}/{payroll.period_year}</p>
                                </div>
                              </div>
                            </td>
                            <td className="py-2 px-2.5 align-top text-sm text-slate-600">
                              {payroll.employee_role || payroll.role || '—'}
                            </td>
                            <td className="py-2 px-2.5 text-right align-top">{formatCurrency(payroll.base_salary)}</td>
                            <td className="py-2 px-2.5 text-right align-top text-emerald-700">{formatCurrency(payroll.total_bonuses ?? payroll.bonuses ?? 0)}</td>
                            <td className="py-2 px-2.5 text-right align-top text-amber-700">{formatCurrency(payroll.total_deductions ?? payroll.deductions ?? 0)}</td>
                            <td className="py-2 px-2.5 text-right align-top">
                              {formatCurrency(payroll.gross_salary)}
                            </td>
                            <td className="py-2 px-2.5 text-right align-top text-blue-600">
                              {formatCurrency(payroll.inss_employee)}
                            </td>
                            <td className="py-2 px-2.5 text-right align-top text-purple-600">
                              {formatCurrency(payroll.irps_retention)}
                            </td>
                            <td className="py-2 px-2.5 text-right align-top font-semibold text-green-600">
                              {formatCurrency(payroll.net_salary)}
                            </td>
                            <td className="py-2 px-2.5 text-center align-top">
                              <Badge className={cn('gap-1 text-[11px]', statusConfig.color)}>
                                <StatusIcon className="h-3 w-3" />
                                {statusConfig.label}
                              </Badge>
                            </td>
                            <td className="py-2 px-2 align-top">
                              <div className="flex justify-center">
                                <UniversalCrudActions
                                  entityLabel="payroll"
                                  entityId={payroll.id}
                                  canView
                                  canEdit={payroll.status === 'draft' && canManagePayroll}
                                  canDelete={payroll.status === 'draft' && canManagePayroll}
                                  canRecover={false}
                                  align="center"
                                  onView={() => {
                                    setSelectedPayroll(payroll);
                                    setViewDialogOpen(true);
                                  }}
                                  onDelete={() => handleDeleteAction(payroll)}
                                  onEdit={() => openPayrollEditDialog(payroll)}
                                  extraActions={(
                                    <div className="flex flex-wrap items-center justify-center gap-1 rounded-lg border border-slate-200 bg-white/90 p-0.5">
                                      {payroll.status !== 'draft' && (
                                        <Button 
                                          variant="outline" 
                                          size="icon"
                                          className="h-7 w-7 rounded-md"
                                          onClick={() => handleDownloadReceipt(payroll.id, getPayrollDisplayReference(payroll))}
                                          title="Descarregar Recibo"
                                          data-testid={`download-payroll-${payroll.id}`}
                                        >
                                          <Download className="h-4 w-4" />
                                        </Button>
                                      )}

                                      {payroll.status === 'draft' && canConfirm && (
                                        <Button 
                                          variant="outline" 
                                          size="icon"
                                          className="h-7 w-7 rounded-md border-blue-200 text-blue-700 hover:bg-blue-50"
                                          onClick={() => {
                                            setSelectedPayroll(payroll);
                                            setConfirmDialogOpen(true);
                                          }}
                                          data-testid={`confirm-payroll-${payroll.id}`}
                                          disabled={!payroll.irps_table_loaded}
                                          title={!payroll.irps_table_loaded ? 'Tabela IRPS não carregada' : 'Marcar como revista'}
                                        >
                                          <Check className="h-4 w-4" />
                                        </Button>
                                      )}

                                      {payroll.status === 'confirmed' && canMarkPaid && (
                                        <Button 
                                          variant="outline" 
                                          size="icon"
                                          className="h-7 w-7 rounded-md border-green-200 text-green-700 hover:bg-green-50"
                                          onClick={() => {
                                            setSelectedPayroll(payroll);
                                            setMarkPaidDialogOpen(true);
                                          }}
                                          data-testid={`mark-paid-${payroll.id}`}
                                          title="Marcar como processado"
                                        >
                                          <CreditCard className="h-4 w-4" />
                                        </Button>
                                      )}
                                    </div>
                                  )}
                                />
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                    <tfoot className="border-t bg-slate-50/95">
                      <tr>
                        <td className="px-3 py-2 text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-500" colSpan={4} data-testid="payroll-operational-totals-label">Totais gerais</td>
                        <td className="px-3 py-2 text-right text-sm font-semibold text-slate-700" data-testid="payroll-operational-totals-base">{formatCurrency(payrollOperationalSummary.totals.baseSalary)}</td>
                        <td className="px-3 py-2 text-right text-sm font-semibold text-emerald-700" data-testid="payroll-operational-totals-bonuses">{formatCurrency(payrollOperationalSummary.totals.bonuses)}</td>
                        <td className="px-3 py-2 text-right text-sm font-semibold text-amber-700" data-testid="payroll-operational-totals-deductions">{formatCurrency(payrollOperationalSummary.totals.deductions)}</td>
                        <td className="px-3 py-2 text-right text-sm font-semibold text-slate-700" data-testid="payroll-operational-totals-gross">{formatCurrency(payrollOperationalSummary.totals.grossSalary)}</td>
                        <td className="px-3 py-2 text-right text-sm font-semibold text-blue-700" data-testid="payroll-operational-totals-inss">{formatCurrency(payrollOperationalSummary.totals.inssEmployee)}</td>
                        <td className="px-3 py-2 text-right text-sm font-semibold text-purple-700" data-testid="payroll-operational-totals-irps">{formatCurrency(payrollOperationalSummary.totals.irps)}</td>
                        <td className="px-3 py-2 text-right text-sm font-semibold text-green-700" data-testid="payroll-operational-totals-net">{formatCurrency(payrollOperationalSummary.totals.netSalary)}</td>
                        <td className="px-3 py-2"></td>
                        <td className="px-3 py-2"></td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Employees Tab */}
        <PayrollEmployeesTabContent
          employeeDirectory={employeeDirectory}
          activeEmployeeFiltersCount={activeEmployeeFiltersCount}
          employeeSearchQuery={employeeSearchQuery}
          onEmployeeSearchChange={setEmployeeSearchQuery}
          employeeStatusFilter={employeeStatusFilter}
          onEmployeeStatusChange={setEmployeeStatusFilter}
          employeeSortField={employeeSortField}
          onEmployeeSortFieldChange={setEmployeeSortField}
          employeeSortDirection={employeeSortDirection}
          onEmployeeSortDirectionChange={setEmployeeSortDirection}
          employeeSortOptions={EMPLOYEE_SORT_OPTIONS}
          onClearFilters={() => {
            setEmployeeSearchQuery('');
            setEmployeeStatusFilter('all');
          }}
          sortedEmployees={sortedEmployees}
          selectedEmployeeIds={selectedEmployeeIds}
          allVisibleEmployeesSelected={allVisibleEmployeesSelected}
          canManagePayroll={canManagePayroll}
          openPayrollEmployeeDialog={openPayrollEmployeeDialog}
          toggleAllVisibleEmployees={toggleAllVisibleEmployees}
          handleBulkProcessEmployees={handleBulkProcessEmployees}
          toggleEmployeeSelection={toggleEmployeeSelection}
          openDossierDialog={openDossierDialog}
        />

        <TabsContent value="vacations" className="space-y-4">
          <PayrollVacationsTabContent
            canManagePayroll={canManagePayroll}
            vacationYear={vacationYear}
            setVacationYear={setVacationYear}
            employees={sortedEmployees.filter((employee) => employee.id)}
            vacationsLoading={vacationsLoading}
            vacationsDashboard={vacationsDashboard}
            vacationRequestForm={vacationRequestForm}
            setVacationRequestForm={setVacationRequestForm}
            onCreateVacationRequest={handleCreateVacationRequest}
            vacationPolicyForm={vacationPolicyForm}
            setVacationPolicyForm={setVacationPolicyForm}
            onSaveVacationPolicy={handleSaveVacationPolicy}
            selectedVacationEmployeeNumber={selectedVacationEmployeeNumber}
            setSelectedVacationEmployeeNumber={setSelectedVacationEmployeeNumber}
            onUpdateVacationRequestStatus={handleUpdateVacationRequestStatus}
            onDownloadVacationReport={handleDownloadVacationReport}
          />
        </TabsContent>

        <TabsContent value="attendance" className="space-y-4">
          <PayrollAttendanceOperationsSection
            attendanceLoading={attendanceLoading}
            attendanceReport={attendanceReport}
            attendanceRecords={attendanceRecords}
            attendanceClosures={attendanceClosures}
            activeAttendanceFiltersCount={activeAttendanceFiltersCount}
            attendanceSearchQuery={attendanceSearchQuery}
            setAttendanceSearchQuery={setAttendanceSearchQuery}
            attendanceSortField={attendanceSortField}
            setAttendanceSortField={setAttendanceSortField}
            attendanceSortDirection={attendanceSortDirection}
            setAttendanceSortDirection={setAttendanceSortDirection}
            attendanceSortOptions={ATTENDANCE_SORT_OPTIONS}
            attendanceBootstrapStatus={attendanceBootstrapStatus}
            attendanceResetIncludesShifts={attendanceResetIncludesShifts}
            setAttendanceResetIncludesShifts={setAttendanceResetIncludesShifts}
            loadAttendanceData={loadAttendanceData}
            handleResetAttendanceOperationalData={handleResetAttendanceOperationalData}
            submitting={submitting}
            manualAttendanceForm={manualAttendanceForm}
            setManualAttendanceForm={setManualAttendanceForm}
            sortedEmployees={sortedEmployees}
            handleSaveManualAttendance={handleSaveManualAttendance}
          />

          <PayrollAttendanceManagementSection
            attendanceReport={attendanceReport}
            sortedAttendanceClosures={sortedAttendanceClosures}
            handleCloseAttendanceMonth={handleCloseAttendanceMonth}
            isManagerReviewer={isManagerReviewer}
            handleReviewAttendanceClose={handleReviewAttendanceClose}
            shiftForm={shiftForm}
            setShiftForm={setShiftForm}
            locations={locations}
            handleSaveShift={handleSaveShift}
            sortedAttendanceShifts={sortedAttendanceShifts}
            handleDeleteShift={handleDeleteShift}
            setAttendanceFile={setAttendanceFile}
            handleExportAttendanceXlsx={handleExportAttendanceXlsx}
            handleDownloadAttendanceTemplateXlsx={handleDownloadAttendanceTemplateXlsx}
            handleAttendanceImport={handleAttendanceImport}
            attendanceFile={attendanceFile}
            attendanceUploading={attendanceUploading}
            sortedAttendanceEmployeeReport={sortedAttendanceEmployeeReport}
            sortedAttendanceStoreReport={sortedAttendanceStoreReport}
          />

          <Card data-testid="payroll-attendance-records-card">
            <CardHeader>
              <CardTitle>Horas de trabalho importadas</CardTitle>
              <CardDescription>Monitorização de horas com base no número de colaborador.</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid gap-4 lg:grid-cols-2 mb-6">
                <div className="rounded-2xl border border-slate-200 bg-white p-4" data-testid="attendance-weekly-map-card">
                  <p className="font-medium text-slate-900">Mapa semanal</p>
                  <p className="text-xs text-slate-500">{attendanceWeeklyMap.week_start || '—'} até {attendanceWeeklyMap.week_end || '—'}</p>
                  <div className="mt-4 space-y-2">
                    {sortedAttendanceWeeklyRows.slice(0, 6).map((row) => (
                      <div key={`${row.employee_number}-${row.week_start || 'week'}`} className="rounded-xl border border-slate-200 bg-slate-50 p-3 text-sm">
                        <div className="flex items-center justify-between gap-2">
                          <p className="font-semibold text-slate-900">{row.employee_number} · {row.employee_name}</p>
                          <Badge variant="outline">Banco {row.bank_hours || 0} h</Badge>
                        </div>
                        <div className="mt-2 flex flex-wrap gap-1">
                          {row.days.map((day) => (
                            <Badge key={`${row.employee_number}-${day.date}`} variant="outline">{day.date.slice(8)} · {day.status}</Badge>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="rounded-2xl border border-slate-200 bg-white p-4" data-testid="attendance-justified-absence-card">
                  <p className="font-medium text-slate-900">Faltas justificadas</p>
                  <div className="mt-4 grid gap-3 md:grid-cols-2">
                    <Input value={absenceForm.employee_number} onChange={(event) => setAbsenceForm((current) => ({ ...current, employee_number: event.target.value }))} placeholder="Nº colaborador" data-testid="justified-absence-employee-number" />
                    <Input type="date" value={absenceForm.date} onChange={(event) => setAbsenceForm((current) => ({ ...current, date: event.target.value }))} data-testid="justified-absence-date" />
                    <Input value={absenceForm.reason} onChange={(event) => setAbsenceForm((current) => ({ ...current, reason: event.target.value }))} placeholder="Motivo" data-testid="justified-absence-reason" />
                    <div className="flex items-center justify-between rounded-xl border border-slate-200 px-3 py-2 text-sm">
                      <span>Remunerada</span>
                      <Switch checked={absenceForm.is_paid} onCheckedChange={(checked) => setAbsenceForm((current) => ({ ...current, is_paid: checked }))} data-testid="justified-absence-paid-switch" />
                    </div>
                  </div>
                  <Button size="sm" className="mt-3 h-8 rounded-lg px-2 text-xs" onClick={handleSaveJustifiedAbsence} data-testid="justified-absence-save-button">Guardar falta justificada</Button>
                  <div className="mt-4 space-y-2">
                    {sortedJustifiedAbsences.slice(0, 8).map((item) => (
                      <div key={item.id} className="rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-sm" data-testid={`justified-absence-row-${item.id}`}>
                        <p className="font-semibold text-slate-900">{item.employee_number} · {item.date}</p>
                        <p className="text-xs text-slate-500">{item.reason}</p>
                        <div className="mt-2 flex gap-1.5">
                          <Button variant="outline" size="sm" className="h-7 rounded-lg px-2 text-xs" onClick={() => setAbsenceForm({ id: item.id, employee_number: item.employee_number, date: item.date, reason: item.reason, is_paid: item.is_paid })} data-testid={`justified-absence-edit-${item.id}`}>Editar</Button>
                          <Button variant="outline" size="sm" className="h-7 rounded-lg px-2 text-xs text-red-600" onClick={() => handleDeleteJustifiedAbsence(item.id)} data-testid={`justified-absence-delete-${item.id}`}>Apagar</Button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {attendanceEditForm.id && (
                <div className="mb-4 rounded-2xl border border-slate-200 bg-slate-50 p-4" data-testid="attendance-record-edit-card">
                  <div className="flex items-center justify-between gap-2">
                    <div>
                      <p className="font-semibold text-slate-900">Editar registo • {attendanceEditForm.employee_number} · {attendanceEditForm.employee_name}</p>
                    </div>
                    <div className="flex gap-1.5">
                      <Button variant="outline" size="sm" className="h-7 rounded-lg px-2 text-xs" onClick={() => setAttendanceEditForm({ id: null, employee_number: '', employee_name: '', work_start: '', lunch_start: '', lunch_end: '', work_end: '' })}>Cancelar</Button>
                      <Button size="sm" className="h-7 rounded-lg px-2 text-xs" onClick={handleSaveAttendanceRecord} data-testid="attendance-record-save-button">Guardar</Button>
                    </div>
                  </div>
                  <div className="mt-3 grid gap-3 md:grid-cols-4">
                    <Input type="time" value={attendanceEditForm.work_start} onChange={(event) => setAttendanceEditForm((current) => ({ ...current, work_start: event.target.value }))} data-testid="attendance-record-work-start" />
                    <Input type="time" value={attendanceEditForm.lunch_start} onChange={(event) => setAttendanceEditForm((current) => ({ ...current, lunch_start: event.target.value }))} data-testid="attendance-record-lunch-start" />
                    <Input type="time" value={attendanceEditForm.lunch_end} onChange={(event) => setAttendanceEditForm((current) => ({ ...current, lunch_end: event.target.value }))} data-testid="attendance-record-lunch-end" />
                    <Input type="time" value={attendanceEditForm.work_end} onChange={(event) => setAttendanceEditForm((current) => ({ ...current, work_end: event.target.value }))} data-testid="attendance-record-work-end" />
                  </div>
                </div>
              )}

              {sortedAttendanceRecords.length === 0 ? (
                <div className="py-8 text-center text-sm text-muted-foreground" data-testid="attendance-empty-state">Sem registos de assiduidade para o período seleccionado.</div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b text-left text-xs uppercase tracking-wide text-muted-foreground">
                        <th className="py-2 pr-3">Nº Colaborador</th>
                        <th className="py-2 pr-3">Funcionário</th>
                        <th className="py-2 pr-3">Loja</th>
                        <th className="py-2 pr-3">Data</th>
                        <th className="py-2 pr-3">Entrada</th>
                        <th className="py-2 pr-3">Saída almoço</th>
                        <th className="py-2 pr-3">Entrada almoço</th>
                        <th className="py-2 pr-3">Saída casa</th>
                        <th className="py-2 pr-3">Horas</th>
                        <th className="py-2 pr-3">Comparação</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {sortedAttendanceRecords.map((record) => (
                        <tr key={record.id} data-testid={`attendance-row-${record.id}`}>
                          <td className="py-2 pr-3 font-mono font-semibold">{record.employee_number}</td>
                          <td className="py-2 pr-3">{record.employee_name}</td>
                          <td className="py-2 pr-3">{record.store_location || '—'}</td>
                          <td className="py-2 pr-3">{record.date}</td>
                          <td className="py-2 pr-3">{record.work_start || '—'}</td>
                          <td className="py-2 pr-3">{record.lunch_start || '—'}</td>
                          <td className="py-2 pr-3">{record.lunch_end || '—'}</td>
                          <td className="py-2 pr-3">{record.work_end || '—'}</td>
                          <td className="py-2 pr-3">{record.worked_hours || 0} h</td>
                          <td className="py-2 pr-3">
                            <Badge variant="outline">
                              {record.comparison?.status || 'sem_turno'}
                              {record.comparison?.late_minutes ? ` • +${record.comparison.late_minutes}m` : ''}
                            </Badge>
                          </td>
                          <td className="py-2 pr-3">
                            <div className="flex gap-1.5">
                              <Button variant="outline" size="sm" className="h-7 rounded-lg px-2 text-xs" onClick={() => setAttendanceEditForm({ id: record.id, employee_number: record.employee_number, employee_name: record.employee_name, work_start: record.work_start || '', lunch_start: record.lunch_start || '', lunch_end: record.lunch_end || '', work_end: record.work_end || '' })} data-testid={`attendance-record-edit-${record.id}`}>Editar</Button>
                              <Button variant="outline" size="sm" className="h-7 rounded-lg px-2 text-xs text-red-600" onClick={() => handleDeleteAttendanceRecord(record.id)} data-testid={`attendance-record-delete-${record.id}`}>Apagar</Button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="reports" className="space-y-4">
          <Card className="overflow-hidden rounded-xl border-slate-200 shadow-sm" data-testid="payroll-reports-toolbar-card">
            <CardContent className="space-y-2 p-3">
              <div className="flex flex-wrap items-center gap-2 text-xs" data-testid="payroll-reports-kpi-strip">
                <Badge variant="outline" className="rounded-full bg-white">Folhas: {salarySheetPreview.total_rows || 0}</Badge>
                <Badge variant="outline" className="rounded-full bg-slate-100 text-slate-700">Geradas: {salarySheetPreview.processing?.status_counts?.generated || 0}</Badge>
                <Badge variant="outline" className="rounded-full bg-blue-50 text-blue-700">Revistas: {salarySheetPreview.processing?.status_counts?.reviewed || 0}</Badge>
                <Badge variant="outline" className="rounded-full bg-emerald-50 text-emerald-700">Processadas: {salarySheetPreview.processing?.status_counts?.processed || 0}</Badge>
                <Badge variant="outline" className="rounded-full bg-white">Nominal: {nominalPreview.total_rows || 0}</Badge>
                <Badge variant="outline" className="rounded-full bg-white">Assiduidade: {attendanceDocumentPreview.summary?.total_records || 0}</Badge>
                <Badge variant="outline" className="rounded-full bg-white" data-testid="payroll-role-alignment-badge">Colaboradores alinhados: {roleAlignmentStats.employees_aligned || 0}</Badge>
                <Badge variant="outline" className="rounded-full bg-white" data-testid="payroll-role-divergence-badge">Divergências encontradas: {roleAlignmentStats.divergences_found || 0}</Badge>
                <Badge variant="outline" className={cn('rounded-full', attendanceBootstrapStatus.is_empty_operational_data ? 'bg-green-50 text-green-700' : 'bg-amber-50 text-amber-700')}>
                  {attendanceBootstrapStatus.is_empty_operational_data ? 'Base pronta para arranque limpo' : 'Base com histórico operacional'}
                </Badge>
              </div>
              <div className="rounded-xl border border-slate-200 bg-white/90 p-2.5 text-sm text-slate-700 shadow-sm" data-testid="payroll-role-alignment-summary-card">
                <div className="flex flex-wrap items-center gap-1.5 text-xs">
                  <Badge variant="outline">Perfil = cargo oficial</Badge>
                  <Badge variant="outline">Registos payroll alinhados: {roleAlignmentStats.payroll_records_aligned || 0}</Badge>
                  <Badge variant="outline">Excepções: {roleAlignmentStats.exceptions_found || 0}</Badge>
                  <span className="text-slate-500">Backfill automático activo.</span>
                </div>
              </div>
              <div className="flex flex-wrap items-center gap-2 rounded-xl border border-slate-200 bg-white/90 p-2 shadow-sm" data-testid="payroll-reports-controls">
                <Select value={String(periodMonth)} onValueChange={(value) => setPeriodMonth(parseInt(value, 10))}>
                  <SelectTrigger className="h-8 w-[146px] rounded-lg border-slate-200 bg-slate-50 text-sm" data-testid="payroll-reports-month-select">
                    <SelectValue placeholder="Mês" />
                  </SelectTrigger>
                  <SelectContent>
                    {MONTHS.map((month) => (
                      <SelectItem key={month.value} value={String(month.value)}>{month.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                <Select value={String(periodYear)} onValueChange={(value) => setPeriodYear(parseInt(value, 10))}>
                  <SelectTrigger className="h-8 w-[108px] rounded-lg border-slate-200 bg-slate-50 text-sm" data-testid="payroll-reports-year-select">
                    <SelectValue placeholder="Ano" />
                  </SelectTrigger>
                  <SelectContent>
                    {[2024, 2025, 2026].map((yearOption) => (
                      <SelectItem key={yearOption} value={String(yearOption)}>{yearOption}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                <Select value={reportScope} onValueChange={setReportScope}>
                  <SelectTrigger className="h-8 w-[126px] rounded-lg border-slate-200 bg-slate-50 text-sm" data-testid="payroll-reports-scope-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="company">Empresa</SelectItem>
                    <SelectItem value="store">Por loja</SelectItem>
                  </SelectContent>
                </Select>

                <Select value={reportStoreLocation} onValueChange={setReportStoreLocation} disabled={reportScope !== 'store'}>
                  <SelectTrigger className="h-8 w-[206px] rounded-lg border-slate-200 bg-slate-50 text-sm" data-testid="payroll-reports-store-select">
                    <SelectValue placeholder="Loja / unidade" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todas</SelectItem>
                    {locations.map((location) => (
                      <SelectItem key={location.id || location.name || location.location} value={location.name || location.location}>{location.name || location.location}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                <Select value={reportEmployeeNumber} onValueChange={setReportEmployeeNumber}>
                  <SelectTrigger className="h-8 w-[206px] rounded-lg border-slate-200 bg-slate-50 text-sm" data-testid="payroll-reports-employee-select">
                    <SelectValue placeholder="Mapa individual" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Resumo geral</SelectItem>
                    {sortedEmployees.filter((employee) => employee.employee_number).map((employee) => (
                      <SelectItem key={employee.employee_number} value={employee.employee_number}>{employee.employee_number} · {employee.full_name || employee.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                <Button variant="outline" size="sm" className="h-8 rounded-lg px-2 text-xs" onClick={loadDocumentsData} data-testid="payroll-reports-refresh-button">
                  <RefreshCw className="h-4 w-4" />
                </Button>
              </div>
            </CardContent>
          </Card>

          <Tabs value={reportsSubTab} onValueChange={setReportsSubTab} className="space-y-4" data-testid="payroll-reports-subtabs-root">
            <TabsList className="flex h-auto flex-wrap gap-1.5 rounded-xl border border-slate-200 bg-white/90 p-1 shadow-sm" data-testid="payroll-reports-subtabs-list">
              <TabsTrigger value="documents" className="h-8 rounded-lg px-3 text-xs" data-testid="payroll-reports-subtab-documents">
                Documentos
              </TabsTrigger>
              <TabsTrigger value="templates" className="h-8 rounded-lg px-3 text-xs" data-testid="payroll-reports-subtab-templates">
                Configuração de Templates
              </TabsTrigger>
            </TabsList>

            <TabsContent value="templates" className="space-y-4">
              <PayrollTemplateConfigPanel
                documents={templateDocuments}
                selectedDocument={selectedTemplateDocument}
                currentSettings={currentTemplateSettings}
                loading={templateSettingsLoading}
                saving={templateSettingsSaving}
                previewing={templatePreviewLoading}
                exporting={templateExcelLoading}
                onSelectDocument={setSelectedTemplateDocument}
                presetValue={selectedTemplatePreset}
                onPresetChange={setSelectedTemplatePreset}
                onApplyPreset={applyTemplatePresetToAllDocuments}
                onFieldChange={updateTemplateSettingField}
                onUpdateSection={updateTemplateSection}
                onMoveSection={moveTemplateSection}
                onReplaceSections={replaceTemplateSections}
                onUpdateColumn={updateTemplateColumn}
                onMoveColumn={moveTemplateColumn}
                onReplaceColumns={replaceTemplateColumns}
                onApplyJson={applyTemplateJsonDraft}
                onSave={handleSaveTemplateSettings}
                onPreviewPdf={handlePreviewTemplatePdf}
                onExportTestXlsx={handleExportTemplateTestXlsx}
              />
            </TabsContent>

            <TabsContent value="documents" className="space-y-4">
              <PayrollStateObligationsCard
                summary={reportsOperationalSummary}
                title="Obrigações RH reflectidas nos documentos"
                description="Os valores abaixo ficam alinhados com a folha salarial, recibo e exportações do período."
              />

              <div className="grid gap-4 xl:grid-cols-[1.05fr_1fr]">
            <Card className="rounded-2xl border-slate-200 shadow-sm" data-testid="payroll-receipt-document-card">
              <CardHeader className="space-y-1 pb-3">
                <CardTitle className="flex items-center gap-2 text-base"><Receipt className="h-4 w-4" /> Recibo original / duplicado</CardTitle>
                <CardDescription>A4 vertical com cabeçalho corporativo, via do trabalhador e via da empresa.</CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="space-y-1.5">
                  <Label className="text-xs">Folha a usar no recibo</Label>
                  <Select value={documentPayrollId} onValueChange={setDocumentPayrollId}>
                    <SelectTrigger data-testid="payroll-receipt-select">
                      <SelectValue placeholder="Seleccionar folha" />
                    </SelectTrigger>
                    <SelectContent>
                      {sortedPayrolls.map((payroll) => (
                        <SelectItem key={payroll.id} value={payroll.id}>{getPayrollDisplayReference(payroll)} · {payroll.employee_name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="rounded-2xl border border-slate-200 bg-slate-50 p-3" data-testid="payroll-receipt-preview-card">
                  <div className="flex flex-wrap items-center gap-2 text-xs">
                    <Badge className="bg-slate-900 text-white" data-testid="payroll-receipt-template-badge">Template ISO A4 oficial</Badge>
                    <Badge variant="outline">Original + duplicado</Badge>
                    <Badge variant="outline">Período {periodMonth}/{periodYear}</Badge>
                    <Badge variant="outline" data-testid="payroll-receipt-logo-badge">Logo oficial CG Business</Badge>
                  </div>
                  {selectedDocumentPayroll && selectedDocumentPayrollPreview ? (
                    <div className="mt-3 space-y-3">
                      <div className="grid gap-3 md:grid-cols-2">
                        <div>
                          <p className="text-xs text-slate-500">Trabalhador</p>
                          <p className="text-sm font-semibold text-slate-900">{selectedDocumentPayroll.employee_name}</p>
                          <p className="text-xs text-slate-500">{selectedDocumentPayrollPreview.employeeNumber}</p>
                          <p className="mt-1 text-xs text-slate-500">Cargo / perfil: {selectedDocumentPayrollPreview.role}</p>
                        </div>
                        <div>
                          <p className="text-xs text-slate-500">Valores principais</p>
                          <p className="text-sm text-slate-700">Vencimento Base: {formatCurrency(selectedDocumentPayrollPreview.baseSalary)}</p>
                          <p className="text-sm text-slate-700">Dias de Trabalho: {selectedDocumentPayrollPreview.daysWorked}</p>
                          <p className="text-sm text-slate-700">Diária: {formatCurrency(selectedDocumentPayrollPreview.dailyRate)}</p>
                          <p className="text-sm text-slate-700">Bruto: {formatCurrency(selectedDocumentPayrollPreview.grossSalary)}</p>
                          <p className="text-sm text-slate-700">Descontos: {formatCurrency(selectedDocumentPayrollPreview.deductions)}</p>
                        </div>
                      </div>

                      <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white" data-testid="payroll-receipt-grid-preview">
                        <div className="grid grid-cols-[1.35fr_1fr_1fr] border-b bg-slate-100 text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-600">
                          <div className="px-3 py-2">Descrição</div>
                          <div className="px-3 py-2 text-right">Proventos</div>
                          <div className="px-3 py-2 text-right">Descontos</div>
                        </div>
                        {[
                          ['Vencimento Base', formatCurrency(selectedDocumentPayrollPreview.baseSalary), '—'],
                          ['Dias de Trabalho', String(selectedDocumentPayrollPreview.daysWorked), '—'],
                          ['Diária', formatCurrency(selectedDocumentPayrollPreview.dailyRate), '—'],
                          ['Bónus e Subsídios', formatCurrency(selectedDocumentPayrollPreview.bonuses), '—'],
                          ['Descontos totais', '—', formatCurrency(selectedDocumentPayrollPreview.deductions)],
                        ].map(([label, earnings, deductions]) => (
                          <div key={label} className="grid grid-cols-[1.35fr_1fr_1fr] border-b text-sm last:border-b-0">
                            <div className="px-3 py-2 font-medium text-slate-800">{label}</div>
                            <div className="px-3 py-2 text-right text-slate-700">{earnings}</div>
                            <div className="px-3 py-2 text-right text-slate-700">{deductions}</div>
                          </div>
                        ))}
                        <div className="grid grid-cols-[1.35fr_1fr_1fr] bg-slate-200 text-sm font-semibold text-slate-900" data-testid="payroll-net-receipt-merged-row">
                          <div className="px-3 py-2">LÍQUIDO A RECEBER</div>
                          <div className="col-span-2 px-3 py-2 text-center">{formatCurrency(selectedDocumentPayrollPreview.netSalary)}</div>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="mt-3 rounded-2xl border border-dashed border-slate-300 bg-white px-4 py-6 text-sm text-slate-500" data-testid="payroll-receipt-empty-state">Sem folhas disponíveis para o período seleccionado.</div>
                  )}
                </div>
                <div className="flex flex-wrap gap-2">
                  <Button size="sm" className="h-8 rounded-lg px-2 text-xs" onClick={() => selectedDocumentPayroll && handleDownloadReceipt(selectedDocumentPayroll.id, getPayrollDisplayReference(selectedDocumentPayroll))} disabled={!selectedDocumentPayroll} data-testid="payroll-receipt-open-pdf-button">
                    PDF
                  </Button>
                </div>
              </CardContent>
            </Card>

            <Card className="rounded-[28px] border-slate-200 bg-white/90 shadow-sm" data-testid="payroll-salary-sheet-card">
              <CardHeader className="space-y-1 pb-3">
                <CardTitle className="flex items-center gap-2 text-base"><Wallet className="h-4 w-4" /> Folha de salários</CardTitle>
                <CardDescription>Template dedicado do Payroll, em horizontal, com totalizadores e branding oficial CG Business.</CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex flex-wrap gap-2 text-xs">
                  <Badge variant="outline">Base total: {formatCurrency(salarySheetPreview.totals?.base_salary || 0)}</Badge>
                  <Badge variant="outline">Bruto: {formatCurrency(salarySheetPreview.totals?.gross_salary || 0)}</Badge>
                  <Badge variant="outline">Bónus: {formatCurrency(salarySheetPreview.totals?.bonuses || 0)}</Badge>
                  <Badge variant="outline">Descontos: {formatCurrency(salarySheetPreview.totals?.deductions || 0)}</Badge>
                  <Badge variant="outline">Líquido: {formatCurrency(salarySheetPreview.totals?.net_salary || 0)}</Badge>
                  <Badge variant="outline" className="bg-blue-50 text-blue-700">INSS Estado: {formatCurrency(salarySheetPreview.state_obligations?.inss_total_state || 0)}</Badge>
                  <Badge variant="outline" className="bg-purple-50 text-purple-700">IRPS Estado: {formatCurrency(salarySheetPreview.state_obligations?.irps_total_state || 0)}</Badge>
                </div>
                <div className="max-h-72 overflow-auto rounded-2xl border border-slate-200">
                  <table className="w-full text-sm">
                    <thead className="sticky top-0 bg-slate-50">
                      <tr>
                        <th className="px-3 py-2 text-left">Loja</th>
                        <th className="px-3 py-2 text-left">Trabalhador</th>
                        <th className="px-3 py-2 text-left">Cargo / Perfil</th>
                        <th className="px-3 py-2 text-right">Bónus</th>
                        <th className="px-3 py-2 text-right">Descontos</th>
                        <th className="px-3 py-2 text-right">Base</th>
                        <th className="px-3 py-2 text-right">Bruto</th>
                        <th className="px-3 py-2 text-right">Líquido</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {salarySheetPreview.groups?.flatMap((group) => group.rows.slice(0, 4).map((row) => ({ ...row, __group: group.store_location }))).map((row) => (
                        <tr key={row.id} data-testid={`salary-sheet-preview-row-${row.id}`}>
                          <td className="px-3 py-2">{row.__group}</td>
                          <td className="px-3 py-2">{row.employee_name}</td>
                          <td className="px-3 py-2" data-testid={`salary-sheet-preview-role-${row.id}`}>{row.role || '—'}</td>
                          <td className="px-3 py-2 text-right">{formatCurrency(row.bonuses || 0)}</td>
                          <td className="px-3 py-2 text-right">{formatCurrency(row.deductions || 0)}</td>
                          <td className="px-3 py-2 text-right">{formatCurrency(row.base_salary || 0)}</td>
                          <td className="px-3 py-2 text-right">{formatCurrency(row.gross_salary || 0)}</td>
                          <td className="px-3 py-2 text-right">{formatCurrency(row.net_salary || 0)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div className="flex flex-wrap gap-2">
                  <Button variant="outline" size="sm" className="h-8 rounded-lg px-2 text-xs" onClick={handleDownloadSalarySheetPdf} data-testid="salary-sheet-download-pdf-button">PDF</Button>
                  <Button variant="outline" size="sm" className="h-8 rounded-lg px-2 text-xs" onClick={handleDownloadSalarySheetXlsx} data-testid="salary-sheet-download-xlsx-button">Excel</Button>
                </div>
              </CardContent>
            </Card>
              </div>

              <div className="grid gap-4 xl:grid-cols-[1fr_1fr]">
            <Card className="rounded-2xl border-slate-200 bg-white/90 shadow-sm" data-testid="payroll-nominal-card">
              <CardHeader className="space-y-1 pb-2">
                <CardTitle className="flex items-center gap-2 text-base"><Users className="h-4 w-4" /> Relação nominal</CardTitle>
                <CardDescription>Lista administrativa dos trabalhadores por loja ou geral.</CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="max-h-72 overflow-auto rounded-xl border border-slate-200">
                  <table className="w-full text-sm">
                    <thead className="sticky top-0 bg-slate-50">
                      <tr>
                        <th className="px-3 py-2 text-left">Nº</th>
                        <th className="px-3 py-2 text-left">Trabalhador</th>
                        <th className="px-3 py-2 text-left">Cargo / Perfil</th>
                        <th className="px-3 py-2 text-left">Loja</th>
                        <th className="px-3 py-2 text-left">Dossier</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {nominalPreview.groups?.flatMap((group) => group.rows.slice(0, 5)).map((row) => (
                        <tr key={row.employee_number} data-testid={`nominal-preview-row-${row.employee_number}`}>
                          <td className="px-3 py-2">{row.employee_number}</td>
                          <td className="px-3 py-2">{row.employee_name}</td>
                          <td className="px-3 py-2">{row.role || '—'}</td>
                          <td className="px-3 py-2">{row.store_location}</td>
                          <td className="px-3 py-2">{row.dossier_complete ? 'Completo' : `${row.completion_percentage}%`}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div className="flex flex-wrap gap-2">
                  <Button variant="outline" size="sm" className="h-8 rounded-lg px-2 text-xs" onClick={handleDownloadNominalPdf} data-testid="nominal-download-pdf-button">PDF</Button>
                  <Button variant="outline" size="sm" className="h-8 rounded-lg px-2 text-xs" onClick={handleDownloadNominalXlsx} data-testid="nominal-download-xlsx-button">Excel</Button>
                </div>
              </CardContent>
            </Card>

            <Card className="rounded-2xl border-slate-200 shadow-sm" data-testid="payroll-attendance-report-card">
              <CardHeader className="space-y-1 pb-2">
                <CardTitle className="flex items-center gap-2 text-base"><Calendar className="h-4 w-4" /> Mapa de assiduidade</CardTitle>
                <CardDescription>Resumo por período com visão individual quando seleccionar um trabalhador.</CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex flex-wrap gap-2 text-xs">
                  <Badge variant="outline">Registos: {attendanceDocumentPreview.summary?.total_records || 0}</Badge>
                  <Badge variant="outline">Horas: {attendanceDocumentPreview.summary?.total_hours || 0}</Badge>
                  <Badge variant="outline">Faltas: {attendanceDocumentPreview.summary?.unjustified_absences || 0}</Badge>
                </div>
                {documentsLoading ? (
                  <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-6 text-sm text-slate-500">A carregar documentos...</div>
                ) : attendanceDocumentPreview.days?.length > 0 ? (
                  <div className="max-h-72 overflow-auto rounded-2xl border border-slate-200">
                    <table className="w-full text-sm">
                      <thead className="sticky top-0 bg-slate-50">
                        <tr>
                          <th className="px-3 py-2 text-left">Data</th>
                          <th className="px-3 py-2 text-left">Estado</th>
                          <th className="px-3 py-2 text-right">Horas</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y">
                        {attendanceDocumentPreview.days.slice(0, 12).map((day) => (
                          <tr key={day.date} data-testid={`attendance-document-day-${day.date}`}>
                            <td className="px-3 py-2">{day.date}</td>
                            <td className="px-3 py-2">{day.status}</td>
                            <td className="px-3 py-2 text-right">{day.hours || 0}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="max-h-72 overflow-auto rounded-2xl border border-slate-200">
                    <table className="w-full text-sm">
                      <thead className="sticky top-0 bg-slate-50">
                        <tr>
                          <th className="px-3 py-2 text-left">Nº</th>
                          <th className="px-3 py-2 text-left">Trabalhador</th>
                          <th className="px-3 py-2 text-left">Loja</th>
                          <th className="px-3 py-2 text-right">Horas</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y">
                        {attendanceDocumentPreview.by_employee?.slice(0, 8).map((row) => (
                          <tr key={row.employee_number} data-testid={`attendance-document-row-${row.employee_number}`}>
                            <td className="px-3 py-2">{row.employee_number}</td>
                            <td className="px-3 py-2">{row.employee_name}</td>
                            <td className="px-3 py-2">{row.store_location || '—'}</td>
                            <td className="px-3 py-2 text-right">{row.total_hours || 0}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
                <div className="flex flex-wrap gap-2">
                  <Button variant="outline" size="sm" className="h-8 rounded-lg px-2 text-xs" onClick={handleDownloadAttendanceDocumentPdf} data-testid="attendance-document-download-pdf-button">PDF</Button>
                  <Button variant="outline" size="sm" className="h-8 rounded-lg px-2 text-xs" onClick={handleDownloadAttendanceDocumentXlsx} data-testid="attendance-document-download-xlsx-button">Excel</Button>
                </div>
              </CardContent>
            </Card>

            <Card className="rounded-2xl border-slate-200 shadow-sm xl:col-span-2" data-testid="payroll-vacations-documents-card">
              <CardHeader className="space-y-1 pb-2">
                <CardTitle className="flex items-center gap-2 text-base"><Calendar className="h-4 w-4" /> Documentos RH — Férias</CardTitle>
                <CardDescription>Relatórios organizados para pedidos, plano individual, plano geral e saldo por colaborador.</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex flex-wrap gap-2 text-xs">
                  <Badge variant="outline">Ano férias: {vacationYear}</Badge>
                  <Badge variant="outline" data-testid="vacations-active-regime-badge">Regime activo: {vacationsDashboard.policy?.active_regime?.label || '—'}</Badge>
                  <Badge variant="outline">Pedidos: {vacationsDashboard.summary?.requests_total || 0}</Badge>
                  <Badge variant="outline">Pendentes: {vacationsDashboard.summary?.requests_pending || 0}</Badge>
                  <Badge variant="outline">Saldo agregado: {vacationsDashboard.summary?.balance_days || 0}</Badge>
                </div>
                <div className="grid gap-4 xl:grid-cols-2">
                  <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4" data-testid="vacations-report-requests-card">
                    <p className="font-semibold text-slate-900">Relatório de pedidos de férias</p>
                    <p className="mt-1 text-xs text-slate-500">Fluxo pendente → aprovado → rejeitado dentro do circuito documental actual.</p>
                    <div className="mt-3 flex flex-wrap gap-2">
                      <Button variant="outline" size="sm" className="h-8 rounded-lg px-2 text-xs" onClick={() => handleDownloadVacationReport('requests', 'pdf')} data-testid="vacations-doc-requests-pdf-button">PDF</Button>
                      <Button variant="outline" size="sm" className="h-8 rounded-lg px-2 text-xs" onClick={() => handleDownloadVacationReport('requests', 'xlsx')} data-testid="vacations-doc-requests-xlsx-button">Excel</Button>
                    </div>
                  </div>
                  <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4" data-testid="vacations-report-individual-card">
                    <p className="font-semibold text-slate-900">Plano individual por colaborador</p>
                    <p className="mt-1 text-xs text-slate-500">Usa o filtro de colaborador da aba Férias para exportar o plano individual.</p>
                    <div className="mt-3 flex flex-wrap gap-2">
                      <Button variant="outline" size="sm" className="h-8 rounded-lg px-2 text-xs" onClick={() => handleDownloadVacationReport('plan-individual', 'pdf')} disabled={selectedVacationEmployeeNumber === 'all'} data-testid="vacations-doc-individual-pdf-button">PDF</Button>
                      <Button variant="outline" size="sm" className="h-8 rounded-lg px-2 text-xs" onClick={() => handleDownloadVacationReport('plan-individual', 'xlsx')} disabled={selectedVacationEmployeeNumber === 'all'} data-testid="vacations-doc-individual-xlsx-button">Excel</Button>
                    </div>
                  </div>
                  <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4" data-testid="vacations-report-general-card">
                    <p className="font-semibold text-slate-900">Plano geral de férias</p>
                    <p className="mt-1 text-xs text-slate-500">Mapa consolidado para controlo interno e fiscalização laboral.</p>
                    <div className="mt-3 flex flex-wrap gap-2">
                      <Button variant="outline" size="sm" className="h-8 rounded-lg px-2 text-xs" onClick={() => handleDownloadVacationReport('plan-general', 'pdf')} data-testid="vacations-doc-general-pdf-button">PDF</Button>
                      <Button variant="outline" size="sm" className="h-8 rounded-lg px-2 text-xs" onClick={() => handleDownloadVacationReport('plan-general', 'xlsx')} data-testid="vacations-doc-general-xlsx-button">Excel</Button>
                    </div>
                  </div>
                  <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4" data-testid="vacations-report-balance-card">
                    <p className="font-semibold text-slate-900">Mapa / resumo de saldo de férias</p>
                    <p className="mt-1 text-xs text-slate-500">Resumo por colaborador com direito anual, dias aprovados, gozados, pendentes e saldo.</p>
                    <div className="mt-3 flex flex-wrap gap-2">
                      <Button variant="outline" size="sm" className="h-8 rounded-lg px-2 text-xs" onClick={() => handleDownloadVacationReport('balance-summary', 'pdf')} data-testid="vacations-doc-balance-pdf-button">PDF</Button>
                      <Button variant="outline" size="sm" className="h-8 rounded-lg px-2 text-xs" onClick={() => handleDownloadVacationReport('balance-summary', 'xlsx')} data-testid="vacations-doc-balance-xlsx-button">Excel</Button>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
              </div>
            </TabsContent>
          </Tabs>
        </TabsContent>
      </ModuleStateBoundary>
      </Tabs>

      {/* View Payroll Dialog */}
      <Dialog open={viewDialogOpen} onOpenChange={setViewDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Detalhes da Folha de Pagamento</DialogTitle>
            <DialogDescription>
              {getPayrollDisplayReference(selectedPayroll)}
            </DialogDescription>
          </DialogHeader>
          
          {selectedPayroll && (
            <div className="grid grid-cols-2 gap-6">
              {/* Left */}
              <div className="space-y-4">
                <div>
                  <p className="text-sm text-muted-foreground">Funcionário</p>
                  <p className="font-medium">{selectedPayroll.employee_name}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">NUIT</p>
                  <p className="font-medium">{selectedPayroll.employee_nuit || '-'}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Período</p>
                  <p className="font-medium">
                    {MONTHS.find(m => m.value === selectedPayroll.period_month)?.label} {selectedPayroll.period_year}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Estado</p>
                  <Badge className={cn('gap-1', STATUS_CONFIG[selectedPayroll.status]?.color)}>
                    {STATUS_CONFIG[selectedPayroll.status]?.label}
                  </Badge>
                </div>
              </div>
              
              {/* Right - Values */}
              <div className="space-y-3 text-sm">
                <h4 className="font-medium text-green-700">Rendimentos</h4>
                <div className="flex justify-between">
                  <span>Salário Base</span>
                  <span>{formatCurrency(selectedPayroll.base_salary)}</span>
                </div>
                {selectedPayroll.overtime_value > 0 && (
                  <div className="flex justify-between text-green-600">
                    <span>+ Horas Extra</span>
                    <span>{formatCurrency(selectedPayroll.overtime_value)}</span>
                  </div>
                )}
                <div className="flex justify-between text-green-600">
                  <span>+ Comissão</span>
                  <span>{formatCurrency(selectedPayroll.commission)}</span>
                </div>
                {selectedPayroll.total_bonuses > 0 && (
                  <div className="flex justify-between text-green-600">
                    <span>+ Bónus</span>
                    <span>{formatCurrency(selectedPayroll.total_bonuses)}</span>
                  </div>
                )}
                {selectedPayroll.total_allowances > 0 && (
                  <div className="flex justify-between text-green-600">
                    <span>+ Subsídios</span>
                    <span>{formatCurrency(selectedPayroll.total_allowances)}</span>
                  </div>
                )}
                
                <Separator />
                <div className="flex justify-between font-medium">
                  <span>Salário Bruto</span>
                  <span>{formatCurrency(selectedPayroll.gross_salary)}</span>
                </div>
                
                <Separator />
                <h4 className="font-medium text-red-700">Descontos</h4>
                <div className="flex justify-between text-red-600">
                  <span>- INSS ({(selectedPayroll.inss_employee_rate * 100).toFixed(0)}%)</span>
                  <span>{formatCurrency(selectedPayroll.inss_employee)}</span>
                </div>
                <div className="flex justify-between text-red-600">
                  <span>- IRPS ({selectedPayroll.irps_table_year})</span>
                  <span>{formatCurrency(selectedPayroll.irps_retention)}</span>
                </div>
                {selectedPayroll.advance_deduction > 0 && (
                  <div className="flex justify-between text-red-600">
                    <span>- Adiantamento</span>
                    <span>{formatCurrency(selectedPayroll.advance_deduction)}</span>
                  </div>
                )}
                {selectedPayroll.other_deductions > 0 && (
                  <div className="flex justify-between text-red-600">
                    <span>- Outros</span>
                    <span>{formatCurrency(selectedPayroll.other_deductions)}</span>
                  </div>
                )}
                
                <Separator />
                <div className="flex justify-between font-bold text-lg">
                  <span>Salário Líquido</span>
                  <span className="text-green-600">{formatCurrency(selectedPayroll.net_salary)}</span>
                </div>
                
                <div className="mt-4 p-2 bg-blue-50 rounded text-xs">
                  <p className="font-medium text-blue-700">Contribuição Patronal</p>
                  <p className="text-blue-600">
                    INSS Empresa ({(selectedPayroll.inss_employer_rate * 100).toFixed(0)}%): {formatCurrency(selectedPayroll.inss_employer)}
                  </p>
                </div>
              </div>
            </div>
          )}
          
          <DialogFooter>
            {selectedPayroll?.status !== 'draft' && (
              <Button variant="outline" onClick={() => handleDownloadReceipt(selectedPayroll.id, getPayrollDisplayReference(selectedPayroll))} data-testid="payroll-view-receipt-button">
                <Download className="h-4 w-4 mr-2" />
                Descarregar Recibo
              </Button>
            )}
            <Button onClick={() => setViewDialogOpen(false)}>Fechar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <PayrollEmployeeCreateDialog
        open={payrollEmployeeDialogOpen}
        onOpenChange={(open) => {
          setPayrollEmployeeDialogOpen(open);
          if (!open) {
            setPayrollEmployeeForm(EMPTY_PAYROLL_EMPLOYEE_FORM);
          }
        }}
        payrollEmployeeForm={payrollEmployeeForm}
        setPayrollEmployeeForm={setPayrollEmployeeForm}
        payrollBaseRoles={payrollBaseRoles}
        creatingPayrollEmployee={creatingPayrollEmployee}
        handleCreatePayrollEmployee={handleCreatePayrollEmployee}
        contractTypes={CONTRACT_TYPES}
        locationOptions={employeeStoreLocationOptions}
        maritalStatusOptions={MARITAL_STATUS_OPTIONS}
        genderOptions={GENDER_OPTIONS}
        documentIdentificationTypeOptions={DOCUMENT_IDENTIFICATION_TYPE_OPTIONS}
      />

      {/* Dossier Dialog */}
      <PayrollEmployeeDossierDialog
        open={dossierDialogOpen}
        onOpenChange={(open) => {
          setDossierDialogOpen(open);
          if (!open) {
            setEmployeeDossier(null);
          }
        }}
        selectedEmployee={selectedEmployee}
        employeeDossier={employeeDossier}
        dossierForm={dossierForm}
        setDossierForm={setDossierForm}
        reintegrationForm={reintegrationForm}
        setReintegrationForm={setReintegrationForm}
        isAdmin={isAdmin}
        submitting={submitting}
        handleSaveDossier={handleSaveDossier}
        handleReintegrateEmployee={handleReintegrateEmployee}
        contractTypes={CONTRACT_TYPES}
        locationOptions={employeeStoreLocationOptions}
        maritalStatusOptions={MARITAL_STATUS_OPTIONS}
        genderOptions={GENDER_OPTIONS}
        documentIdentificationTypeOptions={DOCUMENT_IDENTIFICATION_TYPE_OPTIONS}
      />

      <Dialog open={payrollEditDialogOpen} onOpenChange={(open) => {
        if (!open) {
          resetPayrollEditState();
          return;
        }
        setPayrollEditDialogOpen(true);
      }}>
        <DialogContent className="w-[min(96vw,980px)] max-w-[980px] max-h-[88vh] overflow-y-auto p-4 sm:p-5" data-testid="payroll-edit-dialog">
          <DialogHeader>
            <DialogTitle>Editar folha draft</DialogTitle>
            <DialogDescription>
              {editingPayroll?.employee_name} • {getPayrollDisplayReference(editingPayroll)} • apenas rascunhos podem ser ajustados.
            </DialogDescription>
          </DialogHeader>

          <div className="grid gap-4 md:grid-cols-3">
            <div className="space-y-2">
              <Label className="text-xs">Salário base</Label>
              <Input type="number" step="0.01" value={payrollEditForm.base_salary} onChange={(event) => setPayrollEditForm((current) => ({ ...current, base_salary: event.target.value }))} data-testid="payroll-edit-base-salary" />
            </div>
            <div className="space-y-2">
              <Label className="text-xs">Horas extra</Label>
              <Input type="number" step="0.5" value={payrollEditForm.overtime_hours} onChange={(event) => setPayrollEditForm((current) => ({ ...current, overtime_hours: event.target.value }))} data-testid="payroll-edit-overtime-hours" />
            </div>
            <div className="space-y-2">
              <Label className="text-xs">Taxa H. extra</Label>
              <Input type="number" step="0.1" value={payrollEditForm.overtime_rate} onChange={(event) => setPayrollEditForm((current) => ({ ...current, overtime_rate: event.target.value }))} data-testid="payroll-edit-overtime-rate" />
            </div>
            <div className="space-y-2">
              <Label className="text-xs">Comissão manual</Label>
              <Input type="number" step="0.01" value={payrollEditForm.manual_commission} onChange={(event) => setPayrollEditForm((current) => ({ ...current, manual_commission: event.target.value }))} placeholder="vazio = manter actual" data-testid="payroll-edit-manual-commission" />
            </div>
            <div className="space-y-2">
              <Label className="text-xs">Bónus assiduidade</Label>
              <Input type="number" step="0.01" value={payrollEditForm.attendance_bonus} onChange={(event) => setPayrollEditForm((current) => ({ ...current, attendance_bonus: event.target.value }))} data-testid="payroll-edit-attendance-bonus" />
            </div>
            <div className="space-y-2">
              <Label className="text-xs">Bónus performance</Label>
              <Input type="number" step="0.01" value={payrollEditForm.performance_bonus} onChange={(event) => setPayrollEditForm((current) => ({ ...current, performance_bonus: event.target.value }))} data-testid="payroll-edit-performance-bonus" />
            </div>
            <div className="space-y-2">
              <Label className="text-xs">Outros bónus</Label>
              <Input type="number" step="0.01" value={payrollEditForm.other_bonuses} onChange={(event) => setPayrollEditForm((current) => ({ ...current, other_bonuses: event.target.value }))} data-testid="payroll-edit-other-bonuses" />
            </div>
            <div className="space-y-2">
              <Label className="text-xs">Subsídio transporte</Label>
              <Input type="number" step="0.01" value={payrollEditForm.transport_allowance} onChange={(event) => setPayrollEditForm((current) => ({ ...current, transport_allowance: event.target.value }))} data-testid="payroll-edit-transport-allowance" />
            </div>
            <div className="space-y-2">
              <Label className="text-xs">Subsídio alimentação</Label>
              <Input type="number" step="0.01" value={payrollEditForm.meal_allowance} onChange={(event) => setPayrollEditForm((current) => ({ ...current, meal_allowance: event.target.value }))} data-testid="payroll-edit-meal-allowance" />
            </div>
            <div className="space-y-2">
              <Label className="text-xs">Outros subsídios</Label>
              <Input type="number" step="0.01" value={payrollEditForm.other_allowances} onChange={(event) => setPayrollEditForm((current) => ({ ...current, other_allowances: event.target.value }))} data-testid="payroll-edit-other-allowances" />
            </div>
            <div className="space-y-2">
              <Label className="text-xs">Adiantamento</Label>
              <Input type="number" step="0.01" value={payrollEditForm.advance_deduction} onChange={(event) => setPayrollEditForm((current) => ({ ...current, advance_deduction: event.target.value }))} data-testid="payroll-edit-advance-deduction" />
            </div>
            <div className="space-y-2">
              <Label className="text-xs">Outros descontos</Label>
              <Input type="number" step="0.01" value={payrollEditForm.other_deductions} onChange={(event) => setPayrollEditForm((current) => ({ ...current, other_deductions: event.target.value }))} data-testid="payroll-edit-other-deductions" />
            </div>
          </div>

          <div className="space-y-2">
            <Label className="text-xs">Observações</Label>
            <Textarea value={payrollEditForm.notes} onChange={(event) => setPayrollEditForm((current) => ({ ...current, notes: event.target.value }))} rows={3} data-testid="payroll-edit-notes" />
          </div>

          <div className="grid gap-3 rounded-2xl border border-slate-200 bg-slate-50 p-3 text-sm text-slate-600 md:grid-cols-3" data-testid="payroll-edit-summary-strip">
            <div>
              <p className="text-[11px] uppercase tracking-wide text-slate-500">Actual bruto</p>
              <p className="mt-1 font-semibold text-slate-900">{formatCurrency(editingPayroll?.gross_salary || 0)}</p>
            </div>
            <div>
              <p className="text-[11px] uppercase tracking-wide text-slate-500">Actual líquido</p>
              <p className="mt-1 font-semibold text-slate-900">{formatCurrency(editingPayroll?.net_salary || 0)}</p>
            </div>
            <div>
              <p className="text-[11px] uppercase tracking-wide text-slate-500">Estado</p>
              <p className="mt-1 font-semibold text-slate-900">{STATUS_CONFIG[editingPayroll?.status || 'draft']?.label}</p>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={resetPayrollEditState} data-testid="payroll-edit-cancel-button">Cancelar</Button>
            <Button onClick={handleSavePayrollEdit} disabled={submitting} data-testid="payroll-edit-save-button">
              {submitting ? 'A guardar...' : 'Guardar alterações'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Configuration Dialog */}
      <Dialog open={configDialogOpen} onOpenChange={setConfigDialogOpen}>
        <DialogContent className="w-[min(96vw,1180px)] max-w-[1180px] max-h-[90vh] overflow-y-auto p-4 sm:p-5" data-testid="payroll-config-dialog">
          <DialogHeader>
            <DialogTitle>Configurações de Payroll</DialogTitle>
            <DialogDescription>
              Admin pode editar, criar, activar e repor dados de INSS, meta de comissões e tabelas IRPS.
            </DialogDescription>
          </DialogHeader>
          
          <div className="grid gap-4 xl:grid-cols-[0.9fr_1.25fr]">
            <div className="space-y-4">
              <Card className="rounded-2xl border-slate-200 shadow-sm" data-testid="payroll-config-inss-card">
                <CardHeader className="space-y-1 pb-3">
                  <CardTitle className="flex items-center gap-2 text-base">
                    <Building2 className="h-4 w-4" />
                    INSS
                  </CardTitle>
                  <CardDescription>Editar taxas aplicadas ao trabalhador e ao empregador.</CardDescription>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="grid gap-3 sm:grid-cols-2">
                    <div className="space-y-1.5">
                      <Label className="text-xs">Taxa trabalhador (%)</Label>
                      <Input value={inssForm.employee_rate} onChange={(event) => setInssForm((current) => ({ ...current, employee_rate: event.target.value }))} data-testid="payroll-config-inss-employee-rate" />
                    </div>
                    <div className="space-y-1.5">
                      <Label className="text-xs">Taxa empregador (%)</Label>
                      <Input value={inssForm.employer_rate} onChange={(event) => setInssForm((current) => ({ ...current, employer_rate: event.target.value }))} data-testid="payroll-config-inss-employer-rate" />
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <Button onClick={handleSaveINSSConfig} disabled={configSubmitting} data-testid="payroll-config-inss-save-button">
                      Guardar INSS
                    </Button>
                    <Button variant="outline" onClick={handleResetINSSConfig} disabled={configSubmitting} data-testid="payroll-config-inss-reset-button">
                      Repor padrão
                    </Button>
                  </div>
                </CardContent>
              </Card>

              <Card className="rounded-2xl border-slate-200 shadow-sm" data-testid="payroll-config-goal-card">
                <CardHeader className="space-y-1 pb-3">
                  <CardTitle className="flex items-center gap-2 text-base">
                    <Target className="h-4 w-4" />
                    Meta Empresa (Comissões)
                  </CardTitle>
                  <CardDescription>Configuração base para meta colectiva e taxa de comissão.</CardDescription>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="grid gap-3 sm:grid-cols-2">
                    <div className="space-y-1.5">
                      <Label className="text-xs">Meta mensal (MZN)</Label>
                      <Input value={companyGoalForm.monthly_goal} onChange={(event) => setCompanyGoalForm((current) => ({ ...current, monthly_goal: event.target.value }))} data-testid="payroll-config-goal-monthly-goal" />
                    </div>
                    <div className="space-y-1.5">
                      <Label className="text-xs">Taxa comissão (%)</Label>
                      <Input value={companyGoalForm.commission_rate} onChange={(event) => setCompanyGoalForm((current) => ({ ...current, commission_rate: event.target.value }))} data-testid="payroll-config-goal-commission-rate" />
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <Button onClick={handleSaveCompanyGoal} disabled={configSubmitting} data-testid="payroll-config-goal-save-button">
                      Guardar meta
                    </Button>
                    <Button variant="outline" onClick={handleResetCompanyGoal} disabled={configSubmitting} data-testid="payroll-config-goal-reset-button">
                      Repor padrão
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </div>

            <div className="space-y-4">
              <Card className="rounded-2xl border-slate-200 shadow-sm" data-testid="payroll-config-irps-editor-card">
                <CardHeader className="space-y-1 pb-3">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <div>
                      <CardTitle className="flex items-center gap-2 text-base">
                        <Landmark className="h-4 w-4" />
                        {irpsEditor.id ? `Editar IRPS ${irpsEditor.year}` : 'Nova tabela IRPS'}
                      </CardTitle>
                      <CardDescription>Criar, editar ou activar tabelas com escalões completos.</CardDescription>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      <Button variant="outline" size="sm" onClick={() => openIrpsEditor()} data-testid="payroll-config-irps-new-button">
                        <Plus className="mr-1 h-4 w-4" />
                        Nova tabela
                      </Button>
                      <Button variant="outline" size="sm" onClick={handleResolveIRPSTableGap} data-testid="payroll-config-irps-seed-button">
                        Oficial {periodYear}
                      </Button>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="grid gap-3 md:grid-cols-[120px_1fr_120px] md:items-end">
                    <div className="space-y-1.5">
                      <Label className="text-xs">Ano</Label>
                      <Input value={irpsEditor.year} onChange={(event) => setIrpsEditor((current) => ({ ...current, year: event.target.value }))} data-testid="payroll-config-irps-year" />
                    </div>
                    <div className="space-y-1.5">
                      <Label className="text-xs">Notas</Label>
                      <Textarea value={irpsEditor.notes} onChange={(event) => setIrpsEditor((current) => ({ ...current, notes: event.target.value }))} rows={2} data-testid="payroll-config-irps-notes" />
                    </div>
                    <div className="flex items-center justify-between rounded-xl border border-slate-200 bg-slate-50 px-3 py-2" data-testid="payroll-config-irps-active-switch-row">
                      <div>
                        <p className="text-xs font-medium text-slate-700">Activa</p>
                        <p className="text-[11px] text-slate-500">Tabela do ano</p>
                      </div>
                      <Switch checked={irpsEditor.is_active} onCheckedChange={(checked) => setIrpsEditor((current) => ({ ...current, is_active: checked }))} data-testid="payroll-config-irps-active-switch" />
                    </div>
                  </div>

                  <div className="rounded-2xl border border-slate-200">
                    <div className="grid grid-cols-[1fr_1fr_1fr_1fr_auto] gap-2 border-b bg-slate-50 px-3 py-2 text-[11px] font-semibold uppercase tracking-[0.18em] text-muted-foreground">
                      <span>De</span>
                      <span>Até</span>
                      <span>Taxa %</span>
                      <span>Parcela</span>
                      <span>Ação</span>
                    </div>
                    <div className="space-y-2 p-3">
                      {irpsEditor.brackets.map((bracket, index) => (
                        <div key={`${irpsEditor.id || 'new'}-${index}`} className="grid grid-cols-[1fr_1fr_1fr_1fr_auto] gap-2" data-testid={`payroll-config-irps-bracket-row-${index}`}>
                          <Input value={bracket.min_value} onChange={(event) => handleIrpsBracketChange(index, 'min_value', event.target.value)} data-testid={`payroll-config-irps-bracket-min-${index}`} />
                          <Input value={bracket.max_value} onChange={(event) => handleIrpsBracketChange(index, 'max_value', event.target.value)} placeholder="sem limite" data-testid={`payroll-config-irps-bracket-max-${index}`} />
                          <Input value={bracket.rate} onChange={(event) => handleIrpsBracketChange(index, 'rate', event.target.value)} data-testid={`payroll-config-irps-bracket-rate-${index}`} />
                          <Input value={bracket.deduction} onChange={(event) => handleIrpsBracketChange(index, 'deduction', event.target.value)} data-testid={`payroll-config-irps-bracket-deduction-${index}`} />
                          <Button variant="outline" size="icon" onClick={() => handleRemoveIrpsBracket(index)} disabled={irpsEditor.brackets.length === 1} data-testid={`payroll-config-irps-bracket-remove-${index}`}>
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="flex flex-wrap gap-2">
                    <Button variant="outline" onClick={handleAddIrpsBracket} data-testid="payroll-config-irps-add-bracket-button">
                      <Plus className="mr-1 h-4 w-4" />
                      Adicionar escalão
                    </Button>
                    <Button onClick={handleSaveIRPSTable} disabled={configSubmitting} data-testid="payroll-config-irps-save-button">
                      {irpsEditor.id ? 'Actualizar tabela' : 'Criar tabela'}
                    </Button>
                  </div>
                </CardContent>
              </Card>

              <Card className="rounded-2xl border-slate-200 shadow-sm" data-testid="payroll-config-irps-list-card">
                <CardHeader className="space-y-1 pb-3">
                  <CardTitle className="text-base">Tabelas IRPS</CardTitle>
                  <CardDescription>Gestão completa das tabelas disponíveis por ano.</CardDescription>
                </CardHeader>
                <CardContent>
                  {irpsTables.length > 0 ? (
                    <div className="space-y-2">
                      {irpsTables.map((table) => (
                        <div key={table.id || table.year} className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-slate-200 bg-slate-50 px-3 py-3" data-testid={`payroll-config-irps-table-row-${table.id || table.year}`}>
                          <div>
                            <div className="flex flex-wrap items-center gap-2">
                              <p className="font-medium text-slate-900">Tabela {table.year}</p>
                              {table.is_active && <Badge className="bg-green-100 text-green-700">Activa</Badge>}
                              {!table.id && <Badge variant="outline">Preset</Badge>}
                            </div>
                            <p className="text-xs text-slate-500">{table.brackets?.length || 0} escalões • {table.notes || 'Sem notas'}</p>
                          </div>
                          <div className="flex flex-wrap gap-2">
                            <Button variant="outline" size="sm" onClick={() => openIrpsEditor(table)} data-testid={`payroll-config-irps-edit-${table.id || table.year}`}>
                              Editar
                            </Button>
                            {!table.is_active && table.id && (
                              <Button variant="outline" size="sm" onClick={() => handleActivateIRPSTable(table.id)} data-testid={`payroll-config-irps-activate-${table.id}`}>
                                Activar
                              </Button>
                            )}
                            {table.id && (
                              <Button variant="outline" size="sm" className="text-red-600" onClick={() => handleDeleteIRPSTable(table.id)} data-testid={`payroll-config-irps-delete-${table.id}`}>
                                Eliminar
                              </Button>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="rounded-2xl border border-dashed border-slate-300 bg-slate-50 px-4 py-6 text-center" data-testid="payroll-config-irps-empty-state">
                      <p className="text-sm text-slate-700">Nenhuma tabela IRPS configurada.</p>
                      <div className="mt-3 flex justify-center gap-2">
                        <Button size="sm" onClick={handleResolveIRPSTableGap} data-testid="payroll-config-irps-empty-seed-button">
                          Activar oficial {periodYear}
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => openIrpsEditor()} data-testid="payroll-config-irps-empty-create-button">
                          Criar manualmente
                        </Button>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfigDialogOpen(false)} data-testid="payroll-config-close-button">Fechar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Confirm Dialog */}
      <AlertDialog open={confirmDialogOpen} onOpenChange={setConfirmDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Marcar folha como revista</AlertDialogTitle>
            <AlertDialogDescription>
              Confirma que esta folha já foi revista? Após esta etapa, os valores deixam de poder ser alterados.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleConfirmPayroll} disabled={submitting}>
              {submitting ? 'A rever...' : 'Marcar revisto'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Mark Paid Dialog */}
      <AlertDialog open={markPaidDialogOpen} onOpenChange={setMarkPaidDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Marcar folha como processada</AlertDialogTitle>
            <AlertDialogDescription>
              Confirma que o processamento final/pagamento de {formatCurrency(selectedPayroll?.net_salary || 0)} foi efectuado a {selectedPayroll?.employee_name}?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleMarkPaid} disabled={submitting}>
              {submitting ? 'A processar...' : 'Marcar processado'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Eliminar Rascunho</AlertDialogTitle>
            <AlertDialogDescription>
              Tem certeza que deseja eliminar este rascunho? Esta acção não pode ser revertida.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeletePayroll} disabled={submitting} className="bg-red-600 hover:bg-red-700">
              {submitting ? 'A eliminar...' : 'Eliminar'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
